import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { baseLanguageFor } from "@/lib/i18n/languages";
import { operatorStamped } from "@/lib/operator-stamped";
import { useScrollToTopOnChange } from "@/hooks/useScrollToTopOnChange";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import {
  Home as HomeIcon,
  FileText,
  CreditCard,
  LogIn,
  MessageSquare,
  Headphones,
  Users,
  Calendar,
  Gift,
  Share2,
  Download,
  Heart,
  MapPin,
  ArrowRight,
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  Menu,
  ShieldCheck,
  Lock,
  AlertCircle,
  Info,
  Settings,
  Upload,
  X,
  File,
  CheckCircle2,
  Loader2,
  RefreshCw,
  IdCard,
  Receipt,
  User,
  Mail,
  Edit2,
  Save,
  Clock,
  Hourglass,
  FileCheck,
  ListChecks,
  Euro,
  Landmark,
  Phone,
  Copy,
  Globe,
} from "lucide-react";
import PaidCelebration from "@/components/payments/PaidCelebration";
import { usePaidCelebration } from "@/hooks/usePaidCelebration";
import {
  latestPaidTimestamp,
  paidPeriodMonthLabel,
} from "@/lib/paid-celebration";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase, supabaseAnonKey, supabaseUrl } from "../lib/supabase";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { openProofInNewTab } from "@/lib/proof-url";
import {
  openStoredFile,
  removeStoredFile,
  storageRefFor,
} from "@/lib/storage-url";
import room1 from "@/assets/room-1.jpg";
import room2 from "@/assets/room-2.jpg";
import room3 from "@/assets/room-3.jpg";
import ComingSoon from "@/components/ComingSoon";
import CheckInCheckOut from "@/components/dashboard/CheckInCheckOut";
import ReferralsPanel from "@/components/dashboard/ReferralsPanel";
import TenantSupportHelp from "@/components/tenant/v2/TenantSupportHelp";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatAddress } from "@/lib/v2-format";
import { formatDateNumeric } from "@/lib/date-format";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Progress } from "@/components/ui/progress";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  getDisplayedRentEUR,
  getExpectedDepositEUR,
  getFullMonthlyRentFromBooking,
  isSepaDebitInFlight,
  lastPaidRentCoversPeriod,
  projectedUpcomingChargeEUR,
  resolveDepositState,
  resolveRentSettlement,
  selectEarliestUnpaidRentPayment,
  splitRentLineItems,
  remainingRentCents,
  isReferralFreeMonth,
  getReferralCreditCents,
} from "@/lib/rent";
import { referralCreditDashboardNote } from "@/lib/referrals";
import {
  accruedFeeCents,
  feeCarrierPaymentId,
  owedWithFees,
} from "@/lib/rent-owed-with-fees";
import { RentOpenBalance } from "@/components/dashboard/RentOpenBalance";
import { edgeFunctionErrorMessage } from "@/lib/edge-function-error";
import { getMinToDateAfterSixMonths } from "@/lib/booking-dates";
import {
  pickLiveTenantBooking,
  type LiveBookingCandidate,
} from "@/lib/tenant-live-booking";
import { ensureRentPaymentForMonth } from "@/lib/payment-booking";
import { format, parseISO } from "date-fns";
import { cn } from "@/lib/utils";
import {
  effectivePaymentStatus,
  isEffectivelyPaid,
  paymentStatusInput,
} from "@/lib/paymentStatus";
import { depositRowMatchesBooking } from "@/lib/deposit-booking-match";
import { mergeRecentAndDepositPayments } from "@/lib/payments-recent-window";
import { Calendar as DatePickerCalendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { formatCardFeeBreakdown } from "@/lib/card-fees";
import SepaMandateForm, {
  type SepaMandateLabels,
} from "@/components/dashboard/SepaMandateForm";
import { SEPA_CREDITOR_ID, SEPA_CREDITOR_NAME } from "@/lib/sepa-constants";
import { checkDateOfBirth } from "@/lib/contract-dob-gate";
import { contractRefusalMessage } from "@/lib/adminCreateContract";
import { latestAdultBirthDate } from "@/lib/adult-age";
import { ProtocolFlow } from "@/components/protocol/ProtocolFlow";
import { useProtocolActionNeeded } from "@/hooks/useProtocolActionNeeded";
import { useNowTick } from "@/hooks/useNowTick";
import { useReferralRewardStalled } from "@/hooks/useReferralRewardStalled";
import { TabActionPip } from "@/components/dashboard/TabActionPip";
import {
  computeTenantNextActions,
  nextActionAriaLabel,
  nextActionChipLabel,
  tenantActionsByTab,
  type NextActionTab,
} from "@/lib/tenant-next-action";
import { AccountSettingsDialog } from "@/components/settings/AccountSettingsDialog";
import { TenantDrawerHeader } from "@/components/dashboard/TenantDrawerHeader";
import { LanguagePreferenceCard } from "@/components/settings/LanguagePreferenceCard";
import { AccountDocumentRow } from "@/components/settings/AccountDocumentRow";
import {
  canTenantDeleteDocument,
  uploadTenantDocuments,
  type TenantDocumentType,
} from "@/lib/tenant-document-upload";

/** Bank details are merged with room_payment_config when bank transfer is enabled; otherwise COLIVIQ defaults are shown for reference. */
const COLIVIQ_DEFAULT_BANK_TRANSFER = {
  accountHolder: "COLIVIQ GMBH",
  iban: "DE55701500001009577022",
  bic: "SSKMDEMMXXX",
  bankName: "Stadtsparkasse München",
} as const;

function formatIbanForDisplay(iban: string): string {
  const c = iban.replace(/\s/g, "").toUpperCase();
  if (!c) return "";
  return c.replace(/(.{4})/g, "$1 ").trim();
}

/** Payments tab and dashboard labels in EN/DE for when language is selected */
function getPaymentsLabels(lang: "en" | "de") {
  return lang === "de"
    ? {
        paymentOverview: "Zahlungsübersicht",
        paymentsOverviewEyebrow: "Übersicht",
        paymentsHeroSubtitle: "Miete, Kaution und Historie an einem Ort.",
        paymentsSectionAtAGlance: "Auf einen Blick",
        paymentsSectionDetails: "Miete & Kaution im Detail",
        paymentsSectionPaymentOptions: "Zahlungsoptionen",
        overviewPropertyLabel: "Objekt",
        overviewBillingPeriodLabel: "Abrechnungszeitraum",
        bankTransferSectionTitle: "Bankverbindung",
        bankTransferSectionSubtitle: "Überweisungen an dieses Konto.",
        proRataBadge: "Anteilig",
        paymentMethod: "Zahlungsmethode",
        rentBreakdown: "Mietaufstellung",
        paymentHistory: "Zahlungshistorie",
        monthlyRent: "Monatsmiete",
        monthlyRentDesc: "Grundmiete und Nebenkosten. Monatliche Abrechnung.",
        nextPayment: "Nächste Zahlung",
        usePayNowToSettle: (monthLabel: string) =>
          `Nutzen Sie „Jetzt zahlen“, um die Miete für ${monthLabel} sicher zu begleichen.`,
        nextRentDueNoAction: (date: string) =>
          `Nächste Miete fällig am ${date}. Bis dahin ist keine Aktion nötig.`,
        deposit: "Kaution",
        depositHeldRefunded:
          "Sicher verwahrt und am Ende des Aufenthalts zurückerstattet, abzüglich eventueller Abzüge.",
        rent: "Miete",
        monthlyRentUpper: "MONATSMIETE",
        room: "Zimmer",
        monthsOfRentPaid: "Monate Miete bezahlt",
        months: "Monate",
        paid: "bezahlt",
        dueOn: "Fällig am",
        depositStatus: "Kautionsstatus",
        oneTimeSecurityDeposit:
          "Einmalige Kaution, sicher für Ihren Aufenthalt verwahrt.",
        securityDeposit: "Kaution",
        expected: "Erwartet",
        amountPaid: "Bezahlt",
        paidOn: "Bezahlt am",
        depositActivity: "Kautionsaktivität",
        noDepositRecorded: "Noch keine Kautionszahlung erfasst.",
        depositOnlyEndOfTenancy:
          "Ihre Kaution wird nur am Ende der Mietzeit für vereinbarte Abzüge verwendet. Sie wird nicht auf die Monatsmiete angerechnet.",
        refreshPaymentStatus: "Zahlungsstatus aktualisieren",
        payDeposit: "Kaution zahlen",
        depositPayableWhenSigned:
          "Ihre Kaution wird fällig, sobald Ihr Vertrag unterzeichnet ist.",
        upcomingPayment: "Anstehende Zahlung",
        amountDueLabel: "Fälliger Betrag",
        reviewNextRentPaySecurely:
          "Prüfen Sie Ihre nächste Mietzahlung und zahlen Sie sicher mit Ihrer Karte.",
        upcomingInvoiceProRata: "Anteilige Miete (laut dieser Rechnung).",
        rentLabel: "Miete",
        paidStatus: "Bezahlt",
        overdueStatus: "Überfällig",
        pendingStatus: "Ausstehend",
        openStatus: "Offen",
        partiallyPaidStatus: "Teilweise bezahlt",
        paidOfBilled: (paid: string, billed: string) =>
          `${paid} von ${billed} bezahlt`,
        openRentLabel: "Offene Miete",
        dunningFeesLabel: "Mahngebühren",
        totalOpenLabel: "Gesamt offen",
        outstandingFeesLabel: "Offene Mahngebühren",
        rentFeeSplit: (rent: string, fees: string) =>
          `${rent} Miete · ${fees} Mahngebühren`,
        feesByTransferNote:
          "Karte und Lastschrift ziehen nur die Miete ein. Bitte überweisen Sie die Mahngebühren.",
        bankTransferFeesNote: (fees: string, total: string) =>
          `Zuzüglich ${fees} offene Mahngebühren – bitte insgesamt ${total} überweisen.`,
        sepaInProgressStatus: "SEPA-Lastschrift läuft",
        sepaInProgressNote:
          "Die Lastschrift wurde bei Ihrer Bank eingereicht und wird in 2–5 Bankarbeitstagen abgebucht. Sie müssen nichts weiter tun — wir bestätigen per E-Mail, sobald das Geld eingegangen ist.",
        payNow: "Jetzt zahlen",
        viewDetails: "Details anzeigen",
        paymentCompleted: "Zahlung abgeschlossen ✓",
        oneTimeDepositUpdates:
          "Einmalige Kaution. Betrag und Status werden bei erfassten Zahlungen aktualisiert.",
        dueWhenSignedPayBelow:
          "Fällig nach Vertragsunterzeichnung. Zahlen Sie unten, um abzuschließen.",
        depositHeldEndOfTenancy:
          "Dieser Betrag wird sicher verwahrt und am Ende Ihrer Mietzeit geprüft. Er wird nicht für die Monatsmiete verwendet.",
        manageHowCollected:
          "Verwalten Sie, wie Ihre Mietzahlungen eingezogen werden.",
        secure: "Sicher",
        cardPaymentStripe: "Kartenzahlung über Stripe",
        cardDetailsCheckout: "Kartendaten werden beim Checkout hinzugefügt.",
        stripeAdditionalChargesNoteTitle: "Hinweis",
        stripeAdditionalChargesNoteBody:
          "Bei Kartenzahlung über Stripe können zusätzliche Gebühren anfallen.",
        active: "Aktiv",
        managePaymentMethod: "Zahlungsmethode verwalten",
        last6Months: "Letzte 6 Monate",
        noPaymentsRecordedYet:
          "Noch keine Zahlungen erfasst. Sobald Ihre erste Zahlung verbucht ist, erscheint sie hier.",
        oneTime: "Einmalig",
        hereHowRentStructured:
          "So setzt sich Ihre Monatsmiete für Ihr aktuelles Zimmer zusammen.",
        backToOverview: "Zurück zur Übersicht",
        baseRent: "Grundmiete",
        furnishedPrivateRoom:
          "Ihr möbliertes Einzelzimmer in einer Wohngemeinschaft.",
        utilitiesServices: "Nebenkosten & Leistungen",
        electricityWaterWiFi:
          "Strom, Wasser, Heizung, WLAN und Reinigung der Gemeinschaftsflächen.",
        totalMonthlyRent: "Gesamtmiete monatlich",
        proRataFirstMonthNote: (_fullEuro: string) =>
          `Die erste Mietzahlung kann anteilig sein (nach Einzugsdatum). Der genaue Betrag steht bei der nächsten Zahlung.`,
        depositHeldSeparate: (amount: string) =>
          `Ihre Kaution in Höhe von ${amount} wird auf einem separaten Konto verwahrt und kann nicht für die Monatsmiete verwendet werden. Sie wird nach der Übergabe geprüft und ggf. zurückerstattet.`,
        nextCollectionDate: (date: string) =>
          `Nächster Einzugstermin: ${date}. Nach jeder erfolgreichen Zahlung erhalten Sie eine Quittung.`,
        collectionDate: "Einzugsdatum",
        receiptsHistory: "Belege & Verlauf",
        availableInPaymentHistory: "Unter „Zahlungshistorie“",
        cancel: "Abbrechen",
        confirmPayment: "Zahlung bestätigen",
        reviewAndConfirmPayment: "Zahlung prüfen & bestätigen",
        confirmPaymentTitle: "Zahlung bestätigen",
        confirmPaymentSubtitle:
          "Prüfen Sie die Zusammenfassung und bestätigen Sie anschließend, um Ihre anstehende Miete sicher per Karte über Stripe zu bezahlen.",
        firstRentCollection: "Erste Mietabbuchung",
        firstRentCollectionDesc:
          "Dies umfasst Ihre erste Monatsmiete. Kaution und ggf. Mitgliedsbeitrag werden separat berechnet, falls noch offen.",
        stripeProcessedStatement:
          "Ihre Zahlung wird sicher von Stripe verarbeitet. Die Belastung erscheint kurz nach der Bestätigung auf Ihrem Kontoauszug.",
        paymentSuccessful: "Zahlung erfolgreich",
        last12Payments: "Letzte 12 Zahlungen",
        allPastPaymentsHere:
          "Alle Ihre Miet- und Kautionszahlungen erscheinen hier, sobald sie erfasst sind.",
        paymentStatusRefreshed: "Zahlungsstatus aktualisiert.",
        payByBankTransfer: "Per Überweisung zahlen",
        uploadPaymentProof: "Zahlungsnachweis hochladen",
        uploadPaymentProofMandatory:
          "Bei Zahlung per Überweisung ist das Hochladen eines Zahlungsnachweises verpflichtend.",
        uploadStatement:
          "Kontoauszug oder Überweisungsbeleg hochladen (PDF oder Bild)",
        uploading: "Wird hochgeladen…",
        uploadSuccess:
          "Zahlungsnachweis wurde hochgeladen. Die Prüfung kann einen Moment dauern.",
        proofPendingReview: "Wird geprüft",
        proofApproved: "Angenommen",
        proofRejected: "Abgelehnt",
        rejectionReason: "Grund der Ablehnung",
        viewPaymentProof: "Zahlungsnachweis anzeigen",
        paymentProofUploaded: "Zahlungsnachweis hochgeladen",
        bankTransferNoAdditionalChargesNote:
          "Bei Überweisungen fallen keine zusätzlichen Gebühren an.",
        // Card fee disclosure
        cardFeeTitle: "Kartenzahlung – Gebühren",
        cardFeeSubtotal: "Zwischensumme",
        cardFeeProcessing: "Kartenzahlungsgebühr",
        cardFeeTotal: "Gesamtbetrag",
        cardFeeExplainer:
          "Die Stripe-Kartenzahlungsgebühr wird separat ausgewiesen, damit der volle Mietbetrag bei coliviq ankommt.",
        continueToStripe: "Weiter zu Stripe",
        // SEPA direct debit mandate
        payBySepa: "SEPA-Lastschriftmandat einrichten",
        sepaFormTitle: "SEPA-Basis-Lastschriftmandat",
        sepaCreditorHeading: "Gläubiger",
        sepaCreditorNameLabel: "Zahlungsempfänger",
        sepaCreditorIdLabel: "Gläubiger-ID",
        sepaMandateReferenceLabel: "Mandatsreferenz (UMR)",
        sepaMandateReferencePending: "Wird nach Signatur vergeben",
        sepaAuthorizationHeading: "SEPA-Basis-Lastschrift-Mandat",
        sepaAuthorizationText:
          "Ich ermächtige/Wir ermächtigen {creditor}, Zahlungen von meinem/unserem Konto mittels Lastschrift einzuziehen. Zugleich weise ich mein/weisen wir unser Kreditinstitut an, die von {creditor} auf mein/unser Konto gezogenen Lastschriften einzulösen.",
        sepaRefundNoticeHeading: "Hinweis",
        sepaRefundNoticeText:
          "Ich kann/Wir können innerhalb von acht Wochen, beginnend mit dem Belastungsdatum, die Erstattung des belasteten Betrags verlangen. Es gelten dabei die mit meinem/unserem Kreditinstitut vereinbarten Bedingungen.",
        sepaCustomerDataHeading: "Kundendaten",
        sepaSalutation: "Anrede",
        sepaMr: "Herr",
        sepaMrs: "Frau",
        sepaDivers: "Divers",
        sepaFirstName: "Vorname",
        sepaLastName: "Nachname",
        sepaStreet: "Straße",
        sepaHouseNumber: "Hausnr.",
        sepaPostalCode: "PLZ",
        sepaCity: "Ort",
        sepaCountry: "Land",
        sepaIban: "IBAN",
        sepaBic: "BIC (optional)",
        sepaAccountHolder: "Kontoinhaber",
        sepaSignatureHeading: "Unterschrift",
        sepaSigningPlace: "Ort",
        sepaSigningDate: "Datum",
        sepaConfirmCheckbox:
          "Ich bestätige die Mandatserteilung und stimme dem Einzug zu.",
        sepaTypedNameLabel: "Vollständiger Name zur Unterschrift",
        sepaTypedNameHelper:
          "Geben Sie Ihren vollständigen Namen genau so ein, wie oben (Vorname Nachname), um die Unterschrift zu bestätigen.",
        sepaSubmit: "Mandat erteilen",
        sepaSubmitting: "Mandat wird erteilt…",
        sepaMandatoryField: "Bitte füllen Sie alle Pflichtfelder aus.",
        sepaIbanInvalid: "Ungültige IBAN.",
        sepaTypedNameMismatch:
          "Der eingegebene Name stimmt nicht mit Vor- und Nachname überein.",
        sepaSuccessTitle: "SEPA-Mandat erteilt",
        sepaSuccessBody: (ref: string) =>
          `Ihr SEPA-Mandat ist aktiv. Mandatsreferenz: ${ref}`,
        sepaMandateActiveBadge: "SEPA aktiv",
        sepaActiveAutoDebitNote:
          "Die Miete wird zum Fälligkeitsdatum automatisch von deinem hinterlegten Konto eingezogen — du musst nichts weiter tun.",
        sepaAutoDebitPayNowNote:
          "Hinweis: Für dieses Konto ist ein SEPA-Lastschriftmandat aktiv — die Miete wird zum Fälligkeitsdatum automatisch eingezogen. Eine Kartenzahlung ist nur nötig, wenn du nicht auf den Einzug warten möchtest.",
        sepaGenericError: "Mandat konnte nicht gespeichert werden.",
      }
    : {
        paymentOverview: "Payment overview",
        paymentsOverviewEyebrow: "Overview",
        paymentsHeroSubtitle: "Rent, deposit, and history in one place.",
        paymentsSectionAtAGlance: "At a glance",
        paymentsSectionDetails: "Rent & deposit details",
        paymentsSectionPaymentOptions: "Payment options",
        overviewPropertyLabel: "Property",
        overviewBillingPeriodLabel: "Billing period",
        bankTransferSectionTitle: "Bank transfer details",
        bankTransferSectionSubtitle: "Use these details for SEPA transfers.",
        proRataBadge: "Pro-rata",
        paymentMethod: "Payment method",
        rentBreakdown: "Rent breakdown",
        paymentHistory: "Payment history",
        monthlyRent: "Monthly rent",
        monthlyRentDesc: "Includes base rent and services. Billed monthly.",
        nextPayment: "Next payment",
        usePayNowToSettle: (monthLabel: string) =>
          `Use Pay Now to settle the ${monthLabel} rent securely.`,
        nextRentDueNoAction: (date: string) =>
          `Next rent is due on ${date}. No action needed until then.`,
        deposit: "Deposit",
        depositHeldRefunded:
          "Held securely and refunded at the end of your stay, minus any deductions.",
        rent: "Rent",
        monthlyRentUpper: "MONTHLY RENT",
        room: "Room",
        monthsOfRentPaid: "Months of rent paid",
        months: "months",
        paid: "paid",
        dueOn: "Due on",
        depositStatus: "Deposit status",
        oneTimeSecurityDeposit:
          "One-time security deposit held safely for your stay.",
        securityDeposit: "Security deposit",
        expected: "Expected",
        amountPaid: "Amount paid",
        paidOn: "Paid on",
        depositActivity: "Deposit activity",
        noDepositRecorded: "No deposit payment recorded yet.",
        depositOnlyEndOfTenancy:
          "Your deposit is only used at the end of your tenancy to cover any agreed deductions. It is not applied towards monthly rent.",
        refreshPaymentStatus: "Refresh payment status",
        payDeposit: "Pay Deposit",
        depositPayableWhenSigned:
          "Your deposit becomes payable once your contract is signed.",
        upcomingPayment: "Upcoming payment",
        amountDueLabel: "Amount due",
        reviewNextRentPaySecurely:
          "Review your next rent payment and pay securely with your card.",
        upcomingInvoiceProRata: "Pro-rata amount for this invoice.",
        rentLabel: "Rent",
        paidStatus: "Paid",
        overdueStatus: "Overdue",
        pendingStatus: "Pending",
        openStatus: "Open",
        partiallyPaidStatus: "Partially paid",
        paidOfBilled: (paid: string, billed: string) =>
          `${paid} of ${billed} paid`,
        openRentLabel: "Rent open",
        dunningFeesLabel: "Late fees",
        totalOpenLabel: "Total open",
        outstandingFeesLabel: "Outstanding late fees",
        rentFeeSplit: (rent: string, fees: string) =>
          `${rent} rent · ${fees} late fees`,
        feesByTransferNote:
          "Card and direct debit collect the rent only. Please pay the late fees by bank transfer.",
        bankTransferFeesNote: (fees: string, total: string) =>
          `Plus ${fees} in outstanding late fees – please transfer ${total} in total.`,
        sepaInProgressStatus: "SEPA in progress",
        sepaInProgressNote:
          "The direct debit has been submitted to your bank and will be collected within 2–5 business days. Nothing else for you to do — we email you again once the money has arrived.",
        payNow: "Pay Now",
        viewDetails: "View details",
        paymentCompleted: "Payment Completed ✓",
        oneTimeDepositUpdates:
          "One-time security deposit. Amount and status update when payments are recorded.",
        dueWhenSignedPayBelow:
          "Due once contract is signed. Pay below to complete.",
        depositHeldEndOfTenancy:
          "This amount is held securely and will be reviewed at the end of your tenancy. It is not used to pay your monthly rent.",
        manageHowCollected: "Manage how your rent payments are collected.",
        secure: "Secure",
        cardPaymentStripe: "Card payment via Stripe",
        cardDetailsCheckout: "Card details are added during checkout.",
        stripeAdditionalChargesNoteTitle: "Note",
        stripeAdditionalChargesNoteBody:
          "Additional charges may apply when paying by card via Stripe.",
        active: "Active",
        managePaymentMethod: "Manage payment method",
        last6Months: "Last 6 months",
        noPaymentsRecordedYet:
          "No payments recorded yet. Once your first payment is collected, it will appear here.",
        oneTime: "One-time",
        hereHowRentStructured:
          "Here's how your monthly rent is structured for your current room.",
        backToOverview: "Back to overview",
        baseRent: "Base rent",
        furnishedPrivateRoom:
          "Your furnished private room in a shared apartment.",
        utilitiesServices: "Utilities & services",
        electricityWaterWiFi:
          "Electricity, water, heating, Wi‑Fi and shared space cleaning.",
        totalMonthlyRent: "Total monthly rent",
        proRataFirstMonthNote: (_fullEuro: string) =>
          `Your first rent invoice may be lower (pro-rata by move-in date). The exact amount appears on your upcoming payment.`,
        depositHeldSeparate: (amount: string) =>
          `Your deposit of ${amount} is held in a separate account and cannot be used to pay your monthly rent. It will be reviewed and potentially refunded after your move-out inspection.`,
        nextCollectionDate: (date: string) =>
          `Next collection date: ${date}. You'll receive a receipt after each successful payment.`,
        collectionDate: "Collection date",
        receiptsHistory: "Receipts & history",
        availableInPaymentHistory: 'Available in "Payment history"',
        cancel: "Cancel",
        confirmPayment: "Confirm payment",
        reviewAndConfirmPayment: "Review & confirm payment",
        confirmPaymentTitle: "Confirm your payment",
        confirmPaymentSubtitle:
          "Review the summary below, then confirm to pay your upcoming rent securely with your card via Stripe.",
        firstRentCollection: "First rent collection",
        firstRentCollectionDesc:
          "This includes your first month of rent. Your deposit will be charged separately if still outstanding.",
        stripeProcessedStatement:
          "Your payment is processed securely by Stripe. You'll see the charge on your statement shortly after confirmation.",
        paymentSuccessful: "Payment successful",
        last12Payments: "Last 12 payments",
        allPastPaymentsHere:
          "All of your past rent and deposit payments will appear here once recorded.",
        paymentStatusRefreshed: "Payment status refreshed.",
        payByBankTransfer: "Pay by bank transfer",
        uploadPaymentProof: "Upload payment proof",
        uploadPaymentProofMandatory:
          "When paying by direct bank transfer, uploading payment proof is mandatory.",
        uploadStatement:
          "Upload bank statement or transfer receipt (PDF or image)",
        uploading: "Uploading…",
        uploadSuccess: "Payment proof uploaded. Review may take a moment.",
        proofPendingReview: "Pending review",
        proofApproved: "Approved",
        proofRejected: "Rejected",
        rejectionReason: "Rejection reason",
        viewPaymentProof: "View payment proof",
        paymentProofUploaded: "Payment proof uploaded",
        bankTransferNoAdditionalChargesNote:
          "No additional charges apply for bank transfers.",
        // Card fee disclosure
        cardFeeTitle: "Card payment — processing fee",
        cardFeeSubtotal: "Subtotal",
        cardFeeProcessing: "Card processing fee",
        cardFeeTotal: "Total",
        cardFeeExplainer:
          "Stripe charges a card processing fee which is passed on so coliviq receives the full rent or deposit.",
        continueToStripe: "Continue to Stripe",
        // SEPA direct debit mandate
        payBySepa: "Set up SEPA Direct Debit",
        sepaFormTitle: "SEPA Direct Debit Mandate",
        sepaCreditorHeading: "Creditor",
        sepaCreditorNameLabel: "Payee",
        sepaCreditorIdLabel: "Creditor ID",
        sepaMandateReferenceLabel: "Mandate reference (UMR)",
        sepaMandateReferencePending: "Will be issued after signing",
        sepaAuthorizationHeading: "SEPA Core Direct Debit Mandate",
        sepaAuthorizationText:
          "I authorise {creditor} to send instructions to my bank to debit my account, and my bank to debit my account in accordance with the instructions from {creditor}.",
        sepaRefundNoticeHeading: "Notice",
        sepaRefundNoticeText:
          "As part of your rights, you are entitled to a refund from your bank under the terms and conditions of your agreement with your bank. A refund must be claimed within 8 weeks starting from the date on which your account was debited.",
        sepaCustomerDataHeading: "Customer details",
        sepaSalutation: "Salutation",
        sepaMr: "Mr",
        sepaMrs: "Ms",
        sepaDivers: "Divers",
        sepaFirstName: "First name",
        sepaLastName: "Last name",
        sepaStreet: "Street",
        sepaHouseNumber: "No.",
        sepaPostalCode: "Postal code",
        sepaCity: "City",
        sepaCountry: "Country",
        sepaIban: "IBAN",
        sepaBic: "BIC (optional)",
        sepaAccountHolder: "Account holder",
        sepaSignatureHeading: "Signature",
        sepaSigningPlace: "Place",
        sepaSigningDate: "Date",
        sepaConfirmCheckbox:
          "I confirm the mandate and authorise the direct debit.",
        sepaTypedNameLabel: "Full name for signature",
        sepaTypedNameHelper:
          "Type your full name exactly as entered above (First Last) to sign the mandate.",
        sepaSubmit: "Authorise mandate",
        sepaSubmitting: "Authorising mandate…",
        sepaMandatoryField: "Please fill in all mandatory fields.",
        sepaIbanInvalid: "Invalid IBAN.",
        sepaTypedNameMismatch:
          "The typed name does not match your first and last name.",
        sepaSuccessTitle: "SEPA mandate authorised",
        sepaSuccessBody: (ref: string) =>
          `Your SEPA mandate is active. Mandate reference: ${ref}`,
        sepaMandateActiveBadge: "SEPA active",
        sepaActiveAutoDebitNote:
          "Rent is debited automatically from your registered account on the due date — nothing else for you to do.",
        sepaAutoDebitPayNowNote:
          "Note: an active SEPA mandate is on file — rent is debited automatically on the due date. Paying by card is only needed if you don't want to wait for the debit.",
        sepaGenericError: "Could not save the mandate.",
      };
}

/**
 * Rent statuses shown on the Home room card (feedback 10). The card's headline
 * badge used to render the onboarding stage, whose last stage is "Paid" — and
 * that stage flips as soon as ANY payment for the booking is paid, which in
 * practice is the deposit. A tenant overdue on rent was therefore greeted with
 * a green "Paid". Rent now carries its own status here and the deposit keeps
 * its own on the deposit card.
 */
const RENT_STATUSES = ["paid", "open", "overdue", "sepa_in_progress"] as const;

const RENT_STATUS_BADGE_CLASSES: Record<
  (typeof RENT_STATUSES)[number],
  string
> = {
  paid: "bg-emerald-50 text-emerald-700 border-emerald-200",
  open: "bg-amber-50 text-amber-700 border-amber-200",
  overdue: "bg-red-50 text-red-700 border-red-200",
  sepa_in_progress: "bg-sky-50 text-sky-700 border-sky-200",
};

/** Home tab labels (Your rooms, status flow, stage names) – used when switching language */
function getHomeLabels(lang: "en" | "de") {
  const en = {
    yourRooms: "Your rooms",
    statusFlow:
      "Status flow: Applied → Awaiting approval → Ready to sign → Contract signed → Deposit pending → Paid",
    stages: [
      "Applied",
      "Awaiting approval",
      "Ready to sign",
      "Contract signed",
      "Deposit pending",
      "Paid",
    ] as const,
    noRoomsYet:
      "No rooms yet. Your bookings will appear here with their current stage.",
    room: "room",
    rooms: "rooms",
    moveIn: "Move-in",
    moveOut: "Move-out",
    contractActionTitle: "Your contract needs attention",
    contractActionDesc:
      "Review and sign in DocuSign, then pay your security deposit on the Contract tab when it becomes due.",
    openMyContract: "Open My Contract",
    dateChangeNote:
      "You can update move-in and move-out dates here until your contract is signed. After signing, dates are fixed. The stay cannot be shorter than 6 months.",
    dateLockedNote:
      "Your contract is signed — move dates can no longer be changed here.",
    editDates: "Change dates",
    saveDates: "Save dates",
    moveInPicker: "Move-in date",
    moveOutPicker: "Move-out date",
    datesUpdated: "Stay dates updated.",
    dateValidationError: "Move-out must be at least six months after move-in.",
    stayDatesDialogTitle: "Your stay dates",
  };
  const de = {
    yourRooms: "Deine Zimmer",
    statusFlow:
      "Status: Beworben → Wartet auf Freigabe → Bereit zum Unterschreiben → Vertrag unterschrieben → Kaution ausstehend → Bezahlt",
    stages: [
      "Beworben",
      "Wartet auf Freigabe",
      "Bereit zum Unterschreiben",
      "Vertrag unterschrieben",
      "Kaution ausstehend",
      "Bezahlt",
    ] as const,
    noRoomsYet:
      "Noch keine Zimmer. Deine Buchungen erscheinen hier mit ihrem aktuellen Status.",
    room: "Zimmer",
    rooms: "Zimmer",
    moveIn: "Einzug",
    moveOut: "Auszug",
    contractActionTitle: "Dein Vertrag wartet auf dich",
    contractActionDesc:
      "Prüfe und unterschreibe in DocuSign; die Kaution zahlst du auf dem Tab „Mein Vertrag“, sobald sie fällig ist.",
    openMyContract: "Zu Mein Vertrag",
    dateChangeNote:
      "Ein- und Auszugsdatum können Sie hier ändern, bis Ihr Vertrag unterschrieben ist. Danach sind die Daten fest. Die Mindestmietdauer beträgt 6 Monate.",
    dateLockedNote:
      "Vertrag unterschrieben — die Daten können hier nicht mehr geändert werden.",
    editDates: "Daten ändern",
    saveDates: "Speichern",
    moveInPicker: "Einzugsdatum",
    moveOutPicker: "Auszugsdatum",
    datesUpdated: "Aufenthaltsdaten aktualisiert.",
    dateValidationError:
      "Auszug muss mindestens sechs Monate nach Einzug liegen.",
    stayDatesDialogTitle: "Ihre Aufenthaltsdaten",
  };
  return lang === "de" ? de : en;
}

function isBookingContractSigned(b: {
  contract_signed_at?: string | null;
  contract_status?: string | null;
}) {
  if (b.contract_signed_at) return true;
  return String(b.contract_status ?? "").toLowerCase() === "signed";
}

/**
 * Has no contract been handed to this tenant yet?
 *
 * `not_ready` = nothing generated. `draft` = an admin has generated the PDF but
 * has NOT released it: they read it, verify it, and send it in a separate
 * confirmed step. Both look identical from the tenant's side ("being
 * prepared") — a draft must never surface a signing link.
 */
function isContractNotReleasedToTenant(
  contractStatus?: string | null
): boolean {
  const cs = String(contractStatus ?? "")
    .trim()
    .toLowerCase();
  return cs === "" || cs === "not_ready" || cs === "draft";
}

const Dashboard = () => {
  const { t, language } = useLanguage();
  // Narrowed to the base language: this file's helpers predate the six-language
  // widening and still take en|de. (COL-477 port.)
  const baseLang = baseLanguageFor(language);
  // Payment tab labels — must be declared before any effect that references `p` (avoid TDZ: "Cannot access 'p' before initialization").
  const p = getPaymentsLabels(baseLang);
  const {
    user,
    isOwnerPortalOnly,
    isDemoDualViewAccount,
    isDemoDualViewReady,
    loading: authLoading,
    resetPasswordForEmail,
  } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    // The demo account (COL-447) is deliberately exempt: it owns the hidden
    // demo property AND holds a booking in it, so the tenant dashboard is a
    // real view of its own data, not a bypass. Every other owner-portal-only
    // account still bounces exactly as before.
    //
    // Wait for the demo flag as well as the roles. Roles resolve first, so
    // deciding on them alone would bounce the demo account off its own tenant
    // dashboard on a cold load or a token refresh, before the flag landed.
    if (
      !authLoading &&
      isDemoDualViewReady &&
      isOwnerPortalOnly &&
      !isDemoDualViewAccount
    ) {
      navigate("/admin?tab=analytics", { replace: true });
    }
  }, [
    authLoading,
    isOwnerPortalOnly,
    isDemoDualViewAccount,
    isDemoDualViewReady,
    navigate,
  ]);
  const isMobile = useIsMobile();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState("home");
  // Switching tabs is "opening a new page" for the tenant: land at the top, not
  // wherever the tile grid had been scrolled to (referral feedback item 9).
  useScrollToTopOnChange(activeTab);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [paymentStep, setPaymentStep] = useState<
    | "overview"
    | "method"
    | "breakdown"
    | "confirm"
    | "success"
    | "history"
    | "bank_transfer_upload"
  >("overview");
  const [bankTransferPayment, setBankTransferPayment] = useState<any>(null);
  /** Session-local echo of the picked payment method (feedback 1) — the
   *  booking row is the durable copy, this keeps the radio instant. */
  const [payMethodChoice, setPayMethodChoice] = useState<
    "sepa_debit" | "card" | "bank_transfer" | null
  >(null);
  const [paymentProofFiles, setPaymentProofFiles] = useState<File[]>([]);
  const [paymentProofUploadProgress, setPaymentProofUploadProgress] =
    useState(0);
  const [paymentProofUploading, setPaymentProofUploading] = useState(false);
  const [isSubmittingSepa, setIsSubmittingSepa] = useState(false);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [isProcessingDepositPayment, setIsProcessingDepositPayment] =
    useState(false);
  const [processingPaymentBookingId, setProcessingPaymentBookingId] = useState<
    number | null
  >(null);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<
    "sepa" | "bank_transfer"
  >("sepa");
  const [sepaForm, setSepaForm] = useState({
    accountName: "",
    iban: "",
    bic: "",
    acceptsMandate: false,
  });

  // Card-fee disclosure dialog. `confirmCardFee` returns a promise that resolves
  // true when the tenant accepts the fee and clicks "Continue to Stripe", false
  // otherwise. Used by all `create-card-checkout` callsites.
  const [cardFeeDialog, setCardFeeDialog] = useState<null | {
    netCents: number;
    resolve: (ok: boolean) => void;
  }>(null);
  const confirmCardFee = useCallback(
    (netCents: number): Promise<boolean> =>
      new Promise((resolve) => {
        setCardFeeDialog({ netCents, resolve });
      }),
    []
  );
  const closeCardFeeDialog = useCallback((confirmed: boolean) => {
    setCardFeeDialog((prev) => {
      prev?.resolve(confirmed);
      return null;
    });
  }, []);

  // SEPA mandate form visibility (entry point shown alongside Pay with card /
  // Pay by bank transfer when sepa_debit_enabled for the booking's room).
  const [sepaMandateOpen, setSepaMandateOpen] = useState<null | {
    bookingId: number | null;
  }>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  // Tab ids stay as they were even though two labels were renamed
  // ("profile" → Personal information, "password" → Login): they are part of
  // the /dashboard?settings=<tab> deep link Navigation.tsx builds and of
  // useTenantSettingsAlert's return type. The link handler below also accepts
  // the new names as aliases.
  const [settingsTab, setSettingsTab] = useState<
    "profile" | "documents" | "language" | "password"
  >("profile");
  // Settings → Login: self-service reset link, same edge function the
  // /forgot-password page uses. Nothing is changed in-place; the tenant
  // sets the new password from the emailed link.
  const identityUploadInputRef = useRef<HTMLInputElement | null>(null);
  const salaryUploadInputRef = useRef<HTMLInputElement | null>(null);
  const [uploadingDocs, setUploadingDocs] = useState<{
    [key: string]: boolean;
  }>({});
  const [openingDocuSign, setOpeningDocuSign] = useState(false);
  const [sendingToDocuSign, setSendingToDocuSign] = useState(false);
  const [signingUrlJustSent, setSigningUrlJustSent] = useState<{
    bookingId: string;
    url: string;
  } | null>(null);
  const [justSentToDocuSignBookingId, setJustSentToDocuSignBookingId] =
    useState<string | null>(null);
  const [docuSignConfirmOpen, setDocuSignConfirmOpen] = useState(false);
  /** Shown in Next step when DocuSign returns ?event=ttl_expired (session link expired). */
  const [docuSignSessionExpiredNotice, setDocuSignSessionExpiredNotice] =
    useState(false);
  const pendingDocuSignActionRef = useRef<(() => void) | null>(null);
  const [signedContractUploadProgress, setSignedContractUploadProgress] =
    useState(0);
  const [signedContractUploadBookingId, setSignedContractUploadBookingId] =
    useState<number | null>(null);
  const lastSyncedEnvelopeBookingIdRef = useRef<number | null>(null);
  /** Tracks booking contract version + envelope so we clear TTL UI after admin regenerate (new PDF / void envelope). */
  const docuSignBookingMetaRef = useRef<{
    bookingId: number | null;
    contractVersion: number | null;
    envelopeId: string | null;
  }>({ bookingId: null, contractVersion: null, envelopeId: null });
  const [isEditingProfile, setIsEditingProfile] = useState(false);

  /** Open DocuSign URL. On mobile, window.open often opens an in-app tab that loads the app (Vercel) and shows login; we navigate the current tab to DocuSign so it opens in the system browser or DocuSign app. */
  const openDocuSignUrl = useCallback(
    (url: string): boolean => {
      if (isMobile) {
        toast.info(t("dashboard.openingDocuSign", "Opening DocuSign…"));
        window.location.href = url;
        return false;
      }
      const w = window.open(url, "_blank", "noopener,noreferrer");
      if (!w || w.closed) {
        toast.info(t("dashboard.openingInThisTab", "Opening in this tab."));
        window.location.href = url;
        return false;
      }
      return true;
    },
    [isMobile, t]
  );

  /** Clears TTL banner and `event` / `docusign` query flags before opening a fresh DocuSign session (or when booking is replaced server-side). */
  const clearDocuSignTtlState = useCallback(() => {
    setDocuSignSessionExpiredNotice(false);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("event");
        next.delete("docusign");
        return next;
      },
      { replace: true }
    );
  }, [setSearchParams]);

  const [profileForm, setProfileForm] = useState({
    name: "",
    email: "",
    phone: "",
    dateOfBirth: "",
  });
  // Date-of-birth gate shown before the DocuSign confirm dialog: the tenant
  // confirms/inserts their DoB, which is persisted to their profile and flows
  // into the contract PDF (COL-258).
  const [dobConfirmOpen, setDobConfirmOpen] = useState(false);
  const [dobInput, setDobInput] = useState("");
  const [savingDob, setSavingDob] = useState(false);
  /** True while transitioning DoB gate → DocuSign confirm, so closing the DoB dialog doesn't clear the pending action. */
  const dobProceedingRef = useRef(false);
  const [stayDatesOpen, setStayDatesOpen] = useState(false);
  const [stayDatesBookingId, setStayDatesBookingId] = useState<number | null>(
    null
  );
  const [stayDatesRoomTitle, setStayDatesRoomTitle] = useState("");
  const [stayDatesMoveIn, setStayDatesMoveIn] = useState("");
  const [stayDatesMoveOut, setStayDatesMoveOut] = useState("");
  const [stayDatesMoveInOpen, setStayDatesMoveInOpen] = useState(false);
  const [stayDatesMoveOutOpen, setStayDatesMoveOutOpen] = useState(false);
  const [selectedIdentityFiles, setSelectedIdentityFiles] = useState<File[]>(
    []
  );
  const [selectedSalaryFiles, setSelectedSalaryFiles] = useState<File[]>([]);
  const [homeRoomsPage, setHomeRoomsPage] = useState(1);
  const [favoritesPage, setFavoritesPage] = useState(1);
  const FAVORITES_ROOMS_PER_PAGE = 3;

  // Fetch user documents
  const { data: identityDocuments } = useQuery<any[]>({
    queryKey: ["identity-documents", user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      const { data, error } = await supabase
        .from("tenant_documents")
        .select("*")
        .eq("user_id", user.id)
        .eq("document_type", "id_passport")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data || [];
    },
    enabled: !!user?.id,
  });

  const { data: salaryDocuments } = useQuery<any[]>({
    queryKey: ["salary-documents", user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      const { data, error } = await supabase
        .from("tenant_documents")
        .select("*")
        .eq("user_id", user.id)
        .eq("document_type", "proof_of_income")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data || [];
    },
    enabled: !!user?.id,
  });

  // Only a rejected doc (with an admin note) is tenant-actionable — re-upload.
  // A doc pending admin review must NOT badge Settings: there is nothing the
  // tenant can do about it (COL-260). The Documents tab still shows the
  // informational "under review" notice via documentIssueKind.
  const hasDocumentNotifications = useMemo(() => {
    const allDocs = [...(identityDocuments || []), ...(salaryDocuments || [])];
    return allDocs.some((doc: any) => doc.status === "rejected" && doc.notes);
  }, [identityDocuments, salaryDocuments]);

  const getLocalLastRead = useCallback(
    (scope: "tenant" | "admin", userId: string, issueId: string) => {
      try {
        return window.localStorage.getItem(
          `support:lastRead:${scope}:${userId}:${issueId}`
        );
      } catch {
        return null;
      }
    },
    []
  );

  // Tracks the last status the tenant has acknowledged for each ticket, so the
  // Maintenance tab pip can fire when admin moves a card (todo→pending→closed)
  // even before any chat message exists. Cleared in bulk when the tenant opens
  // the Maintenance tab — same pattern WhatsApp uses for chat list unread counts.
  const getLocalStatusSeen = useCallback((userId: string, issueId: string) => {
    try {
      return window.localStorage.getItem(
        `support:lastStatusSeen:tenant:${userId}:${issueId}`
      );
    } catch {
      return null;
    }
  }, []);

  const setLocalStatusSeen = useCallback(
    (userId: string, issueId: string, status: string) => {
      try {
        window.localStorage.setItem(
          `support:lastStatusSeen:tenant:${userId}:${issueId}`,
          status
        );
      } catch {
        /* localStorage can be disabled (private mode); ignore — pip just won't
           clear, which is a minor UX nit, not a crash. */
      }
    },
    []
  );

  const { data: tenantIssueRows } = useQuery({
    queryKey: ["tenant", "issues", "ids", user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      const { data, error } = await supabase
        .from("issues")
        .select("id,status,updated_at")
        .eq("user_id", user.id);
      if (error) throw error;
      return (data || []).map((r: any) => ({
        id: String(r.id),
        status: String(r.status ?? ""),
        updated_at: String(r.updated_at ?? ""),
      })) as Array<{ id: string; status: string; updated_at: string }>;
    },
    enabled: !!user?.id,
    staleTime: 15_000,
  });

  // Note: a previous `tenantStatusUnread` memo (status-change notifications) was
  // removed when the support badge was changed to count unread chat messages
  // only. Status flips already post a chat message ("Status updated to …"),
  // which the chat unread scan picks up — so adding a second counter here
  // would double-count the same event.

  const {
    data: tenantSupportUnreadTotal,
    refetch: refetchTenantSupportUnread,
  } = useQuery({
    queryKey: [
      "tenant",
      "issues",
      "support-unread-total",
      user?.id,
      (tenantIssueRows || []).length,
      // tenantStatusUnread is intentionally NOT a key dep anymore — the badge
      // now reflects unread chat messages only (admin status flips already
      // post a chat message like "Status updated to RESOLVED", which the chat
      // unread scan picks up; adding tenantStatusUnread on top double-counted).
    ],
    queryFn: async () => {
      if (!user?.id) return 0;
      const ids = (tenantIssueRows || []).map((r) => r.id);
      if (!ids.length) return 0;

      // The badge MUST equal the sum of per-card unread badges shown in the
      // ticket list. Per-card uses `Math.max(rpc, local)` PER ticket then
      // sums; if we take the max of GLOBAL totals here instead, RPC missing
      // ticket A while local catches it AND local missing ticket B while RPC
      // catches it can yield rpcTotal=1, localTotal=1, max=1 — but the per-
      // card sum is 2. Mirror the per-card aggregation here so the two are
      // always identical.
      const localPerIssue = new Map<string, number>();
      for (const id of ids) localPerIssue.set(String(id), 0);
      try {
        const { data: rows } = await supabase
          .from("issue_comments")
          .select("issue_id,user_id,created_at,is_internal,sender_scope")
          .in("issue_id", ids)
          .order("created_at", { ascending: false });
        // sender_scope is text in the DB; narrow it to the values it holds.
        for (const r of (rows || []) as Array<{
          issue_id: string;
          user_id: string;
          created_at: string;
          is_internal: boolean | null;
          sender_scope: "tenant" | "admin" | null;
        }>) {
          if (!r?.issue_id) continue;
          if (r.is_internal) continue;
          const scope = r.sender_scope;
          if (scope === "tenant") continue;
          if (scope == null && r.user_id === user.id) continue;
          const last = getLocalLastRead("tenant", user.id, r.issue_id);
          const lastMs = last ? Date.parse(last) : 0;
          const msgMs = Date.parse(r.created_at);
          if (msgMs > lastMs) {
            const key = String(r.issue_id);
            localPerIssue.set(key, (localPerIssue.get(key) || 0) + 1);
          }
        }
      } catch (e) {
        console.warn("Local unread scan failed (wrench badge):", e);
      }

      try {
        const { data, error } = await (
          supabase as unknown as {
            rpc: (fn: string, args: any) => Promise<any>;
          }
        ).rpc("get_issue_unread_counts", {
          issue_ids: ids,
          include_internal: false,
        });
        if (error) throw error;
        // Per-issue max(rpc, local) → sum. Same shape as the ticket-list query.
        const merged = new Map<string, number>(localPerIssue);
        for (const row of (data || []) as Array<{
          issue_id: string;
          unread_count: number;
        }>) {
          const key = String(row.issue_id);
          const rpcCount = Number(row.unread_count || 0);
          const localCount = merged.get(key) || 0;
          merged.set(key, Math.max(rpcCount, localCount));
        }
        let total = 0;
        for (const v of merged.values()) total += v || 0;
        return total;
      } catch (e) {
        let total = 0;
        for (const v of localPerIssue.values()) total += v || 0;
        return total;
      }
    },
    enabled: !!user?.id,
    staleTime: 10_000,
    retry: false,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
  });

  // Realtime: auto-bump the Maintenance tab badge when admin replies or a new
  // ticket is created under this user.
  useEffect(() => {
    if (!user?.id) return;
    const channel = supabase
      .channel("tenant-support-unread")
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "issue_comments" },
        () => {
          queryClient.invalidateQueries({
            queryKey: ["tenant", "issues", "support-unread-total"],
          });
          queryClient.invalidateQueries({
            queryKey: ["tenant", "issues", "issue-unread"],
          });
          void refetchTenantSupportUnread();
        }
      )
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "issues" },
        () => {
          queryClient.invalidateQueries({
            queryKey: ["tenant", "issues", "ids"],
          });
          queryClient.invalidateQueries({
            queryKey: ["tenant", "issues", "support-unread-total"],
          });
        }
      )
      // Status flips (todo→pending→closed) come through as UPDATEs. Without this
      // listener the Maintenance pip would only animate when the tenant reloaded —
      // breaking the WhatsApp-style live-notification feel.
      .on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "issues" },
        () => {
          queryClient.invalidateQueries({
            queryKey: ["tenant", "issues", "ids"],
          });
          queryClient.invalidateQueries({
            queryKey: ["tenant", "issues", "support-unread-total"],
          });
          void refetchTenantSupportUnread();
        }
      )
      .subscribe();
    return () => {
      try {
        supabase.removeChannel(channel);
      } catch {
        /* ignore */
      }
    };
  }, [user?.id, queryClient, refetchTenantSupportUnread]);

  /*
    Upload documents mutation.

    The storage + insert loop now lives in `uploadTenantDocuments` so the V2
    documents card can offer the same "add a file" without becoming a second
    writer to `tenant_documents` (COL-533). Same behaviour, same per-file
    error shape — this is a move, not a rewrite.
  */
  const uploadDocumentsMutation = useMutation({
    mutationFn: async ({
      files,
      documentType,
      bookingId,
    }: {
      files: File[];
      documentType: TenantDocumentType;
      bookingId?: number;
    }) =>
      uploadTenantDocuments({
        files,
        documentType,
        bookingId,
        userId: user?.id ?? "",
        unknownErrorLabel: t("dashboard.docs.unknownError", "Unknown error"),
      }),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["identity-documents", user?.id],
      });
      queryClient.invalidateQueries({
        queryKey: ["salary-documents", user?.id],
      });

      const successCount = data.uploaded?.length || 0;
      const errorCount = data.errors?.length || 0;

      if (successCount > 0) {
        const docTypeLabel =
          variables.documentType === "id_passport"
            ? t("dashboard.identityDocumentsLabel", "identity document(s)")
            : t("dashboard.salaryStatementsLabel", "salary statement(s)");
        toast.success(
          `${successCount} ${docTypeLabel} ${t("dashboard.uploadedSuccessfully", "uploaded successfully!")}`
        );
      }

      if (errorCount > 0) {
        toast.warning(
          `${errorCount} ${t("dashboard.filesFailedToUpload", "file(s) failed to upload. Please try again.")}`
        );
      }

      // Clear uploading state for all files in a single batch update
      setUploadingDocs((prev) => {
        const newState = { ...prev };
        variables.files.forEach((file) => {
          const key = `${variables.documentType}-${file.name}`;
          delete newState[key];
        });
        return newState;
      });
    },
    onError: (error: any, variables) => {
      toast.error(
        error.message ||
          t("dashboard.failedToUploadDocuments", "Failed to upload documents")
      );

      // Clear uploading state for all files in a single batch update
      setUploadingDocs((prev) => {
        const newState = { ...prev };
        variables.files.forEach((file) => {
          const key = `${variables.documentType}-${file.name}`;
          delete newState[key];
        });
        return newState;
      });
    },
  });

  // Delete document mutation
  const deleteDocumentMutation = useMutation({
    mutationFn: async ({
      documentId,
      fileUrl,
    }: {
      documentId: string;
      fileUrl?: string;
    }) => {
      /*
        The ROW goes first, through an RPC, and the stored file follows.

        This used to delete the file first and then the row directly. The row
        delete matched nothing — `tenant_documents` has INSERT and SELECT
        policies for users and no DELETE — and PostgREST reports a delete that
        matched nothing as success. So the file was removed, the row survived,
        and the tenant was told it had worked: the document stayed in the list
        pointing at a file that no longer existed.

        `delete_tenant_document` is the same remedy `rename_tenant_document`
        already is for the missing UPDATE policy. It raises on a refusal
        instead of quietly doing nothing, so an approved document or someone
        else's row now fails loudly.

        Row first on purpose: if the object delete then fails, the tenant is
        left with an orphaned file in a bucket — invisible and cheap — rather
        than a row they cannot remove pointing at nothing.
      */
      const { data: deleted, error } = await supabase.rpc(
        "delete_tenant_document",
        { _document_id: documentId }
      );
      if (error) throw error;

      const storedUrl =
        (deleted as { file_url?: string } | null)?.file_url ?? fileUrl;
      const fileRef = storedUrl ? storageRefFor(storedUrl, "contracts") : null;
      if (fileRef) {
        // Best effort. The record is already gone, which is what the tenant
        // sees; a surviving object is a storage cost, not a broken screen.
        await removeStoredFile(fileRef);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["identity-documents", user?.id],
      });
      queryClient.invalidateQueries({
        queryKey: ["salary-documents", user?.id],
      });
      toast.success(
        t(
          "dashboard.documentDeletedSuccessfully",
          "Document deleted successfully"
        )
      );
    },
    onError: (error: any) => {
      /*
        `CV403` is the RPC's approved-document refusal. The button is not
        offered for an approved document, so this is the race: an admin
        approved it between render and click. Matched on the SQLSTATE rather
        than the message, which is English and may be reworded.
      */
      if (error?.code === "CV403") {
        toast.error(
          t(
            "dashboard.docs.approvedNeedsStaff",
            "This document has been approved. Ask us to remove it."
          )
        );
        return;
      }
      toast.error(
        error.message ||
          t("dashboard.failedToDeleteDocument", "Failed to delete document")
      );
    },
  });

  const handleFileSelect = (
    files: FileList | null,
    documentType: "id_passport" | "proof_of_income"
  ) => {
    if (!files || files.length === 0) return;
    const fileArray = Array.from(files);
    if (documentType === "id_passport") {
      setSelectedIdentityFiles((prev) => [...prev, ...fileArray]);
    } else {
      setSelectedSalaryFiles((prev) => [...prev, ...fileArray]);
    }
  };

  const handleSaveDocuments = async (
    documentType: "id_passport" | "proof_of_income"
  ) => {
    const files =
      documentType === "id_passport"
        ? selectedIdentityFiles
        : selectedSalaryFiles;
    if (files.length === 0) {
      toast.error(
        t(
          "dashboard.selectAtLeastOneFile",
          "Please select at least one file to upload"
        )
      );
      return;
    }

    // Set uploading state for all files in a single batch update
    setUploadingDocs((prev) => {
      const newState = { ...prev };
      files.forEach((file) => {
        const key = `${documentType}-${file.name}`;
        newState[key] = true;
      });
      return newState;
    });

    // Get active booking ID if available
    const activeBookingId = booking?.id;

    // Upload all files in batch
    uploadDocumentsMutation.mutate({
      files,
      documentType,
      bookingId: activeBookingId,
    });

    // Clear selected files after starting upload
    if (documentType === "id_passport") {
      setSelectedIdentityFiles([]);
    } else {
      setSelectedSalaryFiles([]);
    }
  };

  const handleRemoveSelectedFile = (
    index: number,
    documentType: "id_passport" | "proof_of_income"
  ) => {
    if (documentType === "id_passport") {
      setSelectedIdentityFiles((prev) => prev.filter((_, i) => i !== index));
    } else {
      setSelectedSalaryFiles((prev) => prev.filter((_, i) => i !== index));
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (
    e: React.DragEvent,
    documentType: "id_passport" | "proof_of_income"
  ) => {
    e.preventDefault();
    e.stopPropagation();
    handleFileSelect(e.dataTransfer.files, documentType);
  };

  // Fetch active SEPA mandate / payment method
  const { data: sepaMethod } = useQuery<any | null>({
    queryKey: ["payment-methods", user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      const { data, error } = await supabase
        .from("payment_methods")
        .select("*")
        .eq("user_id", user.id)
        .eq("type", "sepa_debit")
        .eq("status", "active")
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!user?.id,
  });

  const hasActiveMandate = !!sepaMethod;

  // Fetch current active booking for rent info (confirmed or pending bookings) with room and property
  const { data: booking } = useQuery<any | null>({
    queryKey: ["active-booking", user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      const { data, error } = await supabase
        .from("bookings")
        .select(
          `
          *,
          rooms (
            id,
            title,
            price,
            deposit_amount,
            properties (
              id,
              title,
              address,
              zip_code,
              city
            )
          )
        `
        )
        .eq("user_id", user.id)
        .is("admin_archived_at", null)
        // Include admin/manual statuses ("active"/"approved"), not just the
        // self-serve flow ones, so manually-created tenants resolve an active
        // booking and see their rent/deposit instead of €0.
        // Deliberately NOT TENANT_LIVE_BOOKING_STATUSES: that list also covers
        // `notice_given`/`move_out_pending`, and widening this card's status set
        // would change what two tenants currently in move-out see on their rent
        // panel. That is a separate call from fixing the protocol page, so this
        // keeps its own set and takes only the shared RANKING below.
        .in("status", ["confirmed", "pending", "active", "approved"]);
      if (error) throw error;
      // Ranked by the shared resolver rather than "newest row wins", so this
      // card and the protocol page can never describe different bookings.
      return pickLiveTenantBooking(
        (data ?? []) as unknown as LiveBookingCandidate[]
      ) as any;
    },
    enabled: !!user?.id,
  });

  // The room's legacy payment-config row. Only its bank_* columns are read now
  // (as the last fallback for the transfer account); the three method flags on
  // it are one level of the chain and are resolved by the RPC below, never here.
  const roomIdForConfig = booking?.rooms?.id;
  const { data: paymentConfig } = useQuery<any | null>({
    queryKey: ["room-payment-config", roomIdForConfig],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("room_payment_config")
        .select("*")
        .eq("room_id", roomIdForConfig)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!roomIdForConfig,
  });

  // Which methods this room actually accepts. get_room_payment_methods resolves
  // room override → apartment → property → platform default and applies the
  // owner_collects clamp, and the SEPA charge functions ask the same function —
  // so the buttons offered here cannot disagree with what gets charged.
  const { data: paymentMethods } = useQuery<{
    stripe_card_enabled: boolean;
    bank_transfer_enabled: boolean;
    sepa_debit_enabled: boolean;
  } | null>({
    queryKey: ["room-payment-methods", roomIdForConfig],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_room_payment_methods", {
        p_room_id: roomIdForConfig,
      });
      if (error) throw error;
      return Array.isArray(data) ? (data[0] ?? null) : (data ?? null);
    },
    enabled: !!roomIdForConfig,
  });

  // Resolve the single account rent flows into for this room — owner or
  // CoLiviq, decided by the apartment's collection mode (COL-225). This is the
  // only account the tenant should pay; a SECURITY DEFINER RPC returns just the
  // resolved account, never the admin-only financial tables.
  const { data: rentAccount } = useQuery<{
    party: string;
    collection_mode: string;
    holder: string | null;
    iban: string | null;
    bic: string | null;
    bank_name: string | null;
  } | null>({
    queryKey: ["room-rent-account", roomIdForConfig],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_room_rent_account", {
        p_room_id: roomIdForConfig,
      });
      if (error) throw error;
      return Array.isArray(data) ? (data[0] ?? null) : (data ?? null);
    },
    enabled: !!roomIdForConfig,
  });

  // owner_collects: rent goes to the owner directly. The platform card / SEPA
  // rails settle into CoLiviq's Stripe (no Stripe Connect yet), so they would
  // misroute the money — until Connect exists, bank transfer to the owner is
  // the only valid path. We force bank transfer on and gate card/SEPA off.
  const ownerCollects = rentAccount?.collection_mode === "owner_collects";

  // Transfer is the only rail that works under owner_collects, and the resolver
  // forces it on there. Before it answers, keep the old client-side reading so a
  // tenant mid-load is never told there is no way to pay.
  const bankTransferEnabled = paymentMethods
    ? paymentMethods.bank_transfer_enabled === true
    : paymentConfig?.bank_transfer_enabled === true || ownerCollects;

  // Operator payment boundary (COL-353 / PR-8): an external operator's tenant
  // never pays through CoLiviq's Stripe — same reasoning as owner_collects.
  // The DB trigger already forces the config flags off wherever a config row
  // EXISTS; this branch closes the no-config-row case, where the card rail
  // would otherwise default to enabled below. Keyed off is_platform via the
  // helper RPC, never the literal platform UUID.
  const { data: platformOperatorId } = useQuery<string | null>({
    queryKey: ["platform-operator-id"],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("platform_operator_id");
      if (error) throw error;
      return (data as string | null) ?? null;
    },
    staleTime: Infinity,
  });
  const externalOperatorBooking =
    !!booking?.operator_id &&
    !!platformOperatorId &&
    booking.operator_id !== platformOperatorId;

  // Pay-by-transfer destination. get_room_rent_account is the single source of
  // truth for WHICH account rent flows into for this room: it resolves the
  // room-level bank override (room_bank_overrides — set in the admin "Lessor &
  // bank" tab, shown there as "Override active") first, then the
  // apartment/property/lessor/owner account. That resolved account wins here so
  // the tenant sees exactly what the admin sees; the legacy room_payment_config
  // bank_* fields are only a fallback for rooms configured the old way. The
  // hardcoded CoLiviq account is a last resort ONLY for company_collects — for
  // owner_collects we never fall back to it (that would pay CoLiviq, not the
  // owner); if the owner account isn't configured we surface nothing valid.
  const bankTransferDetails = useMemo(() => {
    if (!bankTransferEnabled) return null;

    // Select the destination account as a whole, most-authoritative first, so
    // we never mix an IBAN from one source with a bank name from another.
    const rpcIban = (rentAccount?.iban ?? "").replace(/\s/g, "");
    const cfgIban = (paymentConfig?.bank_iban ?? "").replace(/\s/g, "");

    let acct: {
      accountHolder: string;
      iban: string;
      bic: string;
      bankName: string;
    };
    if (rpcIban) {
      acct = {
        accountHolder: (rentAccount?.holder ?? "").trim(),
        iban: rpcIban,
        bic: (rentAccount?.bic ?? "").trim(),
        bankName: (rentAccount?.bank_name ?? "").trim(),
      };
    } else if (cfgIban) {
      acct = {
        accountHolder: (paymentConfig?.bank_account_holder ?? "").trim(),
        iban: cfgIban,
        bic: (paymentConfig?.bank_bic ?? "").trim(),
        bankName: (paymentConfig?.bank_name ?? "").trim(),
      };
    } else if (!ownerCollects) {
      acct = { ...COLIVIQ_DEFAULT_BANK_TRANSFER };
    } else {
      // owner_collects but the owner bank isn't set up yet — nothing valid.
      return null;
    }

    return {
      ...acct,
      // Who the money is going to ("Owner" / "CoLiviq"), for labelling.
      party: rentAccount?.party ?? "coliviq",
    };
  }, [paymentConfig, rentAccount, ownerCollects, bankTransferEnabled]);

  // Card / SEPA settle into CoLiviq's Stripe, so the resolver disables them for
  // owner_collects. Until it answers, keep the old client-side reading (like
  // bankTransferEnabled above) rather than assuming the unconfigured-room
  // default: a room that already loaded room_payment_config with card off
  // must not show it as payable just because the newer RPC hasn't answered
  // yet — create-card-checkout has no server-side gate of its own.
  // External operators' bookings (COL-353 / PR-8) are gated off on top: the
  // resolver knows nothing about operators, and their rent must never settle
  // into CoLiviq's Stripe.
  const stripeCardEnabled =
    !externalOperatorBooking &&
    (paymentMethods
      ? paymentMethods.stripe_card_enabled === true
      : !ownerCollects && paymentConfig?.stripe_card_enabled !== false);
  const sepaDebitEnabled =
    !externalOperatorBooking && paymentMethods?.sepa_debit_enabled === true;

  // Clear "just sent" state once booking has envelope (e.g. after refetch)
  useEffect(() => {
    if (
      booking?.docusign_envelope_id &&
      String(booking.id) ===
        (signingUrlJustSent?.bookingId ?? justSentToDocuSignBookingId)
    ) {
      setSigningUrlJustSent(null);
      setJustSentToDocuSignBookingId(null);
    }
  }, [
    booking?.id,
    booking?.docusign_envelope_id,
    signingUrlJustSent?.bookingId,
    justSentToDocuSignBookingId,
  ]);

  // After admin regenerates (new contract_version and/or voided envelope), drop stale TTL warning so tenant isn’t stuck.
  useEffect(() => {
    if (!booking?.id) return;
    const bid = Number(booking.id);
    if (!Number.isFinite(bid)) return;
    const cvRaw = (booking as { contract_version?: unknown }).contract_version;
    const contractVersion =
      cvRaw != null &&
      String(cvRaw).trim() !== "" &&
      Number.isFinite(Number(cvRaw))
        ? Number(cvRaw)
        : null;
    const env =
      booking.docusign_envelope_id != null &&
      String(booking.docusign_envelope_id).trim() !== ""
        ? String(booking.docusign_envelope_id)
        : null;
    const prev = docuSignBookingMetaRef.current;
    if (prev.bookingId === bid) {
      if (prev.contractVersion !== contractVersion || prev.envelopeId !== env) {
        setDocuSignSessionExpiredNotice(false);
      }
    }
    docuSignBookingMetaRef.current = {
      bookingId: bid,
      contractVersion,
      envelopeId: env,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sync DocuSign session meta from booking identity fields only
  }, [booking?.id, booking?.contract_version, booking?.docusign_envelope_id]);

  // All approved (confirmed) bookings for Home tab – room details + approved rooms list
  const { data: approvedBookings = [] } = useQuery({
    queryKey: ["tenant-approved-bookings", user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      const { data, error } = await supabase
        .from("bookings")
        .select(
          `
          id,
          start_date,
          end_date,
          move_out_date,
          monthly_rent,
          deposit_amount,
          rent_discount_mode,
          rent_discount_value,
          rent_discount_months,
          rent_discount_start_month,
          deposit_discount_mode,
          deposit_discount_value,
          move_in_date,
          first_month_rent_override,
          accumulated_fees_eur,
          rent_due_date,
          room_number,
          status,
          contract_status,
          contract_signed_at,
          rooms (
            id,
            title,
            price,
            deposit_amount,
            property_id,
            properties (
              id,
              title,
              address,
              zip_code,
              city
            )
          )
        `
        )
        .eq("user_id", user.id)
        .is("admin_archived_at", null)
        .eq("status", "confirmed")
        .order("start_date", { ascending: false });
      if (error) throw error;
      return data || [];
    },
    enabled: !!user?.id,
  });

  // All user bookings (all stages) for Home tab – applied, waiting, pending, approved
  const { data: allBookings = [] } = useQuery({
    queryKey: ["tenant-all-bookings", user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      const { data, error } = await supabase
        .from("bookings")
        .select(
          `
          id,
          start_date,
          end_date,
          move_out_date,
          monthly_rent,
          deposit_amount,
          rent_discount_mode,
          rent_discount_value,
          rent_discount_months,
          rent_discount_start_month,
          deposit_discount_mode,
          deposit_discount_value,
          move_in_date,
          first_month_rent_override,
          accumulated_fees_eur,
          rent_due_date,
          room_number,
          status,
          contract_status,
          contract_signed_at,
          phone,
          created_at,
          rooms (
            id,
            title,
            price,
            deposit_amount,
            property_id,
            properties (
              id,
              title,
              address,
              zip_code,
              city
            )
          )
        `
        )
        .eq("user_id", user.id)
        .is("admin_archived_at", null)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data || [];
    },
    enabled: !!user?.id,
  });

  // Derived lists for dashboard stages
  const appliedBookings = useMemo(() => {
    return allBookings.filter(
      (b: any) =>
        b.status === "pending" &&
        isContractNotReleasedToTenant(b.contract_status)
    );
  }, [allBookings]);

  const waitingForApprovalBookings = useMemo(() => {
    return allBookings.filter(
      (b: any) => b.contract_status === "awaiting_approval"
    );
  }, [allBookings]);

  const pendingBookings = useMemo(() => {
    return allBookings.filter(
      (b: any) =>
        b.status === "pending" &&
        !isContractNotReleasedToTenant(b.contract_status) &&
        b.contract_status !== "awaiting_approval"
    );
  }, [allBookings]);

  const HOME_PAGE_SIZE = 10;
  const homeTotalPages = Math.max(
    1,
    Math.ceil(allBookings.length / HOME_PAGE_SIZE)
  );
  useEffect(() => {
    if (homeRoomsPage > homeTotalPages) setHomeRoomsPage(1);
  }, [homeTotalPages, homeRoomsPage]);

  useEffect(() => {
    if (!user?.id) return undefined;

    const bookingsChannel = supabase
      .channel(`dashboard-bookings-${user.id}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "bookings",
          filter: `user_id=eq.${user.id}`,
        },
        () => {
          queryClient.invalidateQueries({
            queryKey: ["active-booking", user?.id],
          });
          queryClient.invalidateQueries({
            queryKey: ["tenant-approved-bookings", user?.id],
          });
          queryClient.invalidateQueries({
            queryKey: ["tenant-all-bookings", user?.id],
          });
        }
      )
      .subscribe();

    // Whether a payment reads as PAID depends on two tables, so both have to
    // wake this screen up: the payments row (status / the admin attestation)
    // and its payment_proofs row (an approved proof). Invalidating only
    // `payments` left the proof map up to 30s stale, so an admin "Mark paid"
    // could land here and still render Pending until the next poll — the very
    // "it doesn't update on the tenant side" report. Both handlers therefore
    // refresh both queries.
    const refreshPaymentViews = () => {
      queryClient.invalidateQueries({ queryKey: ["payments", user?.id] });
      queryClient.invalidateQueries({
        queryKey: ["payments-upcoming", user?.id],
      });
      // Prefix match — the proofs query key carries the payment-id list as a
      // third element, so it must be invalidated by prefix, not by exact key.
      queryClient.invalidateQueries({
        queryKey: ["payment-proofs", user?.id],
      });
    };

    const paymentsChannel = supabase
      .channel(`dashboard-payments-${user.id}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "payments",
          filter: `user_id=eq.${user.id}`,
        },
        refreshPaymentViews
      )
      .subscribe();

    // payment_proofs has no user_id to filter on; RLS scopes the stream to this
    // tenant's own proofs. Approving a proof is how PaymentsHub marks a manual
    // payment paid, and on an already-`paid` row that UPDATE may be the only
    // change there is — without this channel that path has nothing to notify on.
    const paymentProofsChannel = supabase
      .channel(`dashboard-payment-proofs-${user.id}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "payment_proofs" },
        refreshPaymentViews
      )
      .subscribe();

    return () => {
      supabase.removeChannel(bookingsChannel);
      supabase.removeChannel(paymentsChannel);
      supabase.removeChannel(paymentProofsChannel);
    };
  }, [user?.id, queryClient]);

  // Refetch payments and bookings when switching to Home tab so "Your rooms" status is up to date
  useEffect(() => {
    if (activeTab === "home" && user?.id) {
      queryClient.invalidateQueries({ queryKey: ["payments", user.id] });
      queryClient.invalidateQueries({
        queryKey: ["tenant-all-bookings", user.id],
      });
    }
  }, [activeTab, user?.id, queryClient]);

  // Call docusign-sync-envelope to mark booking as signed when DocuSign redirects with event=signing_complete.
  const invokeSyncDocusignEnvelope = async (bookingId: number) => {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    const base = (supabaseUrl || "").replace(/\/+$/, "");
    const res = await fetch(`${base}/functions/v1/docusign-sync-envelope`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${session?.access_token ?? ""}`,
      },
      body: JSON.stringify({ bookingId }),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) {
      const err = new Error(
        body?.error || res.statusText || "Sync failed"
      ) as Error & { details?: string };
      err.details = body?.details;
      throw err;
    }
    return body;
  };

  // Call get-docusign-signing-url via fetch so we can read 500 response body.
  const invokeGetDocusignSigningUrl = async (bookingId: number) => {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    const base = (supabaseUrl || "").replace(/\/+$/, "");
    const res = await fetch(`${base}/functions/v1/get-docusign-signing-url`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${session?.access_token ?? ""}`,
      },
      body: JSON.stringify({ bookingId }),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) {
      const err = new Error(
        body?.error || res.statusText || "Request failed"
      ) as Error & { details?: string };
      err.details = body?.details;
      throw err;
    }
    if (body?.error && !body?.url) {
      const err = new Error(body.error) as Error & { details?: string };
      err.details = body.details;
      throw err;
    }
    return body;
  };

  // Call create-contract via fetch so we can read 500 response body (error details/hint).
  const invokeCreateContract = async (bookingId: number) => {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    const base = (supabaseUrl || "").replace(/\/+$/, "");
    const url = `${base}/functions/v1/create-contract`;
    const res = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${session?.access_token ?? ""}`,
        apikey: supabaseAnonKey,
      },
      body: JSON.stringify({ bookingId }),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) {
      const err = new Error(
        body?.error || res.statusText || "Request failed"
      ) as Error & { details?: string; hint?: string; code?: string };
      err.details = body?.details;
      err.hint = body?.hint;
      err.code = body?.code;
      throw err;
    }
    if (body?.error) {
      const err = new Error(body.error) as Error & {
        details?: string;
        hint?: string;
      };
      err.details = body.details;
      err.hint = body.hint;
      throw err;
    }
    return body;
  };

  // Call send-contract-to-docusign via fetch so we can open DocuSign right away.
  const invokeSendContractToDocusign = async (bookingId: number) => {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    const base = (supabaseUrl || "").replace(/\/+$/, "");
    const url = `${base}/functions/v1/send-contract-to-docusign`;
    const res = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${session?.access_token ?? ""}`,
      },
      body: JSON.stringify({ bookingId }),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) {
      const err = new Error(
        body?.error || res.statusText || "Request failed"
      ) as Error & { details?: string; code?: string };
      err.details = body?.details;
      err.code = body?.code;
      throw err;
    }
    if (body?.error && !body?.url) {
      const err = new Error(body.error) as Error & {
        details?: string;
        code?: string;
      };
      err.details = body.details;
      err.code = body.code;
      throw err;
    }
    return body;
  };

  // Contract signing mutation
  const createContractMutation = useMutation({
    mutationFn: invokeCreateContract,
    onSuccess: (data: Record<string, unknown>) => {
      queryClient.invalidateQueries({ queryKey: ["active-booking", user?.id] });
      const sent = data?.contractReadyEmailSent === true;
      if (sent) {
        toast.success(
          t(
            "dashboard.contractGeneratedEmailSent",
            "Contract generated successfully! Check your email or open My Contract below to sign via DocuSign."
          )
        );
      } else {
        toast.success(
          t(
            "dashboard.contractGeneratedNoEmail",
            "Contract generated successfully! Open My Contract in your dashboard to sign via DocuSign."
          )
        );
        const skipped = data?.contractReadyEmailSkipped;
        const err = data?.contractReadyEmailError;
        if (skipped != null || err != null) {
          toast.warning(
            t(
              "dashboard.contractNotificationEmailFailed",
              "We could not send the contract notification email."
            ),
            {
              description: [
                skipped != null && skipped !== ""
                  ? `${t("dashboard.reasonPrefix", "Reason:")} ${String(skipped)}`
                  : null,
                err != null && err !== "" ? String(err) : null,
              ]
                .filter(Boolean)
                .join(" "),
              duration: 12000,
            }
          );
        }
      }
    },
    onError: (error: any) => {
      const msg =
        contractRefusalMessage(error, t) ||
        [error?.message, error?.details, error?.hint]
          .filter(Boolean)
          .join(" — ");
      toast.error(
        msg ||
          t("dashboard.failedToCreateContract", "Failed to create contract")
      );
    },
  });

  // Upload signed contract mutation with progress
  const uploadSignedContractMutation = useMutation({
    mutationFn: async ({
      bookingId,
      file,
    }: {
      bookingId: number;
      file: File;
    }) => {
      setSignedContractUploadBookingId(bookingId);
      setSignedContractUploadProgress(0);
      const progressInterval = setInterval(() => {
        setSignedContractUploadProgress((p) => (p >= 90 ? 90 : p + 8));
      }, 200);

      try {
        const fileName = `signed-contracts/booking-${bookingId}-${Date.now()}.pdf`;
        const { error: uploadError } = await supabase.storage
          .from("contracts")
          .upload(fileName, file, {
            contentType: "application/pdf",
            upsert: false,
          });

        if (uploadError) throw uploadError;

        const { data: urlData } = supabase.storage
          .from("contracts")
          .getPublicUrl(fileName);

        const { error: updateError } = await supabase
          .from("bookings")
          .update({
            signed_contract_url: urlData.publicUrl,
            contract_status: "awaiting_approval",
          } as any)
          .eq("id", bookingId);

        if (updateError) throw updateError;

        setSignedContractUploadProgress(100);
        return urlData.publicUrl;
      } finally {
        clearInterval(progressInterval);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["active-booking", user?.id] });
      toast.success(
        t(
          "dashboard.signedContractUploadedSuccessfully",
          "Signed contract uploaded successfully! Awaiting admin approval."
        )
      );
      setTimeout(() => {
        setSignedContractUploadBookingId(null);
        setSignedContractUploadProgress(0);
      }, 400);
    },
    onError: (error: any) => {
      toast.error(
        error.message ||
          t(
            "dashboard.failedToUploadSignedContract",
            "Failed to upload signed contract"
          )
      );
      setSignedContractUploadBookingId(null);
      setSignedContractUploadProgress(0);
    },
  });

  // Upload payment proofs for direct bank transfer (multiple files, progress bar)
  const uploadPaymentProofMutation = useMutation({
    mutationFn: async ({
      paymentId,
      files,
    }: {
      paymentId: string;
      files: File[];
    }) => {
      if (!user?.id) throw new Error("Not authenticated");
      const maxSize = 10 * 1024 * 1024; // 10MB per file
      const validTypes = [
        "application/pdf",
        "image/jpeg",
        "image/jpg",
        "image/png",
        "image/webp",
      ];
      for (const file of files) {
        if (file.size > maxSize)
          throw new Error(
            language === "de"
              ? "Datei maximal 10 MB."
              : "Each file must be 10 MB or less."
          );
        if (!validTypes.includes(file.type))
          throw new Error(
            language === "de"
              ? "Nur PDF oder Bild (JPEG, PNG, WebP)."
              : "Files must be PDF or image (JPEG, PNG, WebP)."
          );
      }
      const total = files.length;
      const uploaded: { proof: any }[] = [];
      setPaymentProofUploadProgress(0);
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        setPaymentProofUploadProgress(((i + 0.3) / total) * 100);
        const ext = file.name.split(".").pop() || "pdf";
        const storagePath = `payment-proofs/${paymentId}/${Date.now()}-${i}-${file.name.replace(/\s+/g, "-")}.${ext}`;
        const { error: uploadError } = await supabase.storage
          .from("contracts")
          .upload(storagePath, file, {
            contentType: file.type || "application/octet-stream",
            upsert: false,
          });
        setPaymentProofUploadProgress(((i + 1) / total) * 100);
        if (uploadError) throw uploadError;
        // No public URL is built for the proof: the row stores the bare path
        // and every viewer signs it on click (COL-436).
        const { data: proof, error: insertError } = await supabase
          .from("payment_proofs")
          .insert({
            payment_id: paymentId,
            file_path: storagePath,
            file_name: file.name,
            file_size: file.size,
            mime_type: file.type,
            status: "pending_review",
          })
          .select()
          .single();
        if (insertError) {
          await supabase.storage.from("contracts").remove([storagePath]);
          throw insertError;
        }
        uploaded.push({ proof });
      }
      setPaymentProofUploadProgress(100);
      return { uploaded };
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["payments", user?.id] });
      queryClient.invalidateQueries({ queryKey: ["payment-proofs", user?.id] });
      setPaymentProofUploadProgress(0);
      setPaymentProofFiles([]);
      setBankTransferPayment(null);
      setPaymentStep("overview");
      const count = variables.files.length;
      toast.success(
        language === "de"
          ? count === 1
            ? "Zahlungsnachweis hochgeladen."
            : `${count} Zahlungsnachweise hochgeladen.`
          : count === 1
            ? "Payment proof uploaded."
            : `${count} payment proofs uploaded.`
      );
    },
    onError: (error: any) => {
      setPaymentProofUploadProgress(0);
      toast.error(
        error.message ||
          (language === "de" ? "Hochladen fehlgeschlagen." : "Upload failed.")
      );
    },
  });

  // Fetch last payments for history and status cards (auto-refresh so amounts update after payment, like admin)
  //
  // Two queries, merged: the newest 24 rows drive history, and EVERY deposit row
  // is fetched separately on top. The deposit is billed once, at the start of a
  // tenancy, so it carries the oldest due_date on the booking — on a tenancy
  // longer than ~24 rent rows the newest-24 window scrolls straight past it and
  // the tenant's deposit card reads "No deposit payment recorded yet" no matter
  // what admin recorded. There are at most a handful of deposit rows per tenant,
  // so fetching them unbounded is cheap.
  const { data: payments } = useQuery<any[]>({
    queryKey: ["payments", user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      const [recentRes, depositRes] = await Promise.all([
        supabase
          .from("payments")
          .select("*")
          .eq("user_id", user.id)
          .order("due_date", { ascending: false })
          .limit(24),
        supabase
          .from("payments")
          .select("*")
          .eq("user_id", user.id)
          .eq("type", "deposit")
          .order("due_date", { ascending: false }),
      ]);
      if (recentRes.error) throw recentRes.error;
      if (depositRes.error) throw depositRes.error;
      // Same ordering the single query produced — due_date DESC, and Postgres
      // sorts DESC NULLS FIRST — so nothing downstream that reads position
      // ("first paid deposit row", the history list) changes shape.
      return mergeRecentAndDepositPayments(
        recentRes.data || [],
        depositRes.data || []
      );
    },
    enabled: !!user?.id,
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  });

  // Unpaid rent payments for "next payment" purposes. Intentionally not
  // date-bounded (no due_date >= today filter): an overdue-but-unpaid
  // prorated row (e.g. the first, partial month materialized on deposit
  // paid) must still surface as "next payment" instead of being skipped in
  // favor of a later, non-prorated full-month row. Excludes only `canceled`
  // (not an include-list of "pending"/"scheduled"/"overdue") so that
  // `failed` rows (a bounced collection — still owed, per rent.ts) and raw
  // `status: "paid"` rows that aren't yet effectively paid (manual/bank
  // transfer without an approved proof) both stay in the candidate set for
  // `isPaymentEffectivelyPaid` downstream to evaluate — matching the
  // admin side's exclude-list semantics in tenant-profile-tabs.tsx.
  //
  // No `.limit(...)`: this is ordered ascending by due_date with paid history
  // still included (only `canceled` is excluded), so a hard limit on a
  // long-running tenancy could cut off before reaching the true earliest
  // UNPAID row — e.g. 10+ already-paid months ordering ahead of the row that
  // actually needs to surface as "next payment". `selectEarliestUnpaidRentPayment`
  // needs to see every candidate row to pick correctly; scoped to a single
  // user's rent rows, this stays small (tenancy months), so fetching all of
  // them is cheap and correct beats an arbitrary cap.
  const {
    data: upcomingPayments = [],
    // COL-103 needs to tell "no rows" apart from "not loaded yet"; the `= []`
    // default above hides that, and a partial ledger would make the rows that
    // arrive late look like fresh payments.
    isPending: upcomingPaymentsPending,
  } = useQuery<any[]>({
    queryKey: ["payments-upcoming", user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      const { data, error } = await supabase
        .from("payments")
        .select("*")
        .eq("user_id", user.id)
        .eq("type", "rent")
        .neq("status", "canceled")
        .order("due_date", { ascending: true });
      if (error) throw error;
      return data || [];
    },
    enabled: !!user?.id,
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  });

  // Payment proofs for direct bank transfer (status, rejection reason)
  // Rejection reason is shown until the payment is approved (so it persists after re-upload and refresh)
  // Sourced from both `payments` (recent history) AND `upcomingPayments` (oldest
  // unpaid-candidate rows), not just `payments`: `upcomingPayments` is ordered
  // due_date ascending with no recency bound, so on a tenancy with more than 24
  // rent rows its oldest candidates can fall outside the `payments` (newest-24)
  // window. Without their proofs loaded, a legitimately-paid old manual/bank-
  // transfer row reads as "no approved proof" and gets misclassified as unpaid,
  // surfacing a years-old date as the next payment.
  // `payments` and `upcomingPayments` load independently, so the id set below
  // can change shape across renders (e.g. `payments` resolves first, then
  // `upcomingPayments` lands moments later). Both facts must be reflected in
  // the query key itself — not just recomputed inside queryFn — otherwise
  // React Query treats this as the same query as before either source
  // finished loading and never refetches for the newly-added ids: an
  // approved older row would read as "no proof" (unpaid) for up to the 30s
  // refetchInterval and could be selected as "next payment"/exposed to Pay
  // Now on a month that's already settled.
  const paymentProofIdsKey = useMemo(() => {
    const idSet = new Set<string>();
    (payments || []).forEach((pay: any) => {
      if (pay?.id != null) idSet.add(String(pay.id));
    });
    (upcomingPayments || []).forEach((pay: any) => {
      if (pay?.id != null) idSet.add(String(pay.id));
    });
    return Array.from(idSet).sort();
  }, [payments, upcomingPayments]);
  const { data: paymentProofsByPaymentId } = useQuery<Record<string, any>>({
    queryKey: ["payment-proofs", user?.id, paymentProofIdsKey],
    queryFn: async () => {
      if (!user?.id) return {};
      const paymentIds = paymentProofIdsKey;
      if (paymentIds.length === 0) return {};
      const { data, error } = await supabase
        .from("payment_proofs")
        .select("*")
        .in("payment_id", paymentIds)
        .order("created_at", { ascending: false });
      // Throw rather than swallow: react-query then keeps the LAST successful
      // map on a transient failure instead of resetting it to `{}`, which
      // used to make every proof-backed payment look unpaid for one pass and
      // could replay the COL-103 celebration once the next fetch recovered.
      if (error) throw error;
      const byPayment = (data || []).reduce(
        (acc: Record<string, any[]>, proof: any) => {
          const id = proof.payment_id;
          if (!acc[id]) acc[id] = [];
          acc[id].push(proof);
          return acc;
        },
        {}
      );
      const map: Record<string, any> = {};
      Object.entries(byPayment).forEach(([paymentId, proofs]) => {
        const sorted = (proofs as any[])
          .slice()
          .sort(
            (a: any, b: any) =>
              new Date(b.created_at).getTime() -
              new Date(a.created_at).getTime()
          );
        const latestProof = sorted[0];
        const hasApproved = sorted.some((pr: any) => pr.status === "approved");
        const lastRejected = sorted.find(
          (pr: any) => pr.status === "rejected" && pr.rejection_reason
        );
        const rejectionReasonToShow = hasApproved
          ? null
          : (lastRejected?.rejection_reason ??
            (latestProof?.admin_comment?.trim() || null));
        map[paymentId] = { ...latestProof, rejectionReasonToShow };
      });
      return map;
    },
    enabled: !!user?.id && (!!payments?.length || !!upcomingPayments?.length),
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  });

  // Stripe: trust DB. Manual/bank transfer: only count as paid when a proof exists (deleting proof must drop "paid" counts).
  const proofForPaymentRow = (p: any) => {
    if (!p?.id || !paymentProofsByPaymentId) return undefined;
    return (
      paymentProofsByPaymentId[p.id] ?? paymentProofsByPaymentId[String(p.id)]
    );
  };
  // Both delegate to the canonical rule in @/lib/paymentStatus (COL-227) so
  // the tenant Dashboard can never disagree with PaymentsHub / AdminTenants.
  //
  // The input is built by `paymentStatusInput`, which carries the ADMIN
  // ATTESTATION (`marked_paid_at` / `marked_paid_by`) and whether a proof
  // exists at all — not just whether the latest proof is approved. Passing only
  // `proofStatus` here is what split this screen from the admin one (#1326):
  // "Mark paid" / "Mark deposit paid" attest the row without approving the
  // proof, so admin read Paid while the tenant kept seeing Pending — for rent
  // rows exactly as much as for the deposit. `payments` is selected with `*`,
  // so both attestation columns are present on every row below.
  const isPaymentEffectivelyPaid = (p: any) => {
    if (!p) return false;
    return isEffectivelyPaid(paymentStatusInput(p, proofForPaymentRow(p)));
  };
  const getEffectiveRentPaymentStatus = (p: any) => {
    if (!p) return "pending";
    return effectivePaymentStatus(paymentStatusInput(p, proofForPaymentRow(p)));
  };

  const paymentPendingList = useMemo(() => {
    if (!payments) return [];
    return (payments as any[])
      .filter(
        (p: any) =>
          p.type === "rent" &&
          !p.paid_at &&
          // COL-417: a part-paid month is still outstanding for the tenant.
          ["pending", "scheduled", "partially_paid", "overdue"].includes(
            p.status
          ) &&
          p.due_date
      )
      .sort(
        (a: any, b: any) =>
          new Date(a.due_date).getTime() - new Date(b.due_date).getTime()
      );
  }, [payments]);

  const paidList = useMemo(() => {
    if (!payments) return [];
    return (payments as any[])
      .filter((p: any) => isPaymentEffectivelyPaid(p))
      .sort(
        (a: any, b: any) =>
          new Date((b.paid_at || b.due_date) as string).getTime() -
          new Date((a.paid_at || a.due_date) as string).getTime()
      )
      .slice(0, 12);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- isPaymentEffectivelyPaid closes over paymentProofsByPaymentId; both are stable for this memo intent
  }, [payments, paymentProofsByPaymentId]);

  // COL-103: the celebration keys off the SAME resolved paid flag the rest of
  // this screen renders, so the pop-up and the badge can never disagree.
  // `payments` is capped at the newest 24 rent rows (plus every deposit), so a
  // long-overdue older month settled today would never be seen there —
  // `upcomingPayments` carries the unbounded ledger, so both are unioned, the
  // richer `payments` row winning on id.
  const celebratablePayments = useMemo(() => {
    // Null means "not loaded yet", which the hook reads as "do not evaluate".
    // Both sources must have settled: seeding from half the ledger would make
    // the other half look like it flipped the moment it arrives.
    if (payments === undefined || upcomingPaymentsPending) return null;
    const byId = new Map<string, any>();
    [
      ...((payments as any[]) || []),
      ...((upcomingPayments as any[]) || []),
    ].forEach((row: any) => {
      if (row?.id != null && !byId.has(String(row.id)))
        byId.set(String(row.id), row);
    });
    return Array.from(byId.values()).map((p: any) => ({
      id: p.id,
      type: p.type,
      amountCents: p.amount_cents ?? null,
      dueDate: p.due_date ?? null,
      // The LATEST of the three stamps a paid transition can leave, so an old
      // `paid_at` cannot mask an attestation or proof approved just now.
      paidAt: latestPaidTimestamp(
        p.paid_at,
        p.marked_paid_at,
        proofForPaymentRow(p)?.reviewed_at
      ),
      paid: isPaymentEffectivelyPaid(p),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mirrors paidList above: isPaymentEffectivelyPaid closes over paymentProofsByPaymentId
  }, [
    payments,
    upcomingPayments,
    upcomingPaymentsPending,
    paymentProofsByPaymentId,
  ]);

  // Proofs settle AFTER payments — their query key is derived from the payment
  // ids — and `isPaymentEffectivelyPaid` needs them: until the map exists a
  // manual/bank row backed by an approved proof reads as UNPAID. Evaluating in
  // that window would record the wrong paid set and then fire a false
  // celebration for every proof-backed payment the moment the proofs land. No
  // rows at all is the one case where no proof query ever runs.
  const paidCelebrationReady =
    celebratablePayments !== null &&
    (paymentProofsByPaymentId !== undefined ||
      celebratablePayments.length === 0);

  const { celebration, dismissCelebration } = usePaidCelebration({
    payments: paidCelebrationReady ? celebratablePayments : null,
    userId: user?.id ?? null,
  });

  // Use active booking when present; else first approved booking that has financials; else any booking with financials (e.g. pending with contract set)
  const firstApprovedWithFinancials = approvedBookings?.find(
    (b: any) =>
      b.monthly_rent != null ||
      b.deposit_amount != null ||
      b.rooms?.price != null
  );
  const firstAnyWithFinancials = allBookings?.find(
    (b: any) =>
      b.monthly_rent != null ||
      b.deposit_amount != null ||
      b.rooms?.price != null
  );
  const financialsBooking =
    booking ??
    firstApprovedWithFinancials ??
    approvedBookings?.[0] ??
    firstAnyWithFinancials ??
    null;

  /** Room id for showing default bank details when the active booking is resolved via financials aggregate. */
  const roomIdForBankDetails =
    booking?.rooms?.id ??
    (financialsBooking as { rooms?: { id?: string | number } } | null)?.rooms
      ?.id ??
    null;

  /** COLIVIQ / room-config bank details whenever we can tie the tenant to a room. */
  const bankTransferInfoForDisplay = useMemo(() => {
    if (!roomIdForBankDetails) return null;
    return bankTransferDetails ?? { ...COLIVIQ_DEFAULT_BANK_TRANSFER };
  }, [roomIdForBankDetails, bankTransferDetails]);

  /**
   * The tenant's chosen payment method (feedback 1) — a UX preference stored
   * on the booking. It drives which method-picker card renders selected and
   * what the overview summarises; it never gates what the backend accepts.
   * Default: an active mandate wins (direct debit IS how rent is collected),
   * else the first offered method.
   */
  const payMethodOptions = useMemo(
    () =>
      [
        ...(sepaDebitEnabled ? (["sepa_debit"] as const) : []),
        ...(stripeCardEnabled ? (["card"] as const) : []),
        ...(bankTransferInfoForDisplay ? (["bank_transfer"] as const) : []),
      ] as Array<"sepa_debit" | "card" | "bank_transfer">,
    [sepaDebitEnabled, stripeCardEnabled, bankTransferInfoForDisplay]
  );
  const storedPayMethod = (financialsBooking as any)
    ?.preferred_payment_method as
    | "sepa_debit"
    | "card"
    | "bank_transfer"
    | null;
  const selectedPayMethod =
    // An ACTIVE mandate pins the selection (feedback 6): direct debit IS how
    // rent is collected until an admin approves ending it, so no stored or
    // clicked preference may display another method as chosen.
    (sepaDebitEnabled && hasActiveMandate ? ("sepa_debit" as const) : null) ??
    payMethodChoice ??
    (storedPayMethod && payMethodOptions.includes(storedPayMethod)
      ? storedPayMethod
      : null) ??
    payMethodOptions[0] ??
    null;

  /** Pending mandate-cancellation request (feedback 6) — filed below, decided
   *  by an admin in the tenant profile. Shown as a badge on the picker. */
  const { data: pendingDdCancelRequest, refetch: refetchDdCancelRequest } =
    useQuery<any | null>({
      queryKey: ["dd-cancel-request", user?.id],
      enabled: !!user?.id && hasActiveMandate,
      queryFn: async () => {
        const { data, error } = await supabase
          .from("sepa_mandate_cancellation_requests")
          .select("id, requested_method, status, created_at")
          .eq("user_id", user!.id)
          .eq("status", "pending")
          .maybeSingle();
        if (error) throw error;
        return data;
      },
    });

  /** Method the tenant wants to switch TO — non-null opens the confirm dialog. */
  const [endDdMethod, setEndDdMethod] = useState<
    "card" | "bank_transfer" | null
  >(null);
  const requestDdCancellation = async () => {
    if (!user?.id || !endDdMethod) return;
    const method = endDdMethod;
    setEndDdMethod(null);
    const { data: mandate, error: mandateError } = await supabase
      .from("sepa_mandates")
      .select("id, booking_id")
      .eq("user_id", user.id)
      .eq("status", "active")
      .order("signed_at", { ascending: false })
      .limit(1)
      .maybeSingle();
    if (mandateError || !mandate) {
      toast.error(
        language === "de"
          ? "Kein aktives Mandat gefunden."
          : "No active mandate found."
      );
      return;
    }
    const { error } = await supabase
      .from("sepa_mandate_cancellation_requests")
      .insert({
        mandate_id: mandate.id,
        user_id: user.id,
        booking_id: financialsBooking?.id ?? mandate.booking_id ?? null,
        requested_method: method,
      });
    if (error) {
      // The partial unique index turns a double-file into a conflict.
      const already = String((error as any)?.code) === "23505";
      toast[already ? "info" : "error"](
        already
          ? language === "de"
            ? "Ihre Anfrage liegt bereits vor und wird geprüft."
            : "Your request is already pending review."
          : language === "de"
            ? "Anfrage konnte nicht gesendet werden."
            : "Could not send the request."
      );
    } else {
      toast.success(
        language === "de"
          ? "Anfrage gesendet — ein Admin prüft die Beendigung des Lastschriftmandats."
          : "Request sent — an admin will review ending your direct debit mandate."
      );
    }
    void refetchDdCancelRequest();
  };

  /** Feedback 8: with an ACTIVE mandate, Pay Now debits via SEPA immediately
   *  instead of opening the card checkout. Reuses create-rent-payment
   *  (COL-294) — it upserts the month's row and confirms an idempotent
   *  off-session SEPA PaymentIntent, so double-clicks can't double-charge. */
  const [sepaPayNowBusy, setSepaPayNowBusy] = useState(false);
  const payNowViaSepa = async () => {
    if (sepaPayNowBusy) return;
    const unpaidUpcoming =
      upcomingPayment && !isPaymentEffectivelyPaid(upcomingPayment)
        ? upcomingPayment
        : null;
    const row = unpaidUpcoming ?? nextRentPayment ?? null;
    const amountCents =
      row?.amount_cents != null
        ? Number(row.amount_cents)
        : Math.round(Number(upcomingRemainingChargeEUR || 0) * 100);
    if (!financialsBooking?.id || !amountCents || amountCents <= 0) {
      toast.error(
        language === "de"
          ? "Keine offene Zahlung gefunden."
          : "No open payment found."
      );
      return;
    }
    setSepaPayNowBusy(true);
    try {
      const { data, error } = await supabase.functions.invoke(
        "create-rent-payment",
        {
          body: {
            bookingId: Number(financialsBooking.id),
            amountCents,
            currency: "eur",
            type: "rent",
            dueDate: (row?.due_date
              ? String(row.due_date)
              : getNextPaymentDate().toISOString()
            ).slice(0, 10),
            description:
              language === "de" ? "Miete (Lastschrift)" : "Rent (direct debit)",
          },
        }
      );
      if (error) throw error;
      if ((data as any)?.alreadyPaid) {
        toast.info(
          language === "de"
            ? "Diese Miete ist bereits bezahlt."
            : "This rent is already paid."
        );
      } else if ((data as any)?.alreadyInFlight) {
        // Feedback 9: a debit is already at the bank for this month — say so
        // instead of reporting a second collection that never happened.
        toast.info(
          language === "de"
            ? "Die Lastschrift läuft bereits — Abbuchung in 2–5 Bankarbeitstagen."
            : "The direct debit is already in progress — collected within 2–5 business days."
        );
      } else {
        toast.success(
          language === "de"
            ? "Lastschrift veranlasst — Abbuchung in 2–5 Bankarbeitstagen."
            : "Direct debit initiated — collected within 2–5 business days."
        );
      }
      void queryClient.invalidateQueries({ queryKey: ["payments", user?.id] });
      void queryClient.invalidateQueries({
        queryKey: ["payments-upcoming", user?.id],
      });
    } catch (e: any) {
      toast.error(
        await edgeFunctionErrorMessage(
          e,
          language === "de"
            ? "Lastschrift fehlgeschlagen."
            : "Direct debit failed."
        )
      );
    } finally {
      setSepaPayNowBusy(false);
    }
  };

  const choosePayMethod = (m: "sepa_debit" | "card" | "bank_transfer") => {
    // Switching away from an active mandate needs admin approval (feedback 6):
    // don't change anything — file (or point at) a cancellation request.
    if (m !== "sepa_debit" && hasActiveMandate) {
      if (pendingDdCancelRequest) {
        toast.info(
          language === "de"
            ? "Ihre Anfrage zur Beendigung des Lastschriftmandats wird bereits geprüft."
            : "Your request to end the direct debit mandate is already under review."
        );
      } else {
        setEndDdMethod(m);
      }
      return;
    }
    setPayMethodChoice(m);
    const bid = financialsBooking?.id;
    if (bid == null) return;
    void supabase
      .from("bookings")
      .update({ preferred_payment_method: m })
      .eq("id", bid)
      .then(({ error }: { error: unknown }) => {
        // Selection still works for the session via local state; only the
        // persistence failed, so stay quiet beyond the console.
        if (error)
          console.error("preferred_payment_method update failed", error);
      });
  };

  const fullMonthlyRent = getFullMonthlyRentFromBooking(financialsBooking);
  const todayIsoForRent = new Date().toISOString().slice(0, 10);
  const moveInIsoForRent = financialsBooking
    ? financialsBooking.move_in_date || financialsBooking.start_date
    : null;
  const rentDisplay = getDisplayedRentEUR({
    fullMonthlyRent,
    moveInIso: moveInIsoForRent,
    firstMonthOverride: financialsBooking?.first_month_rent_override ?? null,
    asOfIsoDate: todayIsoForRent,
  });
  const expectedDepositEUR = financialsBooking
    ? getExpectedDepositEUR(financialsBooking)
    : 0;
  const contractRoom =
    booking?.rooms && Array.isArray((booking as any).rooms)
      ? (booking as any).rooms[0]
      : (booking as any)?.rooms;
  const contractProperty =
    contractRoom?.properties && Array.isArray((contractRoom as any).properties)
      ? (contractRoom as any).properties[0]
      : (contractRoom as any)?.properties;
  // Deposit source of truth: the booking's deposit payment rows (billed + paid),
  // resolved by the shared helper so admin and tenant views always agree.
  const bookingIdNum =
    financialsBooking?.id != null ? Number(financialsBooking.id) : null;
  const depositMatchesBooking = (p: any) =>
    depositRowMatchesBooking(p, bookingIdNum, true);
  const depositRowsForBooking = (payments || []).filter(
    (p: any) => p.type === "deposit" && depositMatchesBooking(p)
  );
  const depositState = resolveDepositState({
    depositPayments: depositRowsForBooking,
    isEffectivelyPaid: isPaymentEffectivelyPaid,
    expectedDepositEUR,
  });
  const depositAmount = depositState.expectedCents / 100;
  const expectedDepositCents = depositState.expectedCents;
  const totalDepositPaidCents = depositState.paidCents;
  const hasPaidDeposit = depositState.status === "paid";
  const depositPaymentsForBooking = depositRowsForBooking.filter((p: any) =>
    isPaymentEffectivelyPaid(p)
  );
  const depositPayment = depositPaymentsForBooking[0] ?? null;

  // Payments for the current booking only (so we don't show another booking's paid rent on a new application)
  const paymentsForCurrentBooking = useMemo(() => {
    if (!payments) return [];
    if (bookingIdNum == null) return payments as any[];
    return (payments as any[]).filter(
      (p: any) => Number(p.booking_id) === bookingIdNum
    );
  }, [payments, bookingIdNum]);

  // Current calendar month: prefer payments row amount (paid or pending) so admin-edited rent matches tenant UI.
  const currentYm = todayIsoForRent.slice(0, 7);
  const currentMonthRentPayment = useMemo(() => {
    const list = (paymentsForCurrentBooking || []).filter(
      (p: any) => p?.type === "rent" && p?.due_date
    );
    const match = list.find(
      (p: any) => String(p.due_date).slice(0, 7) === currentYm
    );
    return match || null;
  }, [paymentsForCurrentBooking, currentYm]);
  /** Billed amount for the current period (pro-rata first month when applicable) — splits & creating payment rows. */
  const billedAmountForCurrentMonth =
    currentMonthRentPayment?.amount_cents != null
      ? Number(currentMonthRentPayment.amount_cents) / 100
      : rentDisplay.amount;
  /** Contract headline: always full monthly rent from booking, never pro-rata. */
  const displayContractMonthlyRentEUR =
    fullMonthlyRent > 0 ? fullMonthlyRent : billedAmountForCurrentMonth;
  const isPartialFirstMonthRent =
    currentMonthRentPayment?.amount_cents != null
      ? Math.round((Number(currentMonthRentPayment.amount_cents) / 100) * 100) <
        Math.round(Number(fullMonthlyRent || 0) * 100)
      : rentDisplay.isPartialFirstMonth;
  const rentBreakdownLineItems = splitRentLineItems(
    fullMonthlyRent,
    billedAmountForCurrentMonth
  );

  // Contract length and paid rent months (for Rent card: "X / Y months")
  const contractMonths = useMemo(() => {
    const startIso =
      financialsBooking?.move_in_date || financialsBooking?.start_date;
    const endIso =
      financialsBooking?.move_out_date || financialsBooking?.end_date;
    const start = startIso ? new Date(startIso) : null;
    const end = endIso ? new Date(endIso) : null;
    if (!start || !end || end < start) return null;
    const months =
      (end.getFullYear() - start.getFullYear()) * 12 +
      (end.getMonth() - start.getMonth()) +
      1;
    return Math.max(1, months);
  }, [
    financialsBooking?.move_in_date,
    financialsBooking?.start_date,
    financialsBooking?.move_out_date,
    financialsBooking?.end_date,
  ]);
  const paidRentsForBooking = (paymentsForCurrentBooking || []).filter(
    (p: any) => p.type === "rent" && isPaymentEffectivelyPaid(p)
  );
  const paidMonths = paidRentsForBooking.length;

  // Show red "!" on My Contract when contract is signed but deposit is still due
  const bookingCsForDepositBadge =
    booking?.contract_status != null &&
    String(booking.contract_status).trim() !== ""
      ? String(booking.contract_status).trim().toLowerCase()
      : "";
  const hasContractDepositDueNotification = !!(
    booking &&
    (bookingCsForDepositBadge === "approved" ||
      bookingCsForDepositBadge === "signed") &&
    !hasPaidDeposit &&
    depositAmount
  );

  // Which handover protocol needs the tenant (COL-368): from the day before
  // move-in / seven days before move-out until it is submitted. Same predicate
  // the card inside the tab uses, so the two agree. Feeds
  // `tenantNextActions` below, which turns it into the Check-in/out pip; the
  // pip's own label and chip now come from `tenant-next-action.ts` alongside
  // every other tab's, so one vocabulary covers the whole strip.
  const protocolActionNeeded = useProtocolActionNeeded();

  // Open booking context from URL (e.g. ?bookingId=123) — switch to contract tab and clear param
  useEffect(() => {
    const bookingIdParam = searchParams.get("bookingId");
    if (bookingIdParam) {
      setActiveTab("contract");
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete("bookingId");
          return next;
        },
        { replace: true }
      );
    }
  }, [searchParams, setSearchParams]);

  // DocuSign redirect: tab=contract&docusign=complete&event=signing_complete — sync envelope, refetch, show signed state
  useEffect(() => {
    const tabParam = searchParams.get("tab");
    const docusign = searchParams.get("docusign");
    const event = searchParams.get("event");
    const bookingIdParam = searchParams.get("bookingId");

    if (tabParam === "contract" || docusign === "complete") {
      setActiveTab("contract");
    } else if (tabParam === "checkin") {
      // The move-in and protocol-reminder emails link here by name ("> Bereich
      // Check in/out"). Without this branch every one of those links opened on
      // Home and left the tenant to find the tab themselves.
      setActiveTab("checkin");
    }

    if (event === "ttl_expired") {
      setDocuSignSessionExpiredNotice(true);
      toast.info(
        language === "de"
          ? "Deine Unterzeichnungssitzung ist abgelaufen. Bitte kontaktiere einen Admin. Nach Vertrags-Neuerstellung kannst du &bdquo;In DocuSign öffnen&ldquo; für eine neue Sitzung nutzen."
          : "Your signing session expired. Please contact an admin. After they regenerate the contract, use Open in DocuSign for a new signing session.",
        { duration: 10000 }
      );
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete("event");
          next.delete("docusign");
          return next;
        },
        { replace: true }
      );
      return;
    }

    if (event === "signing_complete" && user?.id) {
      const bookingId = bookingIdParam
        ? parseInt(bookingIdParam, 10)
        : booking?.id != null
          ? Number(booking.id)
          : null;
      if (Number.isFinite(bookingId) && bookingId > 0) {
        invokeSyncDocusignEnvelope(bookingId)
          .then((res) => {
            queryClient.invalidateQueries({
              queryKey: ["active-booking", user?.id],
            });
            queryClient.invalidateQueries({
              queryKey: ["tenant-approved-bookings", user?.id],
            });
            queryClient.invalidateQueries({
              queryKey: ["tenant-all-bookings", user?.id],
            });
            if (res?.updated) {
              toast.success(
                t(
                  "dashboard.contractSignedSuccessfully",
                  "Contract signed successfully. Your signed contract details are shown below."
                )
              );
            }
          })
          .catch((e: any) => {
            const msg = [e?.message, e?.details].filter(Boolean).join(" — ");
            toast.error(
              msg ||
                t(
                  "dashboard.contractStatusUpdateFailed",
                  "Could not update contract status. Your signature was recorded in DocuSign."
                )
            );
            queryClient.invalidateQueries({
              queryKey: ["active-booking", user?.id],
            });
          })
          .finally(() => {
            setSearchParams(
              (prev) => {
                const next = new URLSearchParams(prev);
                next.delete("event");
                next.delete("docusign");
                next.delete("bookingId");
                return next;
              },
              { replace: true }
            );
          });
      }
      // If no bookingId yet (booking still loading), do not clear URL — effect will run again when booking loads and we can sync then.
      return;
    }

    if (docusign === "complete") {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete("docusign");
          return next;
        },
        { replace: true }
      );
    }
  }, [
    searchParams,
    setSearchParams,
    user?.id,
    booking?.id,
    queryClient,
    language,
    t,
  ]);

  // Self-heal: if booking is stuck in signing stage, sync envelope status from DocuSign so
  // "Ready to Sign" updates to "Signed" even when the user never returns via redirect.
  useEffect(() => {
    if (!user?.id || !booking?.id) return;
    // Run on Contract tab (primary UX) and also on Home tab (fallback).
    if (activeTab !== "contract" && activeTab !== "home") return;
    const bid = Number(booking.id);
    if (!Number.isFinite(bid)) return;
    const cs =
      booking.contract_status != null &&
      String(booking.contract_status).trim() !== ""
        ? String(booking.contract_status).trim().toLowerCase()
        : "";
    if (
      !["ready_to_sign", "envelope_draft", "draft"].includes(cs) ||
      !booking.docusign_envelope_id
    )
      return;
    if (lastSyncedEnvelopeBookingIdRef.current === bid) return;
    lastSyncedEnvelopeBookingIdRef.current = bid;
    invokeSyncDocusignEnvelope(bid)
      .then((res) => {
        if (res?.updated) {
          queryClient.invalidateQueries({
            queryKey: ["active-booking", user?.id],
          });
          queryClient.invalidateQueries({
            queryKey: ["tenant-approved-bookings", user?.id],
          });
          queryClient.invalidateQueries({
            queryKey: ["tenant-all-bookings", user?.id],
          });
        }
      })
      .catch(() => {
        lastSyncedEnvelopeBookingIdRef.current = null;
      });
  }, [
    activeTab,
    user?.id,
    booking?.id,
    booking?.contract_status,
    booking?.docusign_envelope_id,
    queryClient,
  ]);

  // On return from Stripe: confirm payment from session (creates row if webhook didn't), then refetch
  useEffect(() => {
    const paymentStatus = searchParams.get("payment");
    const tabParam = searchParams.get("tab");
    const sessionId = searchParams.get("session_id");
    if (paymentStatus === "success" && user?.id) {
      const stayOnContract = tabParam === "contract";
      if (stayOnContract) setActiveTab("contract");
      const confirmAndRefetch = async () => {
        if (sessionId) {
          try {
            await supabase.functions.invoke("confirm-payment-from-session", {
              body: { session_id: sessionId },
            });
          } catch (_) {
            // Ignore; webhook may have already recorded it
          }
        }
        queryClient.invalidateQueries({ queryKey: ["payments", user.id] });
      };
      confirmAndRefetch();
      const t1 = setTimeout(
        () =>
          queryClient.invalidateQueries({ queryKey: ["payments", user.id] }),
        800
      );
      const t2 = setTimeout(
        () =>
          queryClient.invalidateQueries({ queryKey: ["payments", user.id] }),
        2500
      );
      if (stayOnContract) {
        toast.success(
          language === "de"
            ? "Zahlung erfolgreich. Deine Kaution ist eingegangen."
            : "Payment successful. Your deposit has been recorded.",
          {
            duration: 5000,
          }
        );
      } else {
        setActiveTab("home");
        toast.success(p.paymentSuccessful || "Payment successful");
      }
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete("payment");
          next.delete("session_id");
          next.delete("tab");
          return next;
        },
        { replace: true }
      );
      return () => {
        clearTimeout(t1);
        clearTimeout(t2);
      };
    }
  }, [
    searchParams,
    queryClient,
    user?.id,
    setSearchParams,
    language,
    p.paymentSuccessful,
  ]);

  // Get current month for payment matching
  const now = new Date();
  const currentMonth = now.getMonth();
  const currentYear = now.getFullYear();
  const nextMonth = currentMonth + 1;
  const nextYear = nextMonth > 11 ? currentYear + 1 : currentYear;

  // The single canonical "next payment" row for this booking: the earliest
  // unpaid rent row, any date, overdue included — never skipped in favor of
  // a later row. Every "next payment" consumer on this page (the summary
  // tile, the amount charged, the due date shown, and what Pay Now / bank
  // transfer actually settles) resolves through this one value so they can
  // never disagree with each other or with admin's own `nextDue`
  // (tenant-profile-tabs.tsx). Before this, a separate "prefer a future/
  // today due date" selector fed Pay Now while the summary tile used the
  // earliest-unpaid rule, so an overdue August row and a not-yet-due
  // September row showed on the same screen while Pay Now silently charged
  // September (PR #1120 review).
  const upcomingPayment = useMemo(() => {
    const isPaid = (p: any) => isPaymentEffectivelyPaid(p);
    const isVoid = (p: any) =>
      String(p?.status ?? "").toLowerCase() === "canceled";
    const upcomingForBooking =
      bookingIdNum != null
        ? (upcomingPayments as any[]).filter(
            (p: any) => Number(p.booking_id) === bookingIdNum
          )
        : upcomingPayments;
    const rentForBooking = (paymentsForCurrentBooking || []).filter(
      (p: any) => p?.type === "rent"
    );
    // Merge both sources (by id) rather than trying one then falling back to
    // the other: `upcomingPayments` (earliest-10, unbounded date) and
    // `payments`-derived `paymentsForCurrentBooking` (most-recent-24) can
    // each hold half of a paid/unpaid duplicate pair for the same month —
    // deduping against only one source could let the unpaid duplicate win.
    const byId = new Map<string, any>();
    [...upcomingForBooking, ...rentForBooking].forEach((p: any) => {
      if (p?.id != null) byId.set(String(p.id), p);
    });
    return selectEarliestUnpaidRentPayment(
      Array.from(byId.values()),
      isPaid,
      isVoid
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    upcomingPayments,
    paymentsForCurrentBooking,
    bookingIdNum,
    paymentProofsByPaymentId,
  ]);
  const nextRentPayment =
    upcomingPayment &&
    // COL-417: 'partially_paid' belongs in this allow-list. Without it a tenant
    // who has paid part of the month sees no rent due at all — the dashboard
    // goes silent about money they still owe, which is the opposite of what a
    // part-payment should do.
    ["pending", "scheduled", "partially_paid", "overdue"].includes(
      getEffectiveRentPaymentStatus(upcomingPayment)
    )
      ? upcomingPayment
      : null;
  const paidUpcomingPayment =
    upcomingPayment && isPaymentEffectivelyPaid(upcomingPayment)
      ? upcomingPayment
      : null;
  const lastPaidRent =
    paymentsForCurrentBooking?.find(
      (p: any) => p.type === "rent" && isPaymentEffectivelyPaid(p)
    ) || null;

  const addMonthsPreserveDay = (d: Date, months: number): Date => {
    const copy = new Date(d);
    const day = copy.getDate();
    copy.setMonth(copy.getMonth() + months);
    // If month rollover clipped the day (e.g. Jan 31 → Mar 3), clamp to last day of target month.
    if (copy.getDate() !== day) {
      copy.setDate(0);
    }
    return copy;
  };

  // Calculate next payment date: use next unpaid rent due_date, or move-in date (rent starts there), or 1st of next month
  const getNextPaymentDate = (): Date => {
    if (upcomingPayment?.due_date) {
      return new Date(upcomingPayment.due_date);
    }
    if (nextRentPayment?.due_date) {
      return new Date(nextRentPayment.due_date);
    }
    // If we have a last paid rent but no next rent row yet, show the next cycle (prevents skipping a month).
    if (lastPaidRent?.due_date) {
      return addMonthsPreserveDay(new Date(lastPaidRent.due_date), 1);
    }
    // No payment record yet: rent starts on move-in date — use financialsBooking so it works when active booking is null
    const moveInIso =
      financialsBooking?.move_in_date || financialsBooking?.start_date;
    const moveInDate = moveInIso ? new Date(moveInIso) : null;
    if (moveInDate) {
      moveInDate.setHours(0, 0, 0, 0);
      const todayStart = new Date(now);
      todayStart.setHours(0, 0, 0, 0);
      if (moveInDate >= todayStart) return moveInDate;
      // Move-in has passed with no rent row at all: every month since move-in
      // is still owed, so the next payment is the CURRENT month's cycle
      // (anchored on the move-in day, clamped to short months) — not the 1st
      // of next month. Skipping ahead hid the open current month from the
      // tenant while the admin schedule (which projects months from the
      // lease) correctly showed it as due.
      const currentCycle = new Date(
        now.getFullYear(),
        now.getMonth(),
        Math.min(
          moveInDate.getDate(),
          new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate()
        )
      );
      const leaseEndIso =
        financialsBooking?.move_out_date || financialsBooking?.end_date;
      const leaseEnd = leaseEndIso ? new Date(leaseEndIso) : null;
      if (
        !leaseEnd ||
        Number.isNaN(leaseEnd.getTime()) ||
        currentCycle <= leaseEnd
      ) {
        return currentCycle;
      }
    }
    return new Date(now.getFullYear(), now.getMonth() + 1, 1);
  };

  const nextPaymentDate = getNextPaymentDate();
  const nextPaymentDueLabel = nextPaymentDate.toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
  // Whether `lastPaidRent` (the most recently due paid row) actually covers
  // the period we're about to display (`nextPaymentDate`). When there's no
  // real payments row yet for the next cycle, `nextPaymentDate` has already
  // rolled forward past `lastPaidRent`'s month — showing that old paid row's
  // amount/"Paid" badge next to the *new* period's due date mixed two
  // different months' truths on one card (e.g. August's amount + Paid badge
  // under a "1 September" due date while September was actually still open).
  const nextPaymentDueIso = nextPaymentDate.toISOString().slice(0, 10);
  const lastPaidRentCoversNextPeriod = lastPaidRentCoversPeriod(
    lastPaidRent?.due_date,
    nextPaymentDueIso
  );
  /**
   * COL-417: what the tenant still owes on that row, not what it billed.
   *
   * `payments` is selected with `*` here, so `received_cents` is already in
   * hand. Showing the billed figure told a tenant who had paid EUR 300 of a
   * EUR 650 month that EUR 650 was due — while "Pay now" would have collected
   * EUR 350, because `create-rent-payment` nets the receipts server-side. The
   * tile and the charge have to agree, and a tenant reading the larger number
   * is the one at risk of paying twice.
   *
   * Equal to the billed amount for every month nobody part-paid.
   */
  const upcomingSettlement = upcomingPayment
    ? resolveRentSettlement(upcomingPayment as any, () => null)
    : null;
  /**
   * What the next rent invoice BILLS (payment row), else a projected estimate
   * for the displayed period — see `projectedUpcomingChargeEUR`.
   *
   * Deliberately not net of a partial payment: this is the figure the
   * invoice-level derivations below are about (gross-before-credits, and
   * whether the month is pro-rata). Netting it made a full month that happened
   * to be part-paid read as a short invoice, which is what
   * `isUpcomingInvoiceProRata` then labelled "Pro-rata amount for this
   * invoice". What is still OWED is `upcomingRemainingChargeEUR` below — the
   * same billed/open split `selectOpenPastMonthRent` makes with
   * `amount_eur` vs `open_eur`.
   */
  const upcomingChargeEUR =
    upcomingPayment?.amount_cents != null
      ? Number(upcomingPayment.amount_cents) / 100
      : projectedUpcomingChargeEUR({
          nextPaymentDate,
          fullMonthlyRent,
          moveInIso: moveInIsoForRent,
          firstMonthOverride:
            financialsBooking?.first_month_rent_override ?? null,
        });
  /**
   * COL-417: what is still open on that invoice — the billed figure less what
   * has already arrived. This, not `upcomingChargeEUR`, is what the amount-due
   * tile shows and what every pay action charges; equal to the billed amount
   * for every month nobody part-paid.
   */
  const upcomingRemainingChargeEUR = upcomingSettlement?.isPartial
    ? upcomingSettlement.remainingCents / 100
    : upcomingChargeEUR;
  /**
   * COL-472: which of a booking's open rent rows carries its Mahngebühren —
   * the reminders list's rule (`feeCarrierPaymentId`), over the same merged
   * `payments` + `upcomingPayments` set `getNextRentForBooking` reads, so a
   * long tenancy's older open month is not missed.
   */
  const feeCarrierIdForBooking = (
    bookingId: number | null,
    rentDueDay: unknown
  ): string | null => {
    if (bookingId == null) return null;
    const byId = new Map<string, any>();
    [...(payments || []), ...(upcomingPayments || [])].forEach((p: any) => {
      if (
        p?.id != null &&
        Number(p.booking_id) === bookingId &&
        p.type === "rent" &&
        String(p.status ?? "").toLowerCase() !== "canceled" &&
        !isPaymentEffectivelyPaid(p)
      )
        byId.set(String(p.id), p);
    });
    return feeCarrierPaymentId({
      openRows: Array.from(byId.values()),
      rentDueDay: Number(rentDueDay) || 1,
      todayIso: todayIsoForRent,
    });
  };
  /**
   * COL-472: the booking's Mahngebühren (`accumulated_fees_eur`, one pool, never
   * part of a month's amount_cents). They show on the displayed month only when
   * it is the one the reminders list puts them on; otherwise — rent settled, or
   * the fees sit on a later overdue month — they are listed on their own, so
   * the total owed is never understated and never counted twice.
   *
   * Display only: card / SEPA checkout still charges the rent
   * (`upcomingRemainingChargeEUR`), which the tile says next to the split.
   */
  const upcomingFeePoolCents = accruedFeeCents(
    (financialsBooking as { accumulated_fees_eur?: unknown } | null)
      ?.accumulated_fees_eur
  );
  const upcomingFeeCarrierId = feeCarrierIdForBooking(
    bookingIdNum,
    (financialsBooking as { rent_due_date?: unknown } | null)?.rent_due_date
  );
  const upcomingCarriesFees =
    !!upcomingPayment &&
    !paidUpcomingPayment &&
    upcomingFeeCarrierId != null &&
    String(upcomingPayment.id) === upcomingFeeCarrierId;
  const upcomingOwed = owedWithFees({
    rentOpenCents: Math.round(Number(upcomingRemainingChargeEUR || 0) * 100),
    feesEur: upcomingFeePoolCents / 100,
    carriesFees: upcomingCarriesFees,
  });
  /** Fees owed that the displayed month does not carry. */
  const upcomingStandaloneFeeCents = upcomingCarriesFees
    ? 0
    : upcomingFeePoolCents;
  const eurLabel = (cents: number) =>
    `€${(cents / 100).toLocaleString("de-DE", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  /**
   * EUR 100 referral credits already taken off the displayed row (COL-434).
   * The credit is applied to the NEXT not-yet-due rent row, never the open
   * month, so it shows up here exactly when that row becomes "next" — while
   * September is open the tile shows September at full rent, and only once it
   * is paid does October appear with the strikethrough.
   */
  const upcomingReferralCreditCents = getReferralCreditCents(upcomingPayment);
  const upcomingReferralCreditEUR = upcomingReferralCreditCents / 100;
  /** The rent this invoice was for, before referral credits. */
  const upcomingGrossChargeEUR = upcomingChargeEUR + upcomingReferralCreditEUR;
  const upcomingReferralCreditNote =
    upcomingReferralCreditCents > 0
      ? referralCreditDashboardNote({
          cents: upcomingReferralCreditCents,
          dueDateIso: upcomingPayment?.due_date ?? null,
          language: language === "de" ? "de" : "en",
        })
      : null;
  // Compared against the GROSS figure: a month that is EUR 100 short because of
  // a referral credit is not pro-rata, and used to be labelled as such.
  const isUpcomingInvoiceProRata =
    fullMonthlyRent > 0 &&
    Math.round(upcomingGrossChargeEUR * 100) <
      Math.round(fullMonthlyRent * 100);
  /**
   * A SEPA debit for the displayed invoice is at the bank and has not settled
   * (feedback 9). Nothing more for the tenant to do: the pay actions come off
   * so the same rent can't be paid twice while the collection is in flight.
   */
  const sepaChargeInFlight = isSepaDebitInFlight(upcomingPayment);
  const paymentStatusLabel = (() => {
    // Prefer "Paid" when the upcoming payment is effectively paid (Stripe or approved proof).
    if (paidUpcomingPayment) return "Paid";
    if (upcomingPayment && isPaymentEffectivelyPaid(upcomingPayment))
      return "Paid";

    // A submitted direct debit outranks "Overdue" (feedback 9): the money is on
    // its way, so telling the tenant it is overdue asks them to pay twice.
    if (sepaChargeInFlight) return "SepaInProgress";

    // Overdue only when there is an unpaid rent payment past its due date.
    // Scans the merged `paymentsForCurrentBooking` + `upcomingPayments`
    // (this booking's) candidate set — `paymentsForCurrentBooking` alone is
    // truncated to the newest 24 `payments` rows, so on a long tenancy the
    // selected `upcomingPayment` could already be resolving an older overdue
    // row from `upcomingPayments` that this scan would otherwise miss,
    // showing "Upcoming" instead of "Overdue" (PR #1120 follow-up review).
    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);
    const overdueCandidates = (() => {
      const byId = new Map<string, any>();
      [
        ...(paymentsForCurrentBooking || []),
        ...(upcomingPayments || []),
      ].forEach((p: any) => {
        if (
          p?.id != null &&
          (bookingIdNum == null || Number(p.booking_id) === bookingIdNum)
        ) {
          byId.set(String(p.id), p);
        }
      });
      return Array.from(byId.values());
    })();
    const hasOverdue = overdueCandidates.some((p: any) => {
      if (p?.type !== "rent" || !p?.due_date) return false;
      if (isPaymentEffectivelyPaid(p)) return false;
      const eff = getEffectiveRentPaymentStatus(p);
      if (eff === "overdue") return true;
      const due = new Date(p.due_date);
      due.setHours(0, 0, 0, 0);
      // COL-417: a part-paid month past its due date is still overdue on the
      // remaining balance — fees keep accruing on it, so the tenant must not be
      // told everything is fine.
      return (
        (eff === "pending" ||
          eff === "scheduled" ||
          eff === "partially_paid") &&
        due < todayStart
      );
    });
    if (hasOverdue) return "Overdue";

    if (nextRentPayment) return "Upcoming";
    // Only borrow `lastPaidRent`'s "Paid" badge when it actually covers the
    // period being displayed — once we've projected ahead to a future,
    // not-yet-materialized cycle (`nextPaymentDate`), that old paid row
    // belongs to a past month and must not be shown as if it settled the
    // upcoming one.
    if (lastPaidRent && lastPaidRentCoversNextPeriod) return "Paid";
    return "Pending";
  })();

  // Next rent due per booking (for Home tab approved rooms list). Resolves
  // through the same canonical `selectEarliestUnpaidRentPayment` selector as
  // `upcomingPayment` above so the per-room tile can never disagree with the
  // main "Next payment" card about which row (month) is next — the earliest
  // unpaid rent row, overdue included, never skipped in favor of a later row
  // (PR #1120 review).
  const getNextRentForBooking = (bookingId: number) => {
    // Merge `payments` (newest-24, for this booking) with `upcomingPayments`
    // (full unpaid-candidate history, for this booking) by id: `payments`
    // alone truncates a long tenancy the same way `paymentsForCurrentBooking`
    // does above, so on its own it could omit the actual earliest unpaid row
    // for an approved room and disagree with the main "Next payment" card
    // (PR #1120 follow-up review).
    const byId = new Map<string, any>();
    [...(payments || []), ...(upcomingPayments || [])].forEach((p: any) => {
      if (p?.booking_id === bookingId && p?.type === "rent" && p?.id != null) {
        byId.set(String(p.id), p);
      }
    });
    return selectEarliestUnpaidRentPayment(
      Array.from(byId.values()),
      (p: any) => isPaymentEffectivelyPaid(p),
      (p: any) => String(p?.status ?? "").toLowerCase() === "canceled"
    );
  };

  // For "Next payment" card: show the next unpaid payment date; when no payment record, use move-in date (match admin)
  // `upcomingPayment` above is the same canonical earliest-unpaid selector
  // this card used to compute separately (see PR #1120 review) — reusing it
  // here is what keeps this tile and the "Upcoming payment" card in
  // agreement on which row (month) is next.
  const nextUnpaidPaymentDate = (() => {
    if (upcomingPayment?.due_date) return new Date(upcomingPayment.due_date);
    if (lastPaidRent?.due_date)
      return addMonthsPreserveDay(new Date(lastPaidRent.due_date), 1);
    const moveInIsoUnpaid =
      financialsBooking?.move_in_date || financialsBooking?.start_date;
    const moveIn = moveInIsoUnpaid ? new Date(moveInIsoUnpaid) : null;
    if (moveIn) {
      moveIn.setHours(0, 0, 0, 0);
      const todayStart = new Date(now);
      todayStart.setHours(0, 0, 0, 0);
      if (moveIn >= todayStart) return moveIn;
    }
    return new Date(now.getFullYear(), now.getMonth() + 1, 1);
  })();
  const nextUnpaidPaymentDueLabel = nextUnpaidPaymentDate.toLocaleDateString(
    undefined,
    {
      year: "numeric",
      month: "long",
      day: "numeric",
    }
  );
  // Month/year only, for referencing which rent this is (e.g. "settle the
  // August 2026 rent") without repeating the full date already shown above it.
  const nextUnpaidPaymentMonthLabel = nextUnpaidPaymentDate.toLocaleDateString(
    language === "de" ? "de-DE" : undefined,
    { month: "long", year: "numeric" }
  );
  const hasUnpaidUpcoming = !!upcomingPayment;

  const normalizedContractStatus =
    booking?.contract_status != null &&
    String(booking.contract_status).trim() !== ""
      ? String(booking.contract_status).trim().toLowerCase()
      : "";
  // `draft` is deliberately NOT here. An admin generates the contract PDF
  // first, reads it, and releases it in a separate confirmed step; until that
  // send happens the contract does not exist as far as the tenant is
  // concerned — no signing UI, no banner, and the same "being prepared" copy
  // as not_ready. Only `ready_to_sign` / `envelope_draft` mean released.
  const isContractSigningStage = ["envelope_draft", "ready_to_sign"].includes(
    normalizedContractStatus
  );

  /**
   * Everything the tenant has open right now, for the pips on the tab strip
   * and the mobile drawer (COL-368).
   *
   * Composed here rather than inside a hook because every fact but the
   * referral one is already derived above for the card that renders it —
   * reading those straight is what keeps a pip and the card it leads to from
   * ever disagreeing. Placed after `paymentStatusLabel` and
   * `isContractSigningStage` on purpose: both are `const` declared above and
   * neither is hoisted.
   */
  const referralRewardStalled = useReferralRewardStalled();
  /**
   * `paymentStatusLabel`'s overdue branch compares against `new Date()` at
   * render time, so the Payments pip only flips once something re-renders
   * this component. A tick is what guarantees that happens while the
   * dashboard sits open across a due date — the same reason #1387 added it
   * for the protocol lead-time boundary.
   *
   * `useProtocolActionNeeded()` above already ticks internally, which is why
   * this worked before it was written down. That is an accident of the
   * protocol pip existing: remove or conditionalise that hook and the rent
   * boundary silently stops refreshing, with no test failing. Depending on
   * the clock here directly makes it deliberate.
   *
   * Called for the re-render, not for a value: `paymentStatusLabel` is a
   * plain render-time const, so the memo below already sees its new value
   * once a render happens. Listing the tick as a memo dependency would be
   * genuinely redundant (and `react-hooks/exhaustive-deps` says so) — the
   * render is the whole product.
   */
  useNowTick();
  const tenantNextActions = useMemo(
    () =>
      computeTenantNextActions({
        contractAwaitingSignature: isContractSigningStage,
        depositDue: hasContractDepositDueNotification,
        rentOverdue: paymentStatusLabel === "Overdue",
        protocolKind: protocolActionNeeded?.kind ?? null,
        supportUnread: Number(tenantSupportUnreadTotal || 0),
        referralRewardStalled,
      }),
    [
      isContractSigningStage,
      hasContractDepositDueNotification,
      paymentStatusLabel,
      protocolActionNeeded?.kind,
      tenantSupportUnreadTotal,
      referralRewardStalled,
    ]
  );
  const tenantActionTabs = useMemo(
    () => tenantActionsByTab(tenantNextActions),
    [tenantNextActions]
  );
  const pipLang: "en" | "de" = language === "de" ? "de" : "en";
  /** The pip for one tab, or null when that tab has nothing open. */
  const renderTabPip = (tab: NextActionTab, ringClassName?: string) => {
    const action = tenantActionTabs[tab]?.[0];
    if (!action) return null;
    return (
      <TabActionPip
        count={action.kind === "support" ? action.count : undefined}
        ariaLabel={nextActionAriaLabel(action, pipLang)}
        ringClassName={ringClassName}
        data-testid={`${tab}-action-pip`}
      />
    );
  };
  /** The trailing chip on a mobile drawer row, naming what is open there. */
  const tabChipLabel = (tab: NextActionTab): string | null => {
    const actions = tenantActionTabs[tab];
    if (!actions?.length) return null;
    // A tab can in principle hold more than one action, so name each of
    // them rather than making the tenant open the tab to find the second.
    // Contract's two ("sign", "deposit") are in fact mutually exclusive
    // today — both read the one `booking.contract_status`, and
    // envelope_draft/ready_to_sign is disjoint from approved/signed — so
    // this join is defensive rather than a path production reaches.
    // Support is deliberately absent: its pip already carries the unread
    // number, so a chip repeating it puts the same count on the row twice.
    // That row has never had a trailing chip, and widening the pip is no
    // reason to add one.
    const labelled = actions.filter((action) => action.kind !== "support");
    if (!labelled.length) return null;
    return labelled
      .map((action) => nextActionChipLabel(action.kind, pipLang))
      .join(" · ");
  };

  const contractStatusLabel = (() => {
    const de = language === "de";
    if (!normalizedContractStatus) {
      if (!booking?.status) return "—";
      return booking.status[0].toUpperCase() + booking.status.slice(1);
    }
    switch (normalizedContractStatus) {
      case "not_ready":
      case "draft":
        return de ? "In Vorbereitung" : "Being prepared";
      case "envelope_draft":
      case "ready_to_sign":
        return de ? "Bereit zum Unterschreiben" : "Ready to Sign";
      case "awaiting_approval":
        return de ? "Wartet auf Freigabe" : "Awaiting Approval";
      case "approved":
      case "signed":
        return de ? "Vertrag unterschrieben" : "Contract Signed";
      default:
        return String(booking?.contract_status ?? normalizedContractStatus);
    }
  })();

  const getBookingContractStatusLabel = (b: any) => {
    const de = language === "de";
    const s = b?.contract_status;
    if (!s)
      return b?.status
        ? String(b.status)[0].toUpperCase() + String(b.status).slice(1)
        : "—";
    const sn = String(s).trim().toLowerCase();
    const map: Record<string, string> = {
      not_ready: de ? "In Vorbereitung" : "Being prepared",
      // Generated but not released by an admin — see isContractSigningStage.
      draft: de ? "In Vorbereitung" : "Being prepared",
      envelope_draft: de ? "Bereit zum Unterschreiben" : "Ready to Sign",
      ready_to_sign: de ? "Bereit zum Unterschreiben" : "Ready to Sign",
      awaiting_approval: de ? "Wartet auf Freigabe" : "Awaiting Approval",
      approved: de ? "Vertrag unterschrieben" : "Contract Signed",
      signed: de ? "Vertrag unterschrieben" : "Contract Signed",
    };
    return map[sn] || s;
  };

  // Home tab: status flow – stage labels from homeLabels.stages (EN/DE)
  const HOME_STAGE_BADGE_CLASSES: Record<number, string> = {
    0: "bg-indigo-50 text-indigo-700 border-indigo-200",
    1: "bg-amber-50 text-amber-700 border-amber-200",
    2: "bg-blue-50 text-blue-700 border-blue-200",
    3: "bg-emerald-50 text-emerald-700 border-emerald-200",
    4: "bg-amber-50 text-amber-700 border-amber-200",
    5: "bg-emerald-50 text-emerald-700 border-emerald-200",
  };
  const getHomeRoomStageIndex = (
    b: any,
    paymentPending: { booking_id?: number }[],
    allPayments: any[],
    _allBookings?: any[]
  ): number => {
    const bid = Number(b.id);
    const paymentsList = allPayments || [];

    // Only show Paid when there is at least one payment explicitly linked to this booking and marked paid
    const paidForThisBooking = paymentsList.filter(
      (p: any) =>
        (p.status === "paid" || !!p.paid_at) && Number(p.booking_id) === bid
    );
    if (paidForThisBooking.length > 0) return 5;

    // Treat approved/signed/manual contract completion as "Contract signed" stage.
    if (
      b.contract_status === "approved" ||
      b.contract_status === "signed" ||
      !!b.contract_signed_at
    ) {
      const hasUnpaidRent = paymentPending.some(
        (p: any) => Number(p.booking_id) === bid
      );
      if (hasUnpaidRent) return 4;
      return 3;
    }

    // Applied: just submitted, no contract released yet (an unsent draft counts
    // as "no contract" here — the tenant has nothing to act on).
    if (
      b.status === "pending" &&
      isContractNotReleasedToTenant(b.contract_status)
    )
      return 0;
    if (b.contract_status === "awaiting_approval") return 1;
    if (
      b.status === "pending" &&
      !isContractNotReleasedToTenant(b.contract_status) &&
      b.contract_status !== "awaiting_approval"
    )
      return 2;
    if (b.status === "confirmed") {
      const hasUnpaidRent = paymentPending.some(
        (p: any) => Number(p.booking_id) === bid
      );
      if (hasUnpaidRent) return 4;
      return 3;
    }
    return 0;
  };

  const formatPaymentType = (type: string, lang: "en" | "de" = baseLang) => {
    if (lang === "de") {
      switch (type) {
        case "deposit":
          return "Kaution (einmalig)";
        case "rent":
          return "Monatsmiete";
        default:
          return type;
      }
    }
    switch (type) {
      case "deposit":
        return "Security deposit (one-time)";
      case "rent":
        return "Monthly rent";
      default:
        return type;
    }
  };

  // Fetch user profile to get name
  const { data: profile } = useQuery({
    queryKey: ["user-profile", user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      const { data, error } = await supabase
        .from("profiles")
        .select("name, email, phone, date_of_birth")
        .eq("id", user.id)
        .single();
      if (error) throw error;
      return data as {
        name: string | null;
        email: string | null;
        phone: string | null;
        date_of_birth: string | null;
      } | null;
    },
    enabled: !!user?.id,
  });

  // After account creation the Settings badge was firing on pending documents,
  // which a new tenant can't act on — confusing (COL-260). Surface a concrete,
  // tenant-actionable signal instead: a missing date of birth. When set, the
  // Settings entry shows the badge and opening it lands on the Profile tab so
  // the tenant knows to enter their DoB.
  const needsProfileCompletion = useMemo(() => {
    if (!profile) return false;
    const dob = (profile as { date_of_birth?: string | null }).date_of_birth;
    return !dob || !String(dob).trim();
  }, [profile]);

  // Classify the document notification so we can tell the tenant what to do,
  // not just that "something" is wrong. A rejected doc (with a note) is
  // actionable — re-upload; a pending doc is only informational.
  const documentIssueKind = useMemo<"rejected" | "pending" | null>(() => {
    const allDocs = [...(identityDocuments || []), ...(salaryDocuments || [])];
    if (allDocs.some((doc: any) => doc.status === "rejected" && doc.notes)) {
      return "rejected";
    }
    if (allDocs.some((doc: any) => doc.status === "pending")) {
      return "pending";
    }
    return null;
  }, [identityDocuments, salaryDocuments]);

  // Resolve the Settings badge into the exact tab the tenant should land on,
  // so tapping Settings jumps straight to the issue (WhatsApp-style deep link)
  // instead of dumping them on the default tab. Profile completion is the most
  // tenant-actionable signal (COL-260), so it wins when both are present.
  const settingsAlertTab = useMemo<"profile" | "documents" | null>(() => {
    if (needsProfileCompletion) return "profile";
    if (hasDocumentNotifications) return "documents";
    return null;
  }, [needsProfileCompletion, hasDocumentNotifications]);

  // Settings is no longer a tab here — it lives in the top navbar
  // (Navigation.tsx), which can't reach this component's dialog state. It
  // links to /dashboard?settings=<tab> instead; we open the modal on that
  // tab and strip the param so a refresh or Back doesn't reopen it.
  useEffect(() => {
    const wanted = searchParams.get("settings");
    if (!wanted) return;
    // Accept the displayed tab names as aliases of the ids, so a link written
    // against the labels ("personal", "login") lands where it reads.
    const aliases: Record<string, typeof settingsTab> = {
      profile: "profile",
      personal: "profile",
      documents: "documents",
      language: "language",
      password: "password",
      login: "password",
    };
    const tab = aliases[wanted] ?? settingsAlertTab ?? "profile";
    setSettingsTab(tab);
    setSettingsOpen(true);
    // Updater form, like every other param-stripping effect here: a snapshot
    // could re-add a param another effect deleted in the same commit.
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("settings");
        return next;
      },
      { replace: true }
    );
    // settingsAlertTab is only the fallback for an unrecognised value and
    // must not re-trigger this effect once the param is gone.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, setSearchParams]);

  // Initialize profile form when profile loads (include phone from latest booking if profile has none)
  useEffect(() => {
    if (!profile) return;
    if (isEditingProfile) return;
    const profPhone = (profile as { phone?: string | null }).phone?.trim();
    const fromBooking =
      (allBookings as { phone?: string | null }[])
        .find((b) => b.phone?.trim())
        ?.phone?.trim() ?? "";
    setProfileForm({
      name: profile.name || "",
      email: profile.email || user?.email || "",
      phone: profPhone || fromBooking || "",
      dateOfBirth:
        (profile as { date_of_birth?: string | null }).date_of_birth ?? "",
    });
  }, [profile, user?.email, allBookings, isEditingProfile]);

  // Profile update mutation

  // DocuSign date-of-birth gate (COL-258): open the DoB confirm/insert dialog,
  // prefilled from the saved profile, before the DocuSign confirm dialog.
  const openDobGate = useCallback(() => {
    setDobInput(
      (profile as { date_of_birth?: string | null } | null)?.date_of_birth ?? ""
    );
    dobProceedingRef.current = false;
    setDobConfirmOpen(true);
  }, [profile]);

  // Persist DoB (if changed) then advance to the existing DocuSign confirm dialog.
  const handleDobConfirm = useCallback(async () => {
    // Same rules as V2's gate (`@/lib/contract-dob-gate`), including the
    // 18-year minimum (COL-629).
    const checked = checkDateOfBirth(dobInput);
    if (!checked.ok) {
      toast.error(
        checked.reason === "empty"
          ? language === "de"
            ? "Bitte gib dein Geburtsdatum ein."
            : "Please enter your date of birth."
          : checked.reason === "underage"
            ? language === "de"
              ? "Du musst mindestens 18 Jahre alt sein, um den Mietvertrag zu unterschreiben."
              : "You must be at least 18 years old to sign the rental contract."
            : language === "de"
              ? "Bitte gib ein gültiges Geburtsdatum ein."
              : "Please enter a valid date of birth."
      );
      return;
    }
    const value = checked.value;
    if (!user?.id) {
      toast.error(
        language === "de" ? "Nicht angemeldet." : "Not authenticated."
      );
      return;
    }
    const current =
      (profile as { date_of_birth?: string | null } | null)?.date_of_birth ??
      "";
    setSavingDob(true);
    try {
      if (value !== current) {
        const { error } = await supabase
          .from("profiles")
          .update({ date_of_birth: value } as never)
          .eq("id", user.id);
        if (error) throw error;
        queryClient.invalidateQueries({
          queryKey: ["user-profile", user.id],
        });
      }
      // Transition to the DocuSign confirm dialog without clearing the pending action.
      dobProceedingRef.current = true;
      setDobConfirmOpen(false);
      setDocuSignConfirmOpen(true);
    } catch (e: any) {
      toast.error(
        e?.message ||
          (language === "de"
            ? "Geburtsdatum konnte nicht gespeichert werden."
            : "Could not save date of birth.")
      );
    } finally {
      setSavingDob(false);
    }
  }, [dobInput, language, profile, user?.id, queryClient]);

  const updateStayDatesMutation = useMutation({
    mutationFn: async ({
      bookingId,
      moveIn,
      moveOut,
    }: {
      bookingId: number;
      moveIn: string;
      moveOut: string;
    }) => {
      if (!user?.id) throw new Error("Not signed in");
      const minTo = getMinToDateAfterSixMonths(moveIn);
      if (moveOut < minTo) {
        throw new Error("MIN_TO");
      }
      const { error } = await supabase
        .from("bookings")
        .update({
          move_in_date: moveIn,
          start_date: moveIn,
          end_date: moveOut,
          move_out_date: moveOut,
        })
        .eq("id", bookingId)
        .eq("user_id", user.id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["tenant-all-bookings", user?.id],
      });
      queryClient.invalidateQueries({
        queryKey: ["tenant-approved-bookings", user?.id],
      });
      queryClient.invalidateQueries({ queryKey: ["active-booking", user?.id] });
      toast.success(getHomeLabels(baseLang).datesUpdated);
      setStayDatesOpen(false);
      setStayDatesBookingId(null);
    },
    onError: (error: unknown) => {
      const msg = error instanceof Error ? error.message : String(error);
      const lbl = getHomeLabels(baseLang);
      if (msg === "MIN_TO") {
        toast.error(lbl.dateValidationError);
      } else if (msg.includes("Move dates cannot be changed")) {
        toast.error(lbl.dateLockedNote);
      } else {
        toast.error(
          msg || t("dashboard.failedToUpdateDates", "Failed to update dates")
        );
      }
    },
  });

  // Fetch favorited rooms with property information
  // Group by property to match Properties page behavior (one entry per property)
  const { data: favoritesData, isLoading: favoritesLoading } = useQuery({
    queryKey: ["favorites", user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      const { data: favorites, error: favError } = await supabase
        .from("favorites")
        .select("room_id")
        .eq("user_id", user.id);

      if (favError) throw favError;
      if (!favorites || favorites.length === 0) return [];

      const roomIds = favorites.map((f: any) => f.room_id);
      if (roomIds.length === 0) return [];

      // Fetch rooms with their property information (no limit - get all favorited rooms)
      const { data: rooms, error: roomsError } = await supabase
        .from("rooms")
        .select(
          `
          *,
          properties (
            id,
            title,
            city,
            address,
            zip_code,
            image_url
          )
        `
        )
        .in("id", roomIds);

      if (roomsError) throw roomsError;
      const roomList = rooms || [];

      // Preserve order of favorites (Supabase .in() does not guarantee order)
      const orderMap = new Map(roomIds.map((id: number, i: number) => [id, i]));
      roomList.sort(
        (a: { id: number }, b: { id: number }) =>
          (orderMap.get(a.id) ?? 999) - (orderMap.get(b.id) ?? 999)
      );

      return roomList;
    },
    enabled: !!user?.id,
  });

  // Toggle favorite mutation
  const toggleFavoriteMutation = useMutation({
    mutationFn: async ({ roomId }: { roomId: number }) => {
      if (!user?.id) {
        throw new Error("Please log in to manage favorites");
      }

      // Check if already favorited
      const { data: existing } = await supabase
        .from("favorites")
        .select("id")
        .eq("user_id", user.id)
        .eq("room_id", roomId)
        .maybeSingle();

      if (existing) {
        // Remove from favorites
        const { error } = await supabase
          .from("favorites")
          .delete()
          .eq("user_id", user.id)
          .eq("room_id", roomId);
        if (error) throw error;
      } else {
        // Add to favorites
        const { error } = await supabase
          .from("favorites")
          .insert([{ user_id: user.id, room_id: roomId }]);
        if (error) throw error;
      }

      // Refetch favorites
      queryClient.invalidateQueries({ queryKey: ["favorites", user?.id] });
    },
    onSuccess: () => {
      toast.success(
        t("dashboard.favorites.removed") || "Removed from favorites"
      );
    },
    onError: (error: any) => {
      toast.error(
        error.message ||
          t("dashboard.failedToUpdateFavorites", "Failed to update favorites")
      );
    },
  });

  // Keep favorites page in range when list length changes (e.g. after removing items)
  const favoritesTotalPages = favoritesData?.length
    ? Math.ceil(favoritesData.length / FAVORITES_ROOMS_PER_PAGE)
    : 1;
  useEffect(() => {
    if (favoritesPage > favoritesTotalPages && favoritesTotalPages >= 1) {
      setFavoritesPage(favoritesTotalPages);
    }
  }, [favoritesPage, favoritesTotalPages]);

  // Get display name: profile name, user metadata name, email, or fallback
  const displayName =
    profile?.name ||
    user?.user_metadata?.name ||
    user?.email?.split("@")[0] ||
    "there";

  // Fallback labels when t() returns empty (e.g. key not in Storyblok) – must be before navigationItems.
  // In dev mode t() returns the raw key when missing; treat that as missing too so the hardcoded
  // language-aware fallback below kicks in instead of leaking "dashboard.tab.support" into the UI.
  const tabLabel = (key: string, en: string, de: string) => {
    const value = t(key);
    if (value && value !== key) return value;
    return language === "de" ? de : en;
  };
  const dashboardWelcome =
    t("dashboard.welcome") ||
    (language === "de" ? "Willkommen zurück" : "Welcome back");
  const dashboardSubtitle =
    language === "de"
      ? "Verwalten Sie Ihr Co-Living-Erlebnis"
      : "Manage your co-living experience";
  /** Auth email is the one a reset link can be sent to; profile.email is a mirror. */
  const communityLabel = tabLabel(
    "dashboard.tab.community",
    "Community",
    "Community"
  );

  const homeLabels = getHomeLabels(baseLang);

  // Navigation items configuration (with language fallbacks when t() is empty)
  const navigationItems = [
    {
      value: "home",
      icon: HomeIcon,
      label: tabLabel("dashboard.tab.home", "Home", "Start"),
    },
    {
      value: "favorites",
      icon: Heart,
      label: tabLabel("dashboard.tab.favorites", "Favorites", "Favoriten"),
    },
    {
      value: "contract",
      icon: FileText,
      label: tabLabel("dashboard.tab.contract", "Contract", "Mein Vertrag"),
    },
    {
      value: "payments",
      icon: CreditCard,
      label: tabLabel("dashboard.tab.payments", "Payments", "Zahlungen"),
    },
    {
      value: "checkin",
      icon: LogIn,
      label: tabLabel("dashboard.tab.checkin", "Check-in", "Ein-/Auszug"),
    },
    {
      value: "maintenance",
      icon: Headphones,
      label: tabLabel("dashboard.tab.support", "Support", "Hilfe"),
    },
    {
      value: "community",
      icon: Users,
      label: tabLabel("dashboard.tab.community", "Community", "Community"),
    },
    {
      value: "perks",
      icon: Gift,
      label: tabLabel("dashboard.tab.perks", "Perks", "Lokale Vorteile"),
    },
    {
      value: "referrals",
      icon: Share2,
      label: tabLabel("dashboard.tab.referrals", "Referrals", "Empfehlungen"),
    },
  ];

  // COL-447: an owner-portal-only account's redirect above now waits on the
  // demo flag as well as roles, so there's a real (network-latency-bound, up
  // to the 5s demo-flag timeout) window after roles resolve where we don't
  // yet know whether this account is the demo exemption. Render a neutral
  // loading state through that window instead of the interactive tenant
  // dashboard — every normal tenant (isOwnerPortalOnly false) skips this
  // entirely and renders as before.
  if (isOwnerPortalOnly && !authLoading && !isDemoDualViewReady) {
    return (
      <div className="min-h-screen pt-24 pb-20 lg:pb-20 bg-muted flex items-center justify-center">
        <div className="flex flex-col items-center justify-center py-16">
          <Loader2 className="w-10 h-10 animate-spin text-muted-foreground mb-4" />
          <p className="text-muted-foreground">
            {t("dashboard.loading", "Loading...")}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-20 lg:pb-20 bg-muted">
      <PaidCelebration
        celebration={celebration}
        language={language}
        onDismiss={dismissCelebration}
      />
      <div className="container px-4 pb-4">
        {/* Mobile Header with Menu Button */}
        <div className="lg:hidden mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-foreground mb-1">
              {dashboardWelcome}, {displayName}!
            </h1>
            <p className="text-sm text-muted-foreground">{dashboardSubtitle}</p>
          </div>
          <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="lg:hidden">
                <Menu className="h-6 w-6" />
                <span className="sr-only">
                  {t("dashboard.openMenu", "Open menu")}
                </span>
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-[280px] sm:w-[320px] p-0">
              <div className="flex flex-col h-full">
                <TenantDrawerHeader
                  displayName={displayName}
                  email={user?.email || "User"}
                  settingsLabel={t(
                    "nav.settings",
                    language === "de" ? "Einstellungen" : "Settings"
                  )}
                  onOpenSettings={() => {
                    setSettingsTab("profile");
                    setSettingsOpen(true);
                    setSidebarOpen(false);
                  }}
                />

                {/* Navigation Items */}
                <nav className="flex-1 overflow-y-auto p-4">
                  <div className="space-y-1">
                    {navigationItems.map(({ value, icon: Icon, label }) => (
                      <SheetClose key={value} asChild>
                        <button
                          onClick={() => {
                            setActiveTab(value);
                            setSidebarOpen(false);
                          }}
                          className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                            activeTab === value
                              ? "bg-primary text-primary-foreground"
                              : "text-foreground hover:bg-muted"
                          }`}
                        >
                          <div className="relative">
                            <Icon className="h-5 w-5" />
                            {/* One pip for every tab that has something open.
                                The ring has to disappear into whatever is
                                behind it, and the active row is filled with
                                the primary colour rather than the card. */}
                            {renderTabPip(
                              value as NextActionTab,
                              cn(
                                "-top-1 -right-1",
                                activeTab === value
                                  ? "border-primary"
                                  : "border-background"
                              )
                            )}
                          </div>
                          <span className="font-medium">{label}</span>
                          {tabChipLabel(value as NextActionTab) && (
                            <Badge
                              variant="destructive"
                              className="ml-auto text-xs"
                            >
                              {tabChipLabel(value as NextActionTab)}
                            </Badge>
                          )}
                        </button>
                      </SheetClose>
                    ))}
                    {/* Settings intentionally absent: it now lives in the top
                        navbar's account menu for tenants (one canonical entry
                        point on both mobile and desktop). */}
                  </div>
                </nav>
              </div>
            </SheetContent>
          </Sheet>
        </div>

        {/* Desktop Header */}
        <div className="hidden lg:block mb-8">
          <h1 className="text-3xl font-bold text-foreground mb-2">
            {dashboardWelcome}, {displayName}!
          </h1>
          <p className="text-muted-foreground">{dashboardSubtitle}</p>
        </div>

        <Tabs
          value={activeTab}
          onValueChange={(v) => {
            // When the tenant opens Maintenance, ack all current ticket statuses so the
            // red WhatsApp pip clears immediately — the visit itself is the read receipt
            // (matches WhatsApp's "open the chat list ⇒ unread cleared" behaviour).
            if (v === "maintenance" && user?.id) {
              for (const r of tenantIssueRows || []) {
                if (r.id && r.status) {
                  setLocalStatusSeen(user.id, r.id, r.status);
                }
              }
              queryClient.invalidateQueries({
                queryKey: ["tenant", "issues", "support-unread-total"],
              });
            }
            setActiveTab(v);
          }}
          className="space-y-6"
        >
          {/* Desktop Tabs Navigation - Hidden on mobile */}
          <TabsList className="hidden lg:grid lg:grid-cols-9 gap-2 bg-transparent h-auto p-0">
            <TabsTrigger
              value="home"
              className="flex flex-col items-center gap-2 p-4"
            >
              <HomeIcon className="w-5 h-5" />
              <span className="text-xs">
                {tabLabel("dashboard.tab.home", "Home", "Start")}
              </span>
            </TabsTrigger>
            <TabsTrigger
              value="favorites"
              className="flex flex-col items-center gap-2 p-4"
            >
              <Heart className="w-5 h-5" />
              <span className="text-xs">
                {tabLabel("dashboard.tab.favorites", "Favorites", "Favoriten")}
              </span>
            </TabsTrigger>
            <TabsTrigger
              value="contract"
              className="flex flex-col items-center gap-2 p-4 relative"
            >
              <div className="relative">
                <FileText className="w-5 h-5" />
                {renderTabPip("contract", "-top-1.5 -right-2")}
              </div>
              <span className="text-xs">
                {tabLabel("dashboard.tab.contract", "Contract", "Mein Vertrag")}
              </span>
            </TabsTrigger>
            <TabsTrigger
              value="payments"
              className="flex flex-col items-center gap-2 p-4 relative"
            >
              <div className="relative">
                <CreditCard className="w-5 h-5" />
                {renderTabPip("payments", "-top-1.5 -right-2")}
              </div>
              <span className="text-xs">
                {tabLabel("dashboard.tab.payments", "Payments", "Zahlungen")}
              </span>
            </TabsTrigger>
            <TabsTrigger
              value="checkin"
              className="flex flex-col items-center gap-2 p-4 relative"
            >
              <div className="relative">
                <LogIn className="w-5 h-5" />
                {renderTabPip("checkin", "-top-1.5 -right-2")}
              </div>
              <span className="text-xs">
                {tabLabel("dashboard.tab.checkin", "Check-in", "Ein-/Auszug")}
              </span>
            </TabsTrigger>
            <TabsTrigger
              value="maintenance"
              className="flex flex-col items-center gap-2 p-4 relative"
            >
              <div className="relative">
                <Headphones className="w-5 h-5" />
                {renderTabPip("maintenance", "-top-1.5 -right-2")}
              </div>
              <span className="text-xs">
                {tabLabel("dashboard.tab.support", "Support", "Hilfe")}
              </span>
            </TabsTrigger>
            <TabsTrigger
              value="community"
              className="flex flex-col items-center gap-2 p-4"
            >
              <Users className="w-5 h-5" />
              <span className="text-xs">
                {tabLabel("dashboard.tab.community", "Community", "Community")}
              </span>
            </TabsTrigger>
            <TabsTrigger
              value="perks"
              className="flex flex-col items-center gap-2 p-4"
            >
              <Gift className="w-5 h-5" />
              <span className="text-xs">
                {tabLabel("dashboard.tab.perks", "Perks", "Lokale Vorteile")}
              </span>
            </TabsTrigger>
            <TabsTrigger
              value="referrals"
              className="flex flex-col items-center gap-2 p-4 relative"
            >
              <div className="relative">
                <Share2 className="w-5 h-5" />
                {renderTabPip("referrals", "-top-1.5 -right-2")}
              </div>
              <span className="text-xs">
                {tabLabel(
                  "dashboard.tab.referrals",
                  "Referrals",
                  "Empfehlungen"
                )}
              </span>
            </TabsTrigger>
            {/* Settings used to sit here as a 10th cell; it moved to the top
                navbar's account menu, which also carries its alert pip. */}
          </TabsList>

          {/* Home Tab – ultra modern redesign */}
          <TabsContent value="home" className="space-y-5">
            {/* ── CONTRACT ACTION BANNER ──
                Only for a contract the admin has actually released. An unsent
                draft ('draft') must not prompt the tenant to sign. */}
            {booking &&
              ["envelope_draft", "ready_to_sign"].includes(
                String(booking.contract_status ?? "")
                  .trim()
                  .toLowerCase()
              ) && (
                <div
                  className="relative overflow-hidden rounded-2xl p-5 flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6"
                  style={{
                    background:
                      "linear-gradient(135deg,hsl(173,65%,38%),hsl(173,65%,52%))",
                    boxShadow: "0 4px 24px hsl(173,65%,42%,0.35)",
                  }}
                >
                  <div
                    className="absolute right-0 top-0 bottom-0 w-40 opacity-10 pointer-events-none"
                    style={{
                      background:
                        "radial-gradient(ellipse at right,#fff 0%,transparent 70%)",
                    }}
                  />
                  <div className="flex items-center gap-3.5 flex-1 min-w-0">
                    <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center shrink-0">
                      <FileText className="h-5 w-5 text-white" />
                    </div>
                    <div className="min-w-0">
                      <p className="font-semibold text-white leading-tight">
                        {homeLabels.contractActionTitle}
                      </p>
                      <p className="text-sm text-white/75 mt-0.5 truncate">
                        {homeLabels.contractActionDesc}
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setActiveTab("contract")}
                    className="shrink-0 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white text-sm font-semibold transition-opacity hover:opacity-90"
                    style={{ color: "hsl(173,65%,36%)" }}
                  >
                    {homeLabels.openMyContract}{" "}
                    <ArrowRight className="h-3.5 w-3.5" />
                  </button>
                </div>
              )}

            {/* ── ROOMS HEADER ── */}
            <div className="flex items-center justify-between gap-2">
              <div>
                <h3 className="text-xl font-bold text-foreground">
                  {homeLabels.yourRooms}
                </h3>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {homeLabels.statusFlow}
                </p>
              </div>
              {allBookings.length > 0 && (
                <span
                  className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold"
                  style={{
                    background: "hsl(173,65%,42%,0.12)",
                    color: "hsl(173,65%,42%)",
                    border: "1px solid hsl(173,65%,42%,0.25)",
                  }}
                >
                  {allBookings.length}{" "}
                  {allBookings.length === 1
                    ? homeLabels.room
                    : homeLabels.rooms}
                </span>
              )}
            </div>

            {/* ── ROOMS LIST ── */}
            {allBookings.length === 0 ? (
              <div className="flex flex-col items-center justify-center rounded-2xl border-2 border-dashed border-border py-16 text-center gap-3 bg-muted/20">
                <div className="w-14 h-14 rounded-2xl bg-muted flex items-center justify-center">
                  <HomeIcon className="h-6 w-6 text-muted-foreground" />
                </div>
                <p className="text-sm font-medium text-muted-foreground">
                  {homeLabels.noRoomsYet}
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {(() => {
                  const safePage = Math.min(
                    Math.max(1, homeRoomsPage),
                    homeTotalPages
                  );
                  const start = (safePage - 1) * HOME_PAGE_SIZE;
                  const paginatedBookings = allBookings.slice(
                    start,
                    start + HOME_PAGE_SIZE
                  );
                  return (
                    <>
                      {paginatedBookings.map((b: any) => {
                        const room = b.rooms;
                        const property = room?.properties;
                        const fullMonthlyB = getFullMonthlyRentFromBooking(b);
                        const rentDispB = getDisplayedRentEUR({
                          fullMonthlyRent: fullMonthlyB,
                          moveInIso: b.move_in_date || b.start_date,
                          firstMonthOverride:
                            b.first_month_rent_override ?? null,
                          asOfIsoDate: new Date().toISOString().slice(0, 10),
                        });
                        const ymHome = todayIsoForRent.slice(0, 7);
                        const payRentThisMonthB = (payments || []).find(
                          (p: any) =>
                            Number(p.booking_id) === Number(b.id) &&
                            p.type === "rent" &&
                            p.due_date &&
                            String(p.due_date).slice(0, 7) === ymHome
                        );
                        const billingRentB =
                          payRentThisMonthB?.amount_cents != null
                            ? Number(payRentThisMonthB.amount_cents) / 100
                            : rentDispB.amount;
                        const partialFirstB =
                          payRentThisMonthB?.amount_cents != null
                            ? Math.round(
                                (Number(payRentThisMonthB.amount_cents) / 100) *
                                  100
                              ) < Math.round(Number(fullMonthlyB || 0) * 100)
                            : rentDispB.isPartialFirstMonth;
                        const currentStageIndex = getHomeRoomStageIndex(
                          b,
                          paymentPendingList,
                          payments || [],
                          allBookings
                        );
                        const currentStageLabel =
                          homeLabels.stages[currentStageIndex];

                        // compute rent/deposit details
                        const bid = Number(b.id);
                        const contractMonthsB = (() => {
                          const startIso = b.move_in_date || b.start_date;
                          const endIso = b.move_out_date || b.end_date;
                          const s = startIso ? new Date(startIso) : null;
                          const e = endIso ? new Date(endIso) : null;
                          if (!s || !e || e < s) return null;
                          const m =
                            (e.getFullYear() - s.getFullYear()) * 12 +
                            (e.getMonth() - s.getMonth()) +
                            1;
                          return Math.max(1, m);
                        })();
                        const paidRentsB = (payments || []).filter(
                          (p: any) =>
                            Number(p.booking_id) === bid &&
                            p.type === "rent" &&
                            isPaymentEffectivelyPaid(p)
                        );
                        const paidMonthsB = paidRentsB.length;
                        const nextRentB = getNextRentForBooking(bid);
                        // COL-472: the open balance on this room's rent — a
                        // part-paid month and any Mahngebühren. Fees join the
                        // month only when it is the one the reminders list
                        // puts them on (never a month not due yet); otherwise
                        // they are listed on their own.
                        const openSettlementB = nextRentB
                          ? resolveRentSettlement(nextRentB, () => null)
                          : null;
                        const feeCentsB = accruedFeeCents(
                          b.accumulated_fees_eur
                        );
                        const nextRentCarriesFeesB =
                          !!nextRentB &&
                          feeCentsB > 0 &&
                          feeCarrierIdForBooking(bid, b.rent_due_date) ===
                            String(nextRentB.id);
                        const showOpenMonthB =
                          !!openSettlementB &&
                          (openSettlementB.isPartial || nextRentCarriesFeesB);
                        const unpaidRentPaymentsB = (payments || []).filter(
                          (p: any) =>
                            Number(p.booking_id) === bid &&
                            p.type === "rent" &&
                            !isPaymentEffectivelyPaid(p)
                        );
                        const rentRejectionB = unpaidRentPaymentsB.find(
                          (p: any) =>
                            paymentProofsByPaymentId?.[p.id]
                              ?.rejectionReasonToShow
                        );
                        // Feedback 10: RENT carries its own status, separate
                        // from the deposit. Only rent already DUE counts as
                        // owed — the schedule always holds the next two months,
                        // and counting those would leave every tenant "Open"
                        // forever.
                        const rentStatusB:
                          | (typeof RENT_STATUSES)[number]
                          | null = (() => {
                          const owed = unpaidRentPaymentsB.filter(
                            (pm: any) =>
                              String(pm.status ?? "").toLowerCase() !==
                                "canceled" &&
                              pm.due_date &&
                              String(pm.due_date).slice(0, 10) <=
                                todayIsoForRent
                          );
                          if (owed.some((pm: any) => isSepaDebitInFlight(pm)))
                            return "sepa_in_progress";
                          if (
                            owed.some(
                              (pm: any) =>
                                String(pm.due_date).slice(0, 10) <
                                todayIsoForRent
                            )
                          )
                            return "overdue";
                          if (owed.length > 0) return "open";
                          // Nothing owed today and nothing ever paid = rent
                          // has not started (future move-in). No rent status
                          // to report — the onboarding stage still applies.
                          return paidMonthsB > 0 ? "paid" : null;
                        })();
                        const rentStatusLabelB = rentStatusB
                          ? {
                              paid: p.paidStatus,
                              open: p.openStatus,
                              overdue: p.overdueStatus,
                              sepa_in_progress: p.sepaInProgressStatus,
                            }[rentStatusB]
                          : null;
                        const moveInB =
                          b.move_in_date || b.start_date
                            ? new Date(b.move_in_date || b.start_date)
                            : null;
                        if (moveInB) moveInB.setHours(0, 0, 0, 0);
                        const nowB = new Date();
                        nowB.setHours(0, 0, 0, 0);
                        // Same fallback chain as getNextPaymentDate (feedback #4):
                        // real unpaid row → cycle after the last paid month →
                        // future move-in → CURRENT month's cycle anchored on the
                        // move-in day (a past move-in with no rent row means the
                        // current month is still owed — never skip to next month
                        // and hide it) → 1st of next month as the last resort.
                        const nextPaymentDateB = (() => {
                          if (nextRentB?.due_date)
                            return new Date(nextRentB.due_date);
                          const lastPaidB = [...paidRentsB]
                            .filter((p: any) => p.due_date)
                            .sort((x: any, y: any) =>
                              String(x.due_date).localeCompare(
                                String(y.due_date)
                              )
                            )
                            .pop();
                          if (lastPaidB?.due_date)
                            return addMonthsPreserveDay(
                              new Date(lastPaidB.due_date),
                              1
                            );
                          if (moveInB && moveInB >= nowB) return moveInB;
                          if (moveInB) {
                            const cycleB = new Date(
                              nowB.getFullYear(),
                              nowB.getMonth(),
                              Math.min(
                                moveInB.getDate(),
                                new Date(
                                  nowB.getFullYear(),
                                  nowB.getMonth() + 1,
                                  0
                                ).getDate()
                              )
                            );
                            const leaseEndIsoB = b.move_out_date || b.end_date;
                            const leaseEndB = leaseEndIsoB
                              ? new Date(leaseEndIsoB)
                              : null;
                            if (
                              !leaseEndB ||
                              Number.isNaN(leaseEndB.getTime()) ||
                              cycleB <= leaseEndB
                            ) {
                              return cycleB;
                            }
                          }
                          return new Date(
                            nowB.getFullYear(),
                            nowB.getMonth() + 1,
                            1
                          );
                        })();
                        const nextPaymentLabelB =
                          nextPaymentDateB.toLocaleDateString(undefined, {
                            year: "numeric",
                            month: "long",
                            day: "numeric",
                          });
                        // An UNLINKED deposit row (no booking_id, no
                        // metadata.booking_id) still belongs to this tenancy
                        // when there is only one — that is what the Payments tab
                        // and the admin tenant profile both assume, and a
                        // stricter rule here is another way for this card to
                        // read "Pending" against an admin screen that says Paid.
                        // With several bookings on screen an unlinked row cannot
                        // be attributed to any one of them, so it counts for
                        // none rather than for all.
                        const depositRowsB = (payments || []).filter(
                          (p: any) =>
                            p.type === "deposit" &&
                            depositRowMatchesBooking(
                              p,
                              bid,
                              (allBookings || []).length === 1
                            )
                        );
                        const depositStateB = resolveDepositState({
                          depositPayments: depositRowsB,
                          isEffectivelyPaid: isPaymentEffectivelyPaid,
                          expectedDepositEUR: getExpectedDepositEUR(b),
                        });
                        const depositAmountB =
                          depositStateB.expectedCents / 100;
                        const totalDepositPaidCentsB = depositStateB.paidCents;
                        const hasPaidDepositB = depositStateB.status === "paid";
                        const depositPaymentRowB = (payments || []).find(
                          (pm: any) =>
                            Number(pm.booking_id) === bid &&
                            pm.type === "deposit"
                        );
                        const depositProofB = depositPaymentRowB?.id
                          ? paymentProofsByPaymentId?.[depositPaymentRowB.id]
                          : null;

                        return (
                          <div
                            key={b.id}
                            className="rounded-2xl border border-border/60 bg-card shadow-sm overflow-hidden"
                          >
                            {/* Card top accent */}
                            <div
                              className="h-1"
                              style={{
                                background: `linear-gradient(90deg,hsl(173,65%,42%),hsl(173,65%,58%))`,
                              }}
                            />

                            <div className="p-5 md:p-6 space-y-5">
                              {/* ── Row 1: title + stage badge + address ── */}
                              <div className="flex flex-wrap items-start justify-between gap-3">
                                <div className="flex items-center gap-2.5 min-w-0 flex-1">
                                  <div
                                    className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
                                    style={{
                                      background: "hsl(173,65%,42%,0.10)",
                                    }}
                                  >
                                    <HomeIcon
                                      className="h-4 w-4"
                                      style={{ color: "hsl(173,65%,42%)" }}
                                    />
                                  </div>
                                  <div className="min-w-0">
                                    <h4 className="font-bold text-foreground truncate leading-tight">
                                      {room?.title ??
                                        `Room ${b.room_number ?? b.id}`}
                                    </h4>
                                    {property && (
                                      <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                                        <MapPin className="h-3 w-3 shrink-0" />
                                        {formatAddress(
                                          property.address,
                                          property.zip_code,
                                          property.city
                                        ) || property.title}
                                      </p>
                                    )}
                                  </div>
                                </div>
                                {/* Feedback 10: once onboarding is done (the
                                    old stage-5 "Paid"), the headline badge
                                    tracks RENT — the recurring obligation —
                                    instead of "any payment on this booking was
                                    paid", which was really the deposit. */}
                                {currentStageIndex >= 5 && rentStatusB ? (
                                  <Badge
                                    className={`shrink-0 rounded-full border ${RENT_STATUS_BADGE_CLASSES[rentStatusB]}`}
                                  >
                                    {p.rent} · {rentStatusLabelB}
                                  </Badge>
                                ) : (
                                  <Badge
                                    className={`shrink-0 rounded-full border ${HOME_STAGE_BADGE_CLASSES[currentStageIndex] ?? "bg-muted text-muted-foreground border-border"}`}
                                  >
                                    {currentStageLabel}
                                  </Badge>
                                )}
                              </div>

                              {/* ── Row 2: dates ── */}
                              <div className="flex flex-wrap gap-2">
                                {(b.move_in_date || b.start_date) && (
                                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium text-primary bg-primary/10 border border-primary/20">
                                    <Calendar className="h-3 w-3 shrink-0" />
                                    {homeLabels.moveIn}{" "}
                                    {new Date(
                                      b.move_in_date || b.start_date
                                    ).toLocaleDateString(
                                      language === "de" ? "de-DE" : undefined,
                                      {
                                        month: "short",
                                        day: "numeric",
                                        year: "numeric",
                                      }
                                    )}
                                  </span>
                                )}
                                {(b.move_out_date || b.end_date) && (
                                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium text-violet-700 bg-violet-500/10 border border-violet-500/20">
                                    <Calendar className="h-3 w-3 shrink-0" />
                                    {homeLabels.moveOut}{" "}
                                    {new Date(
                                      b.move_out_date || b.end_date
                                    ).toLocaleDateString(
                                      language === "de" ? "de-DE" : undefined,
                                      {
                                        month: "short",
                                        day: "numeric",
                                        year: "numeric",
                                      }
                                    )}
                                  </span>
                                )}
                              </div>

                              {/* ── Row 3: date change note ── */}
                              {b.status !== "cancelled" && (
                                <div className="rounded-xl bg-muted/40 px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3">
                                  <p className="text-xs text-muted-foreground leading-relaxed flex-1">
                                    {isBookingContractSigned(b)
                                      ? homeLabels.dateLockedNote
                                      : homeLabels.dateChangeNote}
                                  </p>
                                  {!isBookingContractSigned(b) && (
                                    <Button
                                      type="button"
                                      variant="outline"
                                      size="sm"
                                      className="shrink-0 h-8 rounded-full text-xs border-primary/30 hover:bg-primary/[0.06] hover:border-primary/45"
                                      onClick={() => {
                                        setStayDatesBookingId(Number(b.id));
                                        setStayDatesRoomTitle(
                                          (b.rooms?.title as
                                            | string
                                            | null
                                            | undefined) ??
                                            (b.room_number
                                              ? `Room ${b.room_number}`
                                              : `Booking #${b.id}`)
                                        );
                                        const mi =
                                          b.move_in_date || b.start_date;
                                        const mo =
                                          b.move_out_date || b.end_date;
                                        setStayDatesMoveIn(
                                          mi ? String(mi).slice(0, 10) : ""
                                        );
                                        setStayDatesMoveOut(
                                          mo ? String(mo).slice(0, 10) : ""
                                        );
                                        setStayDatesOpen(true);
                                      }}
                                    >
                                      <Calendar className="h-3 w-3 mr-1.5 opacity-70" />
                                      {homeLabels.editDates}
                                    </Button>
                                  )}
                                </div>
                              )}

                              {/* ── Row 4: Rent + Deposit cards ── */}
                              <div className="grid sm:grid-cols-2 gap-4">
                                {/* RENT */}
                                <div className="rounded-xl border border-border/60 p-4 space-y-3 bg-background">
                                  <div className="flex items-center justify-between gap-2">
                                    <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                                      {p.rent}
                                    </span>
                                    <div className="flex items-center gap-2">
                                      {/* Feedback 10: rent's own status, so it
                                          can't be read off the deposit's. */}
                                      {rentStatusB && (
                                        <Badge
                                          className={`shrink-0 rounded-full border text-[10px] ${RENT_STATUS_BADGE_CLASSES[rentStatusB]}`}
                                        >
                                          {rentStatusLabelB}
                                        </Badge>
                                      )}
                                      <div
                                        className="w-7 h-7 rounded-lg flex items-center justify-center"
                                        style={{
                                          background: "hsl(173,65%,42%,0.10)",
                                        }}
                                      >
                                        <Euro
                                          className="h-3.5 w-3.5"
                                          style={{ color: "hsl(173,65%,42%)" }}
                                        />
                                      </div>
                                    </div>
                                  </div>
                                  <div>
                                    <p className="text-3xl font-black tabular-nums text-foreground leading-none">
                                      €
                                      {Number(
                                        (fullMonthlyB > 0
                                          ? fullMonthlyB
                                          : billingRentB) || 0
                                      ).toLocaleString("de-DE", {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2,
                                      })}
                                    </p>
                                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium mt-1">
                                      {p.monthlyRentUpper}
                                    </p>
                                    {partialFirstB && fullMonthlyB > 0 && (
                                      <p className="text-xs text-muted-foreground mt-1">
                                        {p.proRataFirstMonthNote(
                                          `€${Number(fullMonthlyB).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                                        )}
                                      </p>
                                    )}
                                  </div>
                                  {/* progress */}
                                  <div className="space-y-2 pt-2 border-t border-border/40">
                                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                                      {p.monthsOfRentPaid}
                                    </p>
                                    {contractMonthsB != null ? (
                                      <>
                                        <Progress
                                          value={
                                            contractMonthsB > 0
                                              ? Math.round(
                                                  (paidMonthsB /
                                                    contractMonthsB) *
                                                    100
                                                )
                                              : 0
                                          }
                                          className="h-1.5 bg-muted [&>div]:bg-emerald-500 rounded-full"
                                        />
                                        <p className="text-xs text-muted-foreground">
                                          <span className="font-bold text-foreground">
                                            {paidMonthsB}
                                          </span>{" "}
                                          / {contractMonthsB} {p.months}
                                        </p>
                                      </>
                                    ) : (
                                      <p className="text-xs text-foreground font-medium">
                                        {paidMonthsB} {p.paid}
                                      </p>
                                    )}
                                  </div>
                                  <RentOpenBalance
                                    monthLabel={
                                      showOpenMonthB && nextRentB?.due_date
                                        ? paidPeriodMonthLabel(
                                            String(nextRentB.due_date),
                                            language
                                          )
                                        : null
                                    }
                                    expectedCents={
                                      openSettlementB?.expectedCents ?? 0
                                    }
                                    receivedCents={
                                      openSettlementB?.receivedCents ?? 0
                                    }
                                    remainingCents={
                                      openSettlementB?.remainingCents ?? 0
                                    }
                                    isPartial={!!openSettlementB?.isPartial}
                                    feeCents={feeCentsB}
                                    feesOnMonth={nextRentCarriesFeesB}
                                    labels={{
                                      partiallyPaid: p.partiallyPaidStatus,
                                      openStatus: p.openStatus,
                                      paidOf: p.paidOfBilled,
                                      openRent: p.openRentLabel,
                                      dunningFees: p.dunningFeesLabel,
                                      totalOpen: p.totalOpenLabel,
                                      outstandingFees: p.outstandingFeesLabel,
                                    }}
                                  />
                                  {/* next payment */}
                                  <div className="pt-2 border-t border-border/40">
                                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium mb-1.5">
                                      {p.nextPayment}
                                    </p>
                                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold text-primary bg-primary/10 border border-primary/20">
                                      <Calendar className="h-3 w-3 shrink-0" />{" "}
                                      {nextPaymentLabelB}
                                    </span>
                                  </div>
                                  {rentRejectionB?.id &&
                                    paymentProofsByPaymentId?.[
                                      rentRejectionB.id
                                    ]?.rejectionReasonToShow && (
                                      <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-xs text-red-800">
                                        <p className="font-semibold">
                                          {p.rejectionReason}
                                        </p>
                                        <p className="mt-1">
                                          {
                                            paymentProofsByPaymentId[
                                              rentRejectionB.id
                                            ].rejectionReasonToShow
                                          }
                                        </p>
                                      </div>
                                    )}
                                </div>

                                {/* DEPOSIT */}
                                <div className="rounded-xl border border-border/60 p-4 space-y-3 bg-background">
                                  <div className="flex items-center justify-between">
                                    <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                                      {p.deposit}
                                    </span>
                                    <div
                                      className="w-7 h-7 rounded-lg flex items-center justify-center"
                                      style={{
                                        background: "hsl(173,65%,42%,0.10)",
                                      }}
                                    >
                                      <Euro
                                        className="h-3.5 w-3.5"
                                        style={{ color: "hsl(173,65%,42%)" }}
                                      />
                                    </div>
                                  </div>
                                  <div>
                                    <p className="text-3xl font-black tabular-nums text-foreground leading-none">
                                      €
                                      {Number(
                                        depositAmountB || 0
                                      ).toLocaleString("de-DE", {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2,
                                      })}
                                    </p>
                                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium mt-1">
                                      {p.securityDeposit}
                                    </p>
                                  </div>
                                  <div className="pt-2 border-t border-border/40">
                                    {hasPaidDepositB ? (
                                      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                                        ✓ {p.paidStatus}
                                      </span>
                                    ) : (
                                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200">
                                        {p.pendingStatus}
                                      </span>
                                    )}
                                    <p className="text-xs text-muted-foreground mt-2">
                                      {totalDepositPaidCentsB > 0
                                        ? `€${(totalDepositPaidCentsB / 100).toLocaleString("de-DE", { minimumFractionDigits: 2 })} ${p.paid}`
                                        : p.noDepositRecorded}
                                    </p>
                                  </div>
                                  {depositProofB?.rejectionReasonToShow && (
                                    <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-xs text-red-800">
                                      <p className="font-semibold">
                                        {p.rejectionReason}
                                      </p>
                                      <p className="mt-1">
                                        {depositProofB.rejectionReasonToShow}
                                      </p>
                                    </div>
                                  )}
                                </div>
                              </div>

                              {/* ── Row 5: Status stepper ── */}
                              <div className="pt-1 border-t border-border/50">
                                <div className="flex items-center gap-1.5 overflow-x-auto pb-1">
                                  {homeLabels.stages.map((label, idx) => (
                                    <span
                                      key={`${idx}-${label}`}
                                      className="inline-flex items-center gap-1 shrink-0"
                                    >
                                      <span
                                        className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-medium border ${
                                          idx < currentStageIndex
                                            ? "bg-muted/60 text-muted-foreground line-through border-border"
                                            : idx === currentStageIndex
                                              ? `${HOME_STAGE_BADGE_CLASSES[idx] ?? "bg-emerald-50 text-emerald-700 border-emerald-200"}`
                                              : "bg-muted/30 text-muted-foreground/60 border-border/50"
                                        }`}
                                      >
                                        {idx < currentStageIndex && (
                                          <span className="mr-0.5">✓</span>
                                        )}
                                        {label}
                                      </span>
                                      {idx < homeLabels.stages.length - 1 && (
                                        <span className="text-muted-foreground/30 text-xs shrink-0">
                                          →
                                        </span>
                                      )}
                                    </span>
                                  ))}
                                </div>
                                {/* Pay rent CTA */}
                                {currentStageIndex === 4 && (
                                  <div className="mt-3">
                                    <Button
                                      size="sm"
                                      className="rounded-full h-9 px-5 text-sm"
                                      disabled={
                                        processingPaymentBookingId === b.id
                                      }
                                      onClick={async () => {
                                        try {
                                          setProcessingPaymentBookingId(b.id);
                                          const candidate =
                                            (payments || [])
                                              .filter(
                                                (pm: any) =>
                                                  Number(pm.booking_id) ===
                                                    Number(b.id) &&
                                                  pm.type === "rent" &&
                                                  pm.due_date
                                              )
                                              .filter(
                                                (pm: any) =>
                                                  !isPaymentEffectivelyPaid(pm)
                                              )
                                              .sort(
                                                (a: any, b: any) =>
                                                  new Date(
                                                    a.due_date
                                                  ).getTime() -
                                                  new Date(b.due_date).getTime()
                                              )[0] || null;
                                          const amountCents =
                                            candidate?.amount_cents != null
                                              ? // COL-417: charge what's actually still open, not
                                                // the full month, once a receipt has landed.
                                                remainingRentCents(candidate)
                                              : Math.round(
                                                  Number(billingRentB) * 100
                                                );
                                          if (!candidate?.id) {
                                            toast.error(
                                              language === "de"
                                                ? "Keine offene Mietzahlung gefunden. Bitte aktualisieren."
                                                : "No open rent payment found. Please refresh."
                                            );
                                            return;
                                          }
                                          if (
                                            !amountCents ||
                                            amountCents <= 0
                                          ) {
                                            toast.error(
                                              t(
                                                "dashboard.invalidRentAmount",
                                                "Invalid rent amount."
                                              )
                                            );
                                            return;
                                          }
                                          const feeOk =
                                            await confirmCardFee(amountCents);
                                          if (!feeOk) return;
                                          const { data, error } =
                                            await supabase.functions.invoke(
                                              "create-card-checkout",
                                              {
                                                body: {
                                                  bookingId: b.id,
                                                  paymentId: candidate.id,
                                                  dueDate: candidate.due_date,
                                                  amountCents,
                                                  currency: "eur",
                                                  type: "rent",
                                                  description:
                                                    "Monthly rent payment",
                                                  successUrl: `${window.location.origin}/dashboard?payment=success`,
                                                  cancelUrl: `${window.location.origin}/dashboard?payment=canceled`,
                                                },
                                              }
                                            );
                                          if (error) {
                                            toast.error(
                                              error.message ||
                                                t(
                                                  "dashboard.failedToStartPayment",
                                                  "Failed to start payment."
                                                )
                                            );
                                            return;
                                          }
                                          const checkoutUrl = (data as any)
                                            ?.checkoutUrl;
                                          if (!checkoutUrl) {
                                            toast.error(
                                              t(
                                                "dashboard.unableToStartCheckout",
                                                "Unable to start Stripe Checkout session."
                                              )
                                            );
                                            return;
                                          }
                                          window.location.href = checkoutUrl;
                                        } finally {
                                          setProcessingPaymentBookingId(null);
                                        }
                                      }}
                                    >
                                      {processingPaymentBookingId === b.id
                                        ? t(
                                            "dashboard.redirecting",
                                            "Redirecting…"
                                          )
                                        : t("dashboard.payRent", "Pay Rent")}
                                    </Button>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}

                      {/* pagination */}
                      {homeTotalPages > 1 && (
                        <div className="flex items-center justify-between gap-4 pt-2">
                          <p className="text-sm text-muted-foreground">
                            {start + 1}–
                            {Math.min(
                              start + HOME_PAGE_SIZE,
                              allBookings.length
                            )}{" "}
                            {t("dashboard.ofCount", "of")} {allBookings.length}
                          </p>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              className="rounded-full"
                              onClick={() =>
                                setHomeRoomsPage((p) => Math.max(1, p - 1))
                              }
                              disabled={safePage <= 1}
                            >
                              <ArrowLeft className="h-4 w-4" />
                            </Button>
                            <span className="text-sm text-muted-foreground px-1">
                              {safePage} / {homeTotalPages}
                            </span>
                            <Button
                              variant="outline"
                              size="sm"
                              className="rounded-full"
                              onClick={() =>
                                setHomeRoomsPage((p) =>
                                  Math.min(homeTotalPages, p + 1)
                                )
                              }
                              disabled={safePage >= homeTotalPages}
                            >
                              <ArrowRight className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            )}
          </TabsContent>

          {/* Contract Tab */}
          <TabsContent value="contract">
            {booking ? (
              <>
                <Card className="p-8 sm:p-10 rounded-2xl border-0 shadow-sm bg-gradient-to-b from-background to-muted/20">
                  <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-6 mb-8">
                    <div>
                      <h2 className="text-2xl font-bold text-foreground">
                        {language === "de" ? "Mein Vertrag" : "My Contract"}
                      </h2>
                      {(booking.contract_version != null ||
                        booking.contract_pdf_updated_at) && (
                        <p className="text-xs text-muted-foreground mt-1.5">
                          {language === "de" ? "PDF-Version" : "PDF version"}{" "}
                          {booking.contract_version ?? 1}
                          {booking.contract_pdf_updated_at
                            ? ` · ${language === "de" ? "Stand" : "Updated"} ${new Date(booking.contract_pdf_updated_at).toLocaleDateString(language === "de" ? "de-DE" : undefined, { day: "numeric", month: "short", year: "numeric" })}`
                            : null}
                        </p>
                      )}
                    </div>
                    <Badge
                      variant="secondary"
                      className={
                        "shrink-0 font-medium px-3 py-1.5 rounded-full transition-colors " +
                        (normalizedContractStatus === "approved" ||
                        normalizedContractStatus === "signed"
                          ? "bg-emerald-500/10 text-emerald-700 border-emerald-500/20"
                          : normalizedContractStatus === "ready_to_sign" ||
                              normalizedContractStatus === "envelope_draft" ||
                              normalizedContractStatus === "draft"
                            ? "bg-blue-500/10 text-blue-700 border-blue-500/20"
                            : normalizedContractStatus === "awaiting_approval"
                              ? "bg-amber-500/10 text-amber-700 border-amber-500/20"
                              : "bg-muted/80 text-muted-foreground border-border")
                      }
                    >
                      {contractStatusLabel}
                    </Badge>
                  </div>

                  <div className="grid gap-6 lg:gap-8 lg:grid-cols-[2fr,1fr]">
                    <div className="rounded-xl border border-border/80 bg-card p-6 sm:p-7 shadow-sm space-y-5">
                      <div className="flex items-center justify-between pb-4 border-b border-border/60">
                        <h3 className="font-semibold text-foreground">
                          {language === "de"
                            ? "Vertragsdetails"
                            : "Contract details"}
                        </h3>
                        {booking.contract_signed_at && (
                          <span className="text-xs text-muted-foreground">
                            {language === "de" ? "Unterzeichnet" : "Signed"}{" "}
                            {formatDateNumeric(
                              booking.contract_signed_at,
                              language
                            )}
                          </span>
                        )}
                      </div>
                      <div className="divide-y divide-border/60">
                        {(booking.move_in_date || booking.start_date) && (
                          <div className="flex items-center justify-between gap-3 py-3.5 first:pt-0">
                            <span className="text-sm text-muted-foreground shrink-0">
                              {language === "de" ? "Einzug" : "Move-in"}
                            </span>
                            <span className="text-sm font-semibold text-primary bg-primary/10 border border-primary/20 rounded-lg px-3 py-1.5 tabular-nums">
                              {formatDateNumeric(
                                booking.move_in_date || booking.start_date,
                                language
                              )}
                            </span>
                          </div>
                        )}
                        {(booking.move_out_date || booking.end_date) && (
                          <div className="flex items-center justify-between gap-3 py-3.5">
                            <span className="text-sm text-muted-foreground shrink-0">
                              {language === "de" ? "Auszug" : "Move-out"}
                            </span>
                            <span className="text-sm font-semibold text-violet-900 dark:text-violet-100 bg-violet-500/12 border border-violet-500/25 rounded-lg px-3 py-1.5 tabular-nums">
                              {formatDateNumeric(
                                booking.move_out_date || booking.end_date,
                                language
                              )}
                            </span>
                          </div>
                        )}
                        {displayContractMonthlyRentEUR ? (
                          <div className="space-y-1 py-3.5">
                            <div className="flex items-center justify-between">
                              <span className="text-sm text-muted-foreground">
                                {p.monthlyRent}
                              </span>
                              <span className="text-sm font-medium text-foreground">
                                €
                                {Number(
                                  displayContractMonthlyRentEUR
                                ).toLocaleString("de-DE", {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })}
                              </span>
                            </div>
                            {isPartialFirstMonthRent && fullMonthlyRent > 0 && (
                              <p className="text-xs text-muted-foreground">
                                {p.proRataFirstMonthNote(
                                  `€${Number(fullMonthlyRent).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                                )}
                              </p>
                            )}
                          </div>
                        ) : null}
                        {depositAmount && (
                          <div className="flex items-center justify-between py-3.5 last:pb-0">
                            <span className="text-sm text-muted-foreground">
                              {p.deposit}
                            </span>
                            <span className="text-sm font-medium text-foreground">
                              €
                              {Number(depositAmount).toLocaleString("de-DE", {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="rounded-xl border border-border/80 bg-card p-6 sm:p-7 shadow-sm space-y-5">
                      <div className="flex items-center justify-between pb-4 border-b border-border/60">
                        <h3 className="font-semibold text-foreground">
                          {language === "de" ? "Nächster Schritt" : "Next step"}
                        </h3>
                      </div>

                      {docuSignSessionExpiredNotice && (
                        <div
                          role="status"
                          className="rounded-xl border border-amber-500/35 bg-amber-500/[0.12] px-4 py-3.5 text-sm text-amber-950/95 shadow-sm"
                        >
                          <p className="font-medium text-amber-950 mb-1">
                            {language === "de"
                              ? "Sitzung abgelaufen"
                              : "Signing session expired"}
                          </p>
                          <p className="text-amber-950/90 leading-relaxed">
                            {language === "de" ? (
                              <>
                                Deine Unterzeichnungssitzung ist abgelaufen.
                                Bitte kontaktiere einen Admin (z. B.{" "}
                                <a
                                  href="mailto:kontakt@coliviq.de"
                                  className="font-semibold text-amber-900 underline underline-offset-2"
                                >
                                  kontakt@coliviq.de
                                </a>
                                ). Sobald der Vertrag neu erstellt wurde, tippe
                                unten auf{" "}
                                <span className="font-semibold">
                                  &bdquo;In DocuSign öffnen&ldquo;
                                </span>{" "}
                                &mdash; DocuSign startet dann eine neue
                                Unterzeichnungssitzung.
                              </>
                            ) : (
                              <>
                                Your signing session expired. Please contact an
                                admin (e.g.{" "}
                                <a
                                  href="mailto:kontakt@coliviq.de"
                                  className="font-semibold text-amber-900 underline underline-offset-2"
                                >
                                  kontakt@coliviq.de
                                </a>
                                ). Once they regenerate the contract, tap{" "}
                                <span className="font-semibold">
                                  &ldquo;Open in DocuSign&rdquo;
                                </span>{" "}
                                below &mdash; DocuSign will open a new signing
                                session.
                              </>
                            )}
                          </p>
                        </div>
                      )}

                      {isContractNotReleasedToTenant(
                        normalizedContractStatus
                      ) && (
                        <p className="text-sm text-muted-foreground leading-relaxed">
                          {language === "de"
                            ? "Ihr Vertrag wird vorbereitet. Sie erhalten einen Unterzeichnungslink, sobald unser Team ihn geprüft und freigegeben hat."
                            : "Your contract is being prepared. You will receive a signing link once our team has reviewed and released it."}
                        </p>
                      )}

                      {isContractSigningStage && (
                        <div className="space-y-3">
                          {booking.docusign_envelope_id ? (
                            <>
                              <Button
                                className={cn(
                                  "w-full bg-emerald-600 hover:bg-emerald-700",
                                  // Halo while the Contract tab's pip points
                                  // here, so the pip leads somewhere visible
                                  // (COL-368) — same treatment the protocol
                                  // card's primary button already carries.
                                  isContractSigningStage &&
                                    "animate-cta-pulse motion-reduce:animate-none"
                                )}
                                disabled={openingDocuSign}
                                onClick={() => {
                                  pendingDocuSignActionRef.current =
                                    async () => {
                                      setOpeningDocuSign(true);
                                      try {
                                        const data =
                                          await invokeGetDocusignSigningUrl(
                                            Number(booking.id)
                                          );
                                        const url =
                                          typeof data?.url === "string" &&
                                          data.url.startsWith("http")
                                            ? data.url
                                            : null;
                                        if (url) {
                                          openDocuSignUrl(url);
                                          queryClient.invalidateQueries({
                                            queryKey: [
                                              "active-booking",
                                              user?.id,
                                            ],
                                          });
                                        } else {
                                          toast.error(
                                            (data?.error as
                                              | string
                                              | undefined) ||
                                              t(
                                                "dashboard.couldNotOpenDocuSign",
                                                "Could not open DocuSign"
                                              )
                                          );
                                        }
                                      } catch (e: any) {
                                        const msg = [e?.message, e?.details]
                                          .filter(Boolean)
                                          .join(" — ");
                                        toast.error(
                                          msg ||
                                            t(
                                              "dashboard.failedToOpenDocuSign",
                                              "Failed to open DocuSign"
                                            )
                                        );
                                      } finally {
                                        setOpeningDocuSign(false);
                                      }
                                    };
                                  clearDocuSignTtlState();
                                  openDobGate();
                                }}
                              >
                                {openingDocuSign ? (
                                  <Loader2 className="mr-2 w-4 h-4 animate-spin" />
                                ) : (
                                  <FileText className="mr-2 w-4 h-4" />
                                )}
                                {openingDocuSign
                                  ? language === "de"
                                    ? "DocuSign wird geoeffnet..."
                                    : "Opening DocuSign..."
                                  : language === "de"
                                    ? "In DocuSign oeffnen"
                                    : "Open in DocuSign"}
                              </Button>
                            </>
                          ) : (
                            <>
                              {(signingUrlJustSent?.bookingId ===
                                String(booking.id) ||
                                justSentToDocuSignBookingId ===
                                  String(booking.id)) && (
                                <Button
                                  className="w-full bg-emerald-600 hover:bg-emerald-700"
                                  disabled={openingDocuSign}
                                  onClick={() => {
                                    pendingDocuSignActionRef.current =
                                      async () => {
                                        const savedUrl =
                                          signingUrlJustSent?.bookingId ===
                                          String(booking.id)
                                            ? signingUrlJustSent?.url
                                            : null;
                                        if (
                                          savedUrl &&
                                          typeof savedUrl === "string" &&
                                          savedUrl.startsWith("http")
                                        ) {
                                          openDocuSignUrl(savedUrl);
                                          return;
                                        }
                                        setOpeningDocuSign(true);
                                        try {
                                          const bookingIdNum = Number(
                                            booking.id
                                          );
                                          if (Number.isNaN(bookingIdNum)) {
                                            toast.error(
                                              t(
                                                "dashboard.invalidBooking",
                                                "Invalid booking."
                                              )
                                            );
                                            return;
                                          }
                                          const data =
                                            await invokeGetDocusignSigningUrl(
                                              bookingIdNum
                                            );
                                          const url =
                                            typeof data?.url === "string" &&
                                            data.url.startsWith("http")
                                              ? data.url
                                              : null;
                                          if (url) {
                                            openDocuSignUrl(url);
                                            if (!isMobile)
                                              setSigningUrlJustSent({
                                                bookingId: String(booking.id),
                                                url,
                                              });
                                          } else {
                                            toast.error(
                                              (data?.error as
                                                | string
                                                | undefined) ||
                                                t(
                                                  "dashboard.couldNotGetSigningLink",
                                                  "Could not get signing link. Please try again."
                                                )
                                            );
                                          }
                                        } catch (e: any) {
                                          const msg = [e?.message, e?.details]
                                            .filter(Boolean)
                                            .join(" — ");
                                          toast.error(
                                            msg ||
                                              t(
                                                "dashboard.failedToOpenDocuSign",
                                                "Failed to open DocuSign"
                                              )
                                          );
                                        } finally {
                                          setOpeningDocuSign(false);
                                        }
                                      };
                                    clearDocuSignTtlState();
                                    openDobGate();
                                  }}
                                >
                                  {openingDocuSign ? (
                                    <Loader2 className="mr-2 w-4 h-4 animate-spin" />
                                  ) : (
                                    <FileText className="mr-2 w-4 h-4" />
                                  )}
                                  {openingDocuSign
                                    ? language === "de"
                                      ? "DocuSign wird geoeffnet..."
                                      : "Opening DocuSign..."
                                    : language === "de"
                                      ? "In DocuSign oeffnen"
                                      : "Open in DocuSign"}
                                </Button>
                              )}
                              <Button
                                className="w-full"
                                disabled={sendingToDocuSign}
                                onClick={() => {
                                  pendingDocuSignActionRef.current =
                                    async () => {
                                      setSendingToDocuSign(true);
                                      try {
                                        const bookingIdNum = Number(booking.id);
                                        if (!Number.isFinite(bookingIdNum)) {
                                          toast.error(
                                            t(
                                              "dashboard.invalidBooking",
                                              "Invalid booking."
                                            )
                                          );
                                          return;
                                        }

                                        // Ensure we have a PDF draft first.
                                        if (!booking.contract_pdf_url) {
                                          const created =
                                            (await invokeCreateContract(
                                              bookingIdNum
                                            )) as Record<string, unknown>;
                                          if (
                                            created?.contractReadyEmailSent !==
                                            true
                                          ) {
                                            const skipped =
                                              created?.contractReadyEmailSkipped;
                                            const err =
                                              created?.contractReadyEmailError;
                                            if (
                                              skipped != null ||
                                              err != null
                                            ) {
                                              toast.warning(
                                                language === "de"
                                                  ? "Die E-Mail „Vertrag bereit“ konnte nicht gesendet werden."
                                                  : "We could not send the contract notification email.",
                                                {
                                                  description: [
                                                    skipped != null &&
                                                    skipped !== ""
                                                      ? `${t("dashboard.reasonPrefix", "Reason:")} ${String(skipped)}`
                                                      : null,
                                                    err != null && err !== ""
                                                      ? String(err)
                                                      : null,
                                                  ]
                                                    .filter(Boolean)
                                                    .join(" "),
                                                  duration: 12000,
                                                }
                                              );
                                            }
                                          }
                                        }

                                        // Create/open the DocuSign envelope (this is when envelope_draft begins).
                                        const sent =
                                          await invokeSendContractToDocusign(
                                            bookingIdNum
                                          );
                                        queryClient.invalidateQueries({
                                          queryKey: [
                                            "active-booking",
                                            user?.id,
                                          ],
                                        });
                                        const u = sent?.url;
                                        const url =
                                          typeof u === "string" &&
                                          u.startsWith("http")
                                            ? u
                                            : null;
                                        if (url) {
                                          openDocuSignUrl(url);
                                          if (!isMobile)
                                            setSigningUrlJustSent({
                                              bookingId: String(booking.id),
                                              url,
                                            });
                                        } else {
                                          toast.error(
                                            language === "de"
                                              ? "DocuSign konnte nicht geöffnet werden. Bitte erneut versuchen."
                                              : "Could not open DocuSign. Please try again."
                                          );
                                        }
                                      } catch (e: any) {
                                        const msg = [e?.message, e?.details]
                                          .filter(Boolean)
                                          .join(" — ");
                                        toast.error(
                                          msg ||
                                            (language === "de"
                                              ? "DocuSign konnte nicht geoeffnet werden."
                                              : "Failed to open DocuSign")
                                        );
                                      } finally {
                                        setSendingToDocuSign(false);
                                      }
                                    };
                                  clearDocuSignTtlState();
                                  openDobGate();
                                }}
                              >
                                {sendingToDocuSign ? (
                                  <Loader2 className="mr-2 w-4 h-4 animate-spin" />
                                ) : (
                                  <FileText className="mr-2 w-4 h-4" />
                                )}
                                {sendingToDocuSign
                                  ? language === "de"
                                    ? "DocuSign wird vorbereitet..."
                                    : "Preparing DocuSign..."
                                  : language === "de"
                                    ? "In DocuSign oeffnen"
                                    : "Open in DocuSign"}
                              </Button>
                            </>
                          )}

                          <div className="rounded-xl border border-border/70 bg-gradient-to-b from-muted/40 to-card p-4 sm:p-5 space-y-4">
                            <p className="text-sm font-semibold text-foreground">
                              {language === "de"
                                ? "So geht’s weiter"
                                : "What to expect"}
                            </p>
                            <ol className="space-y-2.5 text-sm text-muted-foreground leading-relaxed list-none">
                              <li className="flex gap-3">
                                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-emerald-600/15 text-xs font-bold text-emerald-800">
                                  1
                                </span>
                                <span>
                                  {language === "de"
                                    ? "Öffne DocuSign, prüfe alle Details, trage den Ort in das Eingabefeld ein und unterschreibe den Vertrag."
                                    : "Open DocuSign, check that all details are correct, enter your location in the input box, and sign the contract."}
                                </span>
                              </li>
                              <li className="flex gap-3">
                                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-emerald-600/15 text-xs font-bold text-emerald-800">
                                  2
                                </span>
                                <span>
                                  {language === "de"
                                    ? "Prüfe Name, Miete und Laufzeit, unterschreibe digital und schließe den Vorgang in DocuSign ab."
                                    : "Review your details, sign electronically, and finish in DocuSign."}
                                </span>
                              </li>
                              <li className="flex gap-3">
                                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-emerald-600/15 text-xs font-bold text-emerald-800">
                                  3
                                </span>
                                <span>
                                  {language === "de"
                                    ? "Danach kehrst du hierher zurück. Sobald der Vertrag freigegeben ist, kannst du die Kaution auf diesem Tab bezahlen."
                                    : "Then come back here. Once your contract is approved, you can pay your security deposit on this tab."}
                                </span>
                              </li>
                            </ol>
                            <div className="flex gap-3 rounded-lg border border-amber-500/20 bg-amber-500/[0.07] px-3 py-2.5 text-xs text-amber-950/90">
                              <Clock
                                className="h-4 w-4 shrink-0 text-amber-700 mt-0.5"
                                aria-hidden
                              />
                              <p className="leading-relaxed">
                                {language === "de"
                                  ? "Unterzeichnungslinks sind nur kurz gültig. Wenn die Sitzung abgelaufen ist, kontaktiere einen Admin. Nach Vertrags-Neuerstellung kannst du erneut „In DocuSign öffnen“ für eine neue Sitzung."
                                  : "Signing links expire after a short time. If your session expired, contact an admin. After they regenerate the contract, tap Open in DocuSign again for a new signing session."}
                              </p>
                            </div>
                          </div>
                        </div>
                      )}

                      {normalizedContractStatus === "awaiting_approval" && (
                        <div className="rounded-xl bg-amber-500/5 border border-amber-500/20 p-4 sm:p-5">
                          <div className="flex items-start gap-3">
                            <div className="rounded-full bg-amber-500/10 p-2 shrink-0">
                              <AlertCircle className="w-5 h-5 text-amber-600" />
                            </div>
                            <div>
                              <p className="font-semibold text-amber-900 mb-0.5">
                                {t(
                                  "dashboard.awaitingAdminApproval",
                                  "Awaiting Admin Approval"
                                )}
                              </p>
                              <p className="text-sm text-amber-700/90 leading-relaxed">
                                {t(
                                  "dashboard.contractSignedAwaitingApproval",
                                  "Your contract has been signed and is now awaiting approval from coliviq."
                                )}
                              </p>
                            </div>
                          </div>
                        </div>
                      )}

                      {(normalizedContractStatus === "approved" ||
                        normalizedContractStatus === "signed") && (
                        <>
                          <div className="rounded-xl bg-emerald-500/5 border border-emerald-500/20 p-4 sm:p-5">
                            <div className="flex items-start gap-3">
                              <div className="rounded-full bg-emerald-500/10 p-2 shrink-0">
                                <ShieldCheck className="w-5 h-5 text-emerald-600" />
                              </div>
                              <div>
                                <p className="text-sm text-emerald-700/90 leading-relaxed">
                                  {language === "de" ? (
                                    <>
                                      Dein Vertrag ist vollständig
                                      unterschrieben. Gehe zum{" "}
                                      <button
                                        type="button"
                                        onClick={() => setActiveTab("payments")}
                                        className="font-medium underline underline-offset-2 hover:text-emerald-900"
                                      >
                                        Zahlungsbereich
                                      </button>
                                      , um ausstehende Zahlungen zu sehen.
                                    </>
                                  ) : (
                                    <>
                                      Your contract is fully signed. Continue to
                                      the{" "}
                                      <button
                                        type="button"
                                        onClick={() => setActiveTab("payments")}
                                        className="font-medium underline underline-offset-2 hover:text-emerald-900"
                                      >
                                        Payments
                                      </button>{" "}
                                      area to view pending payments.
                                    </>
                                  )}
                                </p>
                              </div>
                            </div>
                          </div>
                          {!hasPaidDeposit && depositAmount ? (
                            <div className="rounded-xl bg-amber-500/5 border border-amber-500/20 p-4 sm:p-5">
                              <div className="flex items-start gap-3">
                                <div className="rounded-full bg-amber-500/10 p-2 shrink-0">
                                  <AlertCircle className="w-5 h-5 text-amber-600" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="font-semibold text-amber-900 mb-0.5">
                                    {t(
                                      "dashboard.securityDepositDue",
                                      "Security deposit due"
                                    )}
                                  </p>
                                  <p className="text-sm text-amber-700/90 mb-3 leading-relaxed">
                                    {t(
                                      "dashboard.securityDepositAmountDue",
                                      "Your security deposit of"
                                    )}{" "}
                                    €
                                    {Number(depositAmount).toLocaleString(
                                      language === "de" ? "de-DE" : "en-GB"
                                    )}{" "}
                                    {t("dashboard.isDueNow", "is due now.")}
                                  </p>
                                  <Button
                                    className={cn(
                                      "w-full",
                                      // Halo while the Contract tab's pip
                                      // points at the unpaid deposit
                                      // (COL-368).
                                      hasContractDepositDueNotification &&
                                        "animate-cta-pulse motion-reduce:animate-none"
                                    )}
                                    disabled={isProcessingDepositPayment}
                                    onClick={async () => {
                                      try {
                                        setIsProcessingDepositPayment(true);
                                        if (!user?.id) {
                                          toast.error(
                                            t(
                                              "dashboard.loginToPayDeposit",
                                              "You need to be logged in to pay your deposit."
                                            )
                                          );
                                          return;
                                        }
                                        const bookingId = booking?.id
                                          ? Number(booking.id)
                                          : null;
                                        if (!bookingId) {
                                          toast.error(
                                            t(
                                              "dashboard.missingBooking",
                                              "Missing booking."
                                            )
                                          );
                                          return;
                                        }
                                        const amountCents = Math.round(
                                          Number(depositAmount) * 100
                                        );
                                        const { data: existing } =
                                          await supabase
                                            .from("payments")
                                            .select(
                                              "id, amount_cents, due_date, type"
                                            )
                                            .eq("user_id", user.id)
                                            .eq("booking_id", bookingId)
                                            .eq("type", "deposit")
                                            .maybeSingle();
                                        let payment = existing ?? null;
                                        if (!payment) {
                                          const {
                                            data: created,
                                            error: createErr,
                                          } = await supabase
                                            .from("payments")
                                            .insert(
                                              operatorStamped<"payments">({
                                                user_id: user.id,
                                                booking_id: bookingId,
                                                type: "deposit",
                                                amount_cents: amountCents,
                                                currency: "eur",
                                                status: "pending",
                                                due_date:
                                                  financialsBooking?.move_in_date ||
                                                  financialsBooking?.start_date ||
                                                  new Date()
                                                    .toISOString()
                                                    .slice(0, 10),
                                                description: "Security deposit",
                                              })
                                            )
                                            .select()
                                            .single();
                                          if (createErr || !created) {
                                            toast.error(
                                              createErr?.message ||
                                                t(
                                                  "dashboard.couldNotCreateDepositPayment",
                                                  "Could not create deposit payment."
                                                )
                                            );
                                            return;
                                          }
                                          payment = created;
                                        }

                                        const depositCentsForFee = Number(
                                          payment.amount_cents ?? amountCents
                                        );
                                        const feeOk =
                                          await confirmCardFee(
                                            depositCentsForFee
                                          );
                                        if (!feeOk) return;
                                        const { data, error } =
                                          await supabase.functions.invoke(
                                            "create-card-checkout",
                                            {
                                              body: {
                                                bookingId,
                                                paymentId: payment.id,
                                                dueDate: payment.due_date,
                                                amountCents: depositCentsForFee,
                                                currency: "eur",
                                                type: "deposit",
                                                description: "Security deposit",
                                                successUrl: `${window.location.origin}/dashboard?tab=contract&payment=success`,
                                                cancelUrl: `${window.location.origin}/dashboard?tab=contract&payment=canceled`,
                                              },
                                            }
                                          );
                                        if (error) {
                                          toast.error(
                                            error.message ||
                                              t(
                                                "dashboard.failedToStartDepositPayment",
                                                "Failed to start deposit payment."
                                              )
                                          );
                                          return;
                                        }
                                        const checkoutUrl = (data as any)
                                          ?.checkoutUrl;
                                        if (!checkoutUrl) {
                                          toast.error(
                                            t(
                                              "dashboard.unableToStartCheckout",
                                              "Unable to start Stripe Checkout session."
                                            )
                                          );
                                          return;
                                        }
                                        window.location.href = checkoutUrl;
                                      } finally {
                                        setIsProcessingDepositPayment(false);
                                      }
                                    }}
                                  >
                                    {isProcessingDepositPayment
                                      ? t(
                                          "dashboard.redirecting",
                                          "Redirecting…"
                                        )
                                      : t(
                                          "dashboard.payDeposit",
                                          "Pay Deposit"
                                        )}
                                  </Button>
                                </div>
                              </div>
                            </div>
                          ) : null}
                          <div className="space-y-4 pt-1">
                            {booking.signed_contract_url ? (
                              <Button
                                variant="outline"
                                className="w-full"
                                // Signed on click (COL-436): the contracts
                                // bucket is going private, so the stored
                                // public URL is never opened directly.
                                onClick={async () => {
                                  if (
                                    !(await openStoredFile(
                                      booking.signed_contract_url,
                                      "contracts"
                                    ))
                                  ) {
                                    toast.error(
                                      language === "de"
                                        ? "Dokument konnte nicht geöffnet werden"
                                        : "Could not open the document"
                                    );
                                  }
                                }}
                              >
                                <FileText className="mr-2 w-4 h-4 opacity-80" />
                                {t(
                                  "dashboard.viewContractPdf",
                                  "View Contract PDF"
                                )}
                              </Button>
                            ) : (
                              <div className="rounded-lg border border-amber-500/20 bg-amber-500/5 p-3.5 text-sm text-amber-700">
                                {t(
                                  "dashboard.signedContractBeingFinalized",
                                  "Signed contract is being finalized. Please check back shortly."
                                )}
                              </div>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </Card>

                {/* DocuSign: confirm before opening signing session */}
                <Dialog
                  open={docuSignConfirmOpen}
                  onOpenChange={(open) => {
                    setDocuSignConfirmOpen(open);
                    if (!open) pendingDocuSignActionRef.current = null;
                  }}
                >
                  <DialogContent className="fixed left-[50%] top-[50%] z-50 w-[calc(100vw-2rem)] max-w-[calc(100vw-2rem)] sm:w-full sm:max-w-xl max-h-[85vh] sm:max-h-[90vh] translate-x-[-50%] translate-y-[-50%] rounded-xl sm:rounded-2xl border border-border bg-card shadow-xl p-0 gap-0 overflow-hidden flex flex-col data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] duration-300">
                    <DialogHeader className="text-left px-4 sm:px-6 md:px-8 pt-10 sm:pt-6 md:pt-8 pb-2 shrink-0">
                      <DialogTitle className="text-base sm:text-lg md:text-xl font-semibold text-foreground flex items-start gap-2 leading-tight">
                        <AlertCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5 animate-pulse" />
                        <span>
                          {language === "de"
                            ? "Bist du bereit zum Unterschreiben?"
                            : "Ready to sign?"}
                        </span>
                      </DialogTitle>
                    </DialogHeader>
                    <div className="flex-1 min-h-0 overflow-y-auto px-4 sm:px-6 md:px-8 pb-2">
                      <DialogDescription asChild>
                        <div className="text-xs sm:text-sm text-muted-foreground leading-relaxed space-y-3">
                          <p>
                            {language === "de"
                              ? "Mit diesem Button öffnest du das offizielle DocuSign-Dokument zur Unterschrift. Links sind aus Sicherheitsgründen nur kurz gültig. Ist die Sitzung abgelaufen, kontaktiere einen Admin; nach Vertrags-Neuerstellung erzeugt dieser Schritt eine neue Unterzeichnungssitzung."
                              : "This opens the official DocuSign document for signing. Links are short-lived for security. If your previous session expired, contact an admin first; after they regenerate the contract, continuing here starts a new signing session."}
                            <span className="mt-3 block rounded-lg border border-amber-500/25 bg-amber-500/[0.06] px-3 py-2.5 text-foreground space-y-1.5">
                              <span className="block font-medium">
                                {language === "de"
                                  ? "Ist die Sitzung abgelaufen? Kontaktiere einen Admin. Sobald der Vertrag neu erstellt wurde, funktioniert „In DocuSign öffnen“ wieder mit einer neuen Sitzung."
                                  : "If the session expired, contact an admin. Once they regenerate the contract, Open in DocuSign will work again with a new signing session."}
                              </span>
                            </span>
                          </p>
                        </div>
                      </DialogDescription>
                    </div>
                    <DialogFooter className="flex flex-col-reverse sm:flex-row gap-3 px-4 sm:px-6 md:px-8 pt-4 border-t border-border shrink-0 pb-[max(1rem,env(safe-area-inset-bottom))]">
                      <Button
                        variant="outline"
                        className="w-full sm:w-auto"
                        onClick={() => setDocuSignConfirmOpen(false)}
                      >
                        {language === "de" ? "Abbrechen" : "Cancel"}
                      </Button>
                      <Button
                        className="w-full sm:w-auto bg-emerald-600 hover:bg-emerald-700 text-white"
                        onClick={() => {
                          setDocuSignConfirmOpen(false);
                          const fn = pendingDocuSignActionRef.current;
                          pendingDocuSignActionRef.current = null;
                          // Let the dialog portal unmount commit first; avoids rare React insertBefore errors.
                          if (fn) setTimeout(() => fn(), 0);
                        }}
                      >
                        {language === "de"
                          ? "Ich verstehe, weiter zu DocuSign."
                          : "I understand, continue to DocuSign."}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>

                {/* DocuSign: confirm / insert date of birth before signing (COL-258) */}
                <Dialog
                  open={dobConfirmOpen}
                  onOpenChange={(open) => {
                    setDobConfirmOpen(open);
                    if (!open && !dobProceedingRef.current) {
                      pendingDocuSignActionRef.current = null;
                    }
                    dobProceedingRef.current = false;
                  }}
                >
                  <DialogContent className="fixed left-[50%] top-[50%] z-50 w-[calc(100vw-2rem)] max-w-[calc(100vw-2rem)] sm:w-full sm:max-w-lg max-h-[85vh] sm:max-h-[90vh] translate-x-[-50%] translate-y-[-50%] rounded-xl sm:rounded-2xl border border-border bg-card shadow-xl p-0 gap-0 overflow-hidden flex flex-col data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] duration-300">
                    <DialogHeader className="text-left px-4 sm:px-6 md:px-8 pt-10 sm:pt-6 md:pt-8 pb-2 shrink-0">
                      <DialogTitle className="text-base sm:text-lg md:text-xl font-semibold text-foreground flex items-start gap-2 leading-tight">
                        <Calendar className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                        <span>
                          {language === "de"
                            ? "Bestätige dein Geburtsdatum"
                            : "Confirm your date of birth"}
                        </span>
                      </DialogTitle>
                    </DialogHeader>
                    <div className="flex-1 min-h-0 overflow-y-auto px-4 sm:px-6 md:px-8 pb-2">
                      <DialogDescription asChild>
                        <div className="text-xs sm:text-sm text-muted-foreground leading-relaxed space-y-3">
                          <p>
                            {language === "de"
                              ? "Dein Geburtsdatum wird für den Mietvertrag benötigt. Bitte bestätige oder ergänze es, bevor du mit der Unterschrift fortfährst."
                              : "Your date of birth is required for the rental contract. Please confirm or add it before continuing to sign."}
                          </p>
                          <div className="space-y-2 pt-1">
                            <Label
                              htmlFor="dob-gate"
                              className="text-sm font-medium text-foreground"
                            >
                              {language === "de"
                                ? "Geburtsdatum"
                                : "Date of birth"}
                            </Label>
                            <Input
                              id="dob-gate"
                              type="date"
                              value={dobInput}
                              max={latestAdultBirthDate()}
                              onChange={(e) => setDobInput(e.target.value)}
                              className="h-11 rounded-xl"
                            />
                          </div>
                        </div>
                      </DialogDescription>
                    </div>
                    <DialogFooter className="flex flex-col-reverse sm:flex-row gap-3 px-4 sm:px-6 md:px-8 pt-4 border-t border-border shrink-0 pb-[max(1rem,env(safe-area-inset-bottom))]">
                      <Button
                        variant="outline"
                        className="w-full sm:w-auto"
                        onClick={() => setDobConfirmOpen(false)}
                        disabled={savingDob}
                      >
                        {language === "de" ? "Abbrechen" : "Cancel"}
                      </Button>
                      <Button
                        className="w-full sm:w-auto bg-emerald-600 hover:bg-emerald-700 text-white"
                        onClick={handleDobConfirm}
                        disabled={savingDob || !dobInput.trim()}
                      >
                        {savingDob ? (
                          <>
                            <Loader2 className="mr-2 w-4 h-4 animate-spin" />
                            {language === "de" ? "Speichern…" : "Saving…"}
                          </>
                        ) : language === "de" ? (
                          "Weiter"
                        ) : (
                          "Continue"
                        )}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </>
            ) : (
              <ComingSoon description="This feature will be available soon." />
            )}
          </TabsContent>

          {/* Favorites Tab - My Favourite Rooms: modern UI, dynamic list, 3-item nav */}
          <TabsContent value="favorites">
            <Card className="overflow-hidden rounded-2xl border border-border/60 bg-card shadow-sm">
              <div className="p-6 sm:p-8">
                {/* 3-item navigation: Home | Favorites | Browse Properties */}
                <nav
                  className="flex flex-wrap items-center gap-2 mb-6 text-sm"
                  aria-label={t(
                    "dashboard.favoritesSectionNavAriaLabel",
                    "Favorites section navigation"
                  )}
                >
                  <Link
                    to="/dashboard"
                    className="text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1.5"
                  >
                    <HomeIcon className="w-4 h-4" />
                    {tabLabel("dashboard.tab.home", "Home", "Start")}
                  </Link>
                  <span className="text-muted-foreground/60" aria-hidden>
                    /
                  </span>
                  <span className="font-medium text-foreground flex items-center gap-1.5">
                    <Heart className="w-4 h-4 text-primary" />
                    {t("dashboard.favorites.title")}
                  </span>
                  <span className="text-muted-foreground/60" aria-hidden>
                    /
                  </span>
                  <Link
                    to="/rooms"
                    className="text-primary hover:underline font-medium flex items-center gap-1.5"
                  >
                    {t("properties.title")}
                    <ArrowRight className="w-3.5 h-3.5" />
                  </Link>
                </nav>

                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
                  <div>
                    <h2 className="text-2xl sm:text-3xl font-bold text-foreground tracking-tight">
                      {t("dashboard.favorites.title")}
                    </h2>
                    {favoritesData && favoritesData.length > 0 && (
                      <p className="mt-1 text-sm text-muted-foreground">
                        {favoritesData.length === 1
                          ? "1 room saved"
                          : `${favoritesData.length} rooms saved`}
                      </p>
                    )}
                  </div>
                  {favoritesData && favoritesData.length > 0 && (
                    <Link to="/rooms">
                      <Button
                        variant="outline"
                        size="sm"
                        className="rounded-lg gap-2"
                      >
                        {t("properties.title")}
                        <ArrowRight className="w-4 h-4" />
                      </Button>
                    </Link>
                  )}
                </div>

                {favoritesLoading ? (
                  <div className="flex flex-col items-center justify-center py-16">
                    <Loader2 className="w-10 h-10 animate-spin text-muted-foreground mb-4" />
                    <p className="text-muted-foreground">
                      Loading your favorites...
                    </p>
                  </div>
                ) : !favoritesData || favoritesData.length === 0 ? (
                  <div className="text-center py-14 px-4 rounded-xl bg-muted/30 border border-dashed border-border">
                    <Heart className="w-14 h-14 mx-auto mb-4 text-muted-foreground/70" />
                    <p className="text-lg font-semibold text-foreground mb-2">
                      {t("dashboard.favorites.empty")}
                    </p>
                    <p className="text-muted-foreground mb-6 max-w-sm mx-auto">
                      {t("dashboard.favorites.emptyDesc")}
                    </p>
                    <Link to="/rooms">
                      <Button className="rounded-lg gap-2">
                        {t("properties.title")}
                        <ArrowRight className="w-4 h-4" />
                      </Button>
                    </Link>
                  </div>
                ) : (
                  (() => {
                    const totalRooms = favoritesData.length;
                    const totalPages = Math.ceil(
                      totalRooms / FAVORITES_ROOMS_PER_PAGE
                    );
                    const safePage = Math.min(
                      Math.max(1, favoritesPage),
                      totalPages
                    );
                    const start = (safePage - 1) * FAVORITES_ROOMS_PER_PAGE;
                    const end = Math.min(
                      start + FAVORITES_ROOMS_PER_PAGE,
                      totalRooms
                    );
                    const paginatedRooms = favoritesData.slice(start, end);
                    return (
                      <>
                        <p className="text-sm text-muted-foreground mb-4">
                          Showing {start + 1}–{end} of {totalRooms}
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                          {paginatedRooms.map((room: any) => {
                            const property = room.properties;

                            // Image: prefer room.images[0], then property.image_url, then fallback
                            let imageUrl: string | null = null;
                            if (room.images) {
                              try {
                                const arr =
                                  typeof room.images === "string"
                                    ? JSON.parse(room.images)
                                    : Array.isArray(room.images)
                                      ? room.images
                                      : [];
                                const first = arr[0];
                                if (typeof first === "string" && first)
                                  imageUrl = first.startsWith("http")
                                    ? first
                                    : `https://a.storyblok.com/f/${first}`;
                                else if (first?.url) imageUrl = first.url;
                                else if (first?.filename)
                                  imageUrl = `https://a.storyblok.com/f/${first.filename}`;
                              } catch {
                                /* ignore invalid room.images JSON */
                              }
                            }
                            if (!imageUrl && property?.image_url) {
                              const propImg = property.image_url.trim();
                              imageUrl = propImg.startsWith("http")
                                ? propImg
                                : propImg.startsWith("f/")
                                  ? `https://a.storyblok.com/${propImg}`
                                  : `https://a.storyblok.com/f/${propImg}`;
                            }
                            if (!imageUrl) {
                              const fallbackImages = [room1, room2, room3];
                              imageUrl =
                                fallbackImages[room.id % fallbackImages.length];
                            }

                            const city = property?.city ?? "";
                            const buildingTitle = property?.title ?? "Property";

                            return (
                              <Card
                                key={room.id}
                                className="overflow-hidden rounded-xl border border-border/60 bg-card shadow-sm hover:shadow-md hover:border-primary/20 transition-all duration-200"
                              >
                                <div className="relative h-52 overflow-hidden group">
                                  <img
                                    src={imageUrl}
                                    alt={room.title}
                                    className="w-full h-full object-cover bg-muted transition-transform duration-300 group-hover:scale-105"
                                    onError={(e) => {
                                      if (e.currentTarget.src !== room1)
                                        e.currentTarget.src = room1;
                                    }}
                                  />
                                  <button
                                    type="button"
                                    onClick={() =>
                                      toggleFavoriteMutation.mutate({
                                        roomId: room.id,
                                      })
                                    }
                                    className="absolute top-3 right-3 p-2.5 rounded-full bg-white/95 hover:bg-white shadow-md z-10 transition-colors hover:scale-110"
                                    title="Remove from favorites"
                                  >
                                    <Heart className="w-5 h-5 fill-red-500 text-red-500" />
                                  </button>
                                  {room.available !== false && (
                                    <Badge className="absolute top-3 left-3 rounded-lg bg-primary/95 text-primary-foreground text-xs font-medium">
                                      {t("properties.available")}
                                    </Badge>
                                  )}
                                </div>
                                <div className="p-5">
                                  <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-muted-foreground mb-2">
                                    <MapPin className="w-3.5 h-3.5 shrink-0" />
                                    {city || t("properties.viewDetails")}
                                  </div>
                                  <h3 className="text-lg font-bold text-foreground mb-1 line-clamp-1">
                                    {buildingTitle}
                                  </h3>
                                  <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
                                    <Users className="w-4 h-4 shrink-0" />
                                    <span className="line-clamp-1">
                                      {room.title}
                                    </span>
                                  </div>
                                  <div className="flex items-center justify-between pt-3 border-t border-border/60">
                                    <div>
                                      <span className="text-2xl font-bold text-foreground">
                                        €
                                        {Number(room.price ?? 0).toLocaleString(
                                          "en-US",
                                          {
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2,
                                          }
                                        )}
                                      </span>
                                      <span className="text-sm text-muted-foreground ml-1">
                                        {t("properties.perMonth")}
                                      </span>
                                    </div>
                                    <Link to={`/property/${room.id}`}>
                                      <Button
                                        size="sm"
                                        className="rounded-lg gap-1.5"
                                      >
                                        {t("properties.viewDetails")}
                                        <ArrowRight className="w-4 h-4" />
                                      </Button>
                                    </Link>
                                  </div>
                                </div>
                              </Card>
                            );
                          })}
                        </div>
                        {totalPages > 1 && (
                          <div className="mt-8 flex flex-wrap items-center justify-center gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              className="rounded-lg gap-1"
                              disabled={safePage <= 1}
                              onClick={() =>
                                setFavoritesPage((p) => Math.max(1, p - 1))
                              }
                            >
                              <ChevronLeft className="w-4 h-4" />
                              Previous
                            </Button>
                            <span className="px-3 py-1.5 text-sm text-muted-foreground">
                              Page {safePage} of {totalPages}
                            </span>
                            <Button
                              variant="outline"
                              size="sm"
                              className="rounded-lg gap-1"
                              disabled={safePage >= totalPages}
                              onClick={() =>
                                setFavoritesPage((p) =>
                                  Math.min(totalPages, p + 1)
                                )
                              }
                            >
                              Next
                              <ChevronRight className="w-4 h-4" />
                            </Button>
                          </div>
                        )}
                      </>
                    );
                  })()
                )}
              </div>
            </Card>
          </TabsContent>

          {/* Payments Tab */}
          <TabsContent
            value="payments"
            className="mt-0 w-full min-w-0 max-w-full overflow-x-hidden"
          >
            <div className="flex flex-col gap-6 lg:flex-row lg:gap-8 xl:gap-10">
              {/* Payments sidebar navigation — premium modern rail */}
              <aside className="hidden lg:flex w-[16rem] shrink-0 flex-col text-sm">
                <div className="sticky top-24 space-y-6">
                  <div className="rounded-2xl border border-border/50 bg-card/80 p-3 shadow-lg backdrop-blur-xl dark:bg-card/60">
                    <p className="mb-3 px-3 text-[10px] font-extrabold uppercase tracking-[0.22em] text-muted-foreground/60">
                      {language === "de" ? "Zahlungen" : "Payments"}
                    </p>
                    <nav className="flex flex-col gap-1">
                      {(
                        [
                          {
                            step: "overview" as const,
                            icon: <HomeIcon className="h-4 w-4" />,
                            label: p.paymentOverview,
                          },
                          {
                            step: "method" as const,
                            icon: <CreditCard className="h-4 w-4" />,
                            label: p.paymentMethod,
                          },
                          {
                            step: "breakdown" as const,
                            icon: <FileText className="h-4 w-4" />,
                            label: p.rentBreakdown,
                          },
                          {
                            step: "history" as const,
                            icon: <Calendar className="h-4 w-4" />,
                            label: p.paymentHistory,
                          },
                        ] as const
                      ).map((item) => (
                        <button
                          key={item.step}
                          type="button"
                          onClick={() => setPaymentStep(item.step)}
                          className={cn(
                            "group relative flex items-center gap-3 rounded-xl px-3 py-3 text-left transition-all duration-200",
                            paymentStep === item.step
                              ? "bg-gradient-to-r from-primary/[0.08] to-primary/[0.03] font-semibold text-foreground dark:from-primary/15 dark:to-primary/5"
                              : "text-muted-foreground hover:bg-muted/50 hover:text-foreground"
                          )}
                        >
                          {/* Active indicator bar */}
                          <span
                            className={cn(
                              "absolute left-0 top-1/2 h-6 w-[3px] -translate-y-1/2 rounded-full transition-all duration-200",
                              paymentStep === item.step
                                ? "bg-primary opacity-100"
                                : "bg-transparent opacity-0 group-hover:bg-muted-foreground/30 group-hover:opacity-100"
                            )}
                          />
                          <span
                            className={cn(
                              "flex h-8 w-8 shrink-0 items-center justify-center rounded-lg transition-colors",
                              paymentStep === item.step
                                ? "bg-primary/15 text-primary dark:bg-primary/20"
                                : "bg-muted/50 text-muted-foreground group-hover:bg-muted group-hover:text-foreground"
                            )}
                          >
                            {item.icon}
                          </span>
                          <span className="text-[13px]">{item.label}</span>
                        </button>
                      ))}
                    </nav>
                  </div>

                  {/* Sidebar quick stat */}
                  <div className="rounded-2xl border border-border/50 bg-card/80 p-4 shadow-lg backdrop-blur-xl dark:bg-card/60">
                    <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground/60">
                      {language === "de" ? "Monatliche Miete" : "Monthly rent"}
                    </p>
                    <p className="mt-2 text-2xl font-bold tabular-nums tracking-tight text-foreground">
                      €
                      {Number(displayContractMonthlyRentEUR).toLocaleString(
                        "de-DE"
                      )}
                    </p>
                    {contractMonths != null && (
                      <div className="mt-3">
                        <div className="flex items-center justify-between text-[11px] text-muted-foreground">
                          <span>
                            {paidMonths} / {contractMonths} {p.months}
                          </span>
                          <span className="font-semibold tabular-nums text-foreground">
                            {contractMonths > 0
                              ? Math.round((paidMonths / contractMonths) * 100)
                              : 0}
                            %
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </aside>

              <div className="min-w-0 w-full max-w-full flex-1 space-y-6 sm:space-y-8">
                {/* Mobile / tablet: horizontal payment section nav */}
                {(paymentStep === "overview" ||
                  paymentStep === "method" ||
                  paymentStep === "breakdown" ||
                  paymentStep === "history") && (
                  <div className="lg:hidden">
                    <nav
                      className="-mx-1 flex gap-1.5 overflow-x-auto scroll-pb-2 px-1 pb-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
                      aria-label={
                        language === "de"
                          ? "Zahlungsbereiche"
                          : "Payment sections"
                      }
                    >
                      {(
                        [
                          {
                            step: "overview" as const,
                            label: p.paymentOverview,
                          },
                          { step: "method" as const, label: p.paymentMethod },
                          {
                            step: "breakdown" as const,
                            label: p.rentBreakdown,
                          },
                          { step: "history" as const, label: p.paymentHistory },
                        ] as const
                      ).map((item) => (
                        <button
                          key={item.step}
                          type="button"
                          onClick={() => setPaymentStep(item.step)}
                          className={cn(
                            "shrink-0 rounded-full px-4 py-2 text-xs font-medium transition-all sm:text-sm",
                            paymentStep === item.step
                              ? "bg-primary font-semibold text-primary-foreground shadow-md"
                              : "bg-muted/40 text-muted-foreground hover:bg-muted/70 hover:text-foreground"
                          )}
                        >
                          {item.label}
                        </button>
                      ))}
                    </nav>
                  </div>
                )}

                {/* Overview screen */}
                {paymentStep === "overview" && (
                  <div className="mx-auto w-full max-w-screen-2xl space-y-8 sm:space-y-10">
                    <header className="relative overflow-hidden rounded-2xl border border-border/70 bg-gradient-to-br from-card via-card to-primary/[0.06] p-6 shadow-[0_1px_3px_rgba(0,0,0,0.05)] sm:p-8 dark:to-primary/[0.04]">
                      <div
                        className="pointer-events-none absolute -right-8 -top-12 h-40 w-40 rounded-full bg-primary/[0.12] blur-3xl dark:bg-primary/[0.08]"
                        aria-hidden
                      />
                      <div
                        className="pointer-events-none absolute bottom-0 left-1/4 h-24 w-48 -translate-x-1/2 translate-y-1/2 rounded-full bg-primary/[0.06] blur-2xl dark:bg-primary/[0.05]"
                        aria-hidden
                      />
                      <div className="relative space-y-2">
                        <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-primary/80">
                          {p.paymentsOverviewEyebrow}
                        </p>
                        <h2 className="text-2xl font-semibold tracking-tight text-foreground sm:text-[1.75rem]">
                          {p.paymentOverview}
                        </h2>
                        <p className="max-w-xl text-sm leading-relaxed text-muted-foreground">
                          {p.paymentsHeroSubtitle}
                        </p>
                      </div>
                    </header>

                    <div className="flex flex-col gap-8 lg:flex-row lg:items-start lg:gap-8 xl:gap-12">
                      <div className="min-w-0 flex-1 space-y-8">
                        {/* At a glance — first in main column */}
                        <section
                          className="space-y-4"
                          aria-labelledby="payments-at-a-glance"
                        >
                          <h3
                            id="payments-at-a-glance"
                            className="flex items-center gap-2.5 text-left text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground"
                          >
                            <span
                              className="h-1.5 w-1.5 shrink-0 rounded-full bg-primary ring-4 ring-primary/15 dark:ring-primary/25"
                              aria-hidden
                            />
                            {p.paymentsSectionAtAGlance}
                          </h3>
                          <div className="overflow-hidden rounded-2xl border border-border/80 bg-card shadow-[0_2px_8px_-2px_rgba(0,0,0,0.06),0_4px_16px_-4px_rgba(0,0,0,0.04)]">
                            <div className="grid gap-px bg-border/60 md:grid-cols-3 dark:bg-border/80">
                              <div className="bg-card p-6 transition-colors duration-200 sm:p-7 md:hover:bg-muted/25">
                                <div className="flex items-start justify-between gap-3">
                                  <p className="text-sm font-medium text-foreground">
                                    {p.monthlyRent}
                                  </p>
                                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-sm">
                                    <Euro
                                      className="h-5 w-5"
                                      strokeWidth={2.5}
                                    />
                                  </div>
                                </div>
                                <p className="mt-5 text-3xl font-bold tabular-nums tracking-tight sm:text-4xl">
                                  <span className="text-primary">€</span>
                                  <span className="text-foreground">
                                    {Number(
                                      displayContractMonthlyRentEUR
                                    ).toLocaleString("de-DE")}
                                  </span>
                                </p>
                                {isPartialFirstMonthRent &&
                                  fullMonthlyRent > 0 && (
                                    <div className="mt-4 rounded-lg border border-border/60 bg-muted/30 px-3 py-2.5">
                                      <p className="flex items-center gap-1 text-xs font-semibold text-foreground">
                                        <Receipt className="h-3 w-3 text-primary" />
                                        {p.proRataBadge}
                                      </p>
                                      <p className="mt-1 text-xs leading-snug text-muted-foreground">
                                        {p.proRataFirstMonthNote(
                                          `€${Number(fullMonthlyRent).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                                        )}
                                      </p>
                                    </div>
                                  )}
                                <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
                                  {p.monthlyRentDesc}
                                </p>
                              </div>

                              <div className="bg-card p-6 transition-colors duration-200 sm:p-7 md:hover:bg-muted/25">
                                <div className="flex items-start justify-between gap-3">
                                  <p className="text-sm font-medium text-foreground">
                                    {p.nextPayment}
                                  </p>
                                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-sm">
                                    <Calendar
                                      className="h-5 w-5"
                                      strokeWidth={2.25}
                                    />
                                  </div>
                                </div>
                                <p className="mt-5 text-xl font-bold leading-snug tracking-tight text-foreground break-words sm:text-2xl">
                                  {nextUnpaidPaymentDueLabel}
                                </p>
                                <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
                                  {hasUnpaidUpcoming
                                    ? p.usePayNowToSettle(
                                        nextUnpaidPaymentMonthLabel
                                      )
                                    : p.nextRentDueNoAction(
                                        nextUnpaidPaymentDueLabel
                                      )}
                                </p>
                              </div>

                              <div className="bg-card p-6 transition-colors duration-200 sm:p-7 md:hover:bg-muted/25">
                                <div className="flex items-start justify-between gap-3">
                                  <p className="text-sm font-medium text-foreground">
                                    {p.deposit}
                                  </p>
                                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-sm">
                                    <Euro
                                      className="h-5 w-5"
                                      strokeWidth={2.5}
                                    />
                                  </div>
                                </div>
                                <p className="mt-5 text-3xl font-bold tabular-nums tracking-tight sm:text-4xl">
                                  <span className="text-primary">€</span>
                                  <span className="text-foreground">
                                    {Number(depositAmount).toLocaleString(
                                      "de-DE"
                                    )}
                                  </span>
                                </p>
                                <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
                                  {p.depositHeldRefunded}
                                </p>
                              </div>
                            </div>
                          </div>
                        </section>

                        {/* Upcoming payment — light card, nested summary (reference UI) */}
                        <Card className="overflow-hidden rounded-2xl border border-border/80 bg-card shadow-[0_2px_8px_-2px_rgba(0,0,0,0.06),0_8px_24px_-8px_rgba(0,0,0,0.05)] transition-shadow duration-300 hover:shadow-[0_4px_12px_-2px_rgba(0,0,0,0.08),0_12px_28px_-8px_rgba(0,0,0,0.06)]">
                          <div className="border-b border-border/60 bg-gradient-to-br from-muted/35 via-muted/15 to-primary/[0.04] px-5 py-5 sm:px-8 sm:py-6 dark:from-muted/25 dark:to-primary/[0.05]">
                            <h3 className="text-lg font-semibold tracking-tight text-foreground sm:text-xl">
                              {p.upcomingPayment}
                            </h3>
                            <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">
                              {p.reviewNextRentPaySecurely}
                            </p>
                          </div>
                          <div className="px-5 py-6 sm:px-8 sm:py-8">
                            <div className="rounded-xl border border-border/60 bg-gradient-to-b from-muted/25 to-muted/10 p-5 shadow-inner sm:p-6 dark:from-muted/20 dark:to-muted/5">
                              <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between sm:gap-6">
                                <div className="min-w-0 flex-1 space-y-6">
                                  {financialsBooking?.rooms?.properties && (
                                    <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                                      {financialsBooking.rooms.properties.title}
                                      {financialsBooking.rooms.title &&
                                        ` · ${financialsBooking.rooms.title}`}
                                    </p>
                                  )}
                                  <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between sm:gap-6">
                                    <div>
                                      <p className="text-xs font-medium text-muted-foreground">
                                        {p.amountDueLabel}
                                      </p>
                                      <p className="mt-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground/80">
                                        {nextPaymentDate.toLocaleDateString(
                                          undefined,
                                          { month: "long", year: "numeric" }
                                        )}{" "}
                                        · {p.rentLabel}
                                      </p>
                                    </div>
                                    <div className="flex flex-col items-start gap-2 sm:items-end sm:text-right">
                                      <span
                                        className={cn(
                                          "inline-flex w-fit items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold",
                                          paymentStatusLabel === "Paid" ||
                                            paidUpcomingPayment
                                            ? "border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-800/50 dark:bg-emerald-950/40 dark:text-emerald-200"
                                            : paymentStatusLabel ===
                                                "SepaInProgress"
                                              ? "border-sky-200 bg-sky-50 text-sky-800 dark:border-sky-800/50 dark:bg-sky-950/40 dark:text-sky-200"
                                              : paymentStatusLabel === "Overdue"
                                                ? "border-red-200 bg-red-50 text-red-800 dark:border-red-900/50 dark:bg-red-950/30 dark:text-red-200"
                                                : "border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-800/50 dark:bg-amber-950/40 dark:text-amber-100"
                                        )}
                                      >
                                        {paymentStatusLabel === "Paid" ||
                                        paidUpcomingPayment ? (
                                          <>
                                            <CheckCircle2 className="h-3.5 w-3.5" />
                                            {p.paidStatus}
                                          </>
                                        ) : paymentStatusLabel ===
                                          "SepaInProgress" ? (
                                          <>
                                            <Landmark className="h-3.5 w-3.5" />
                                            {p.sepaInProgressStatus}
                                          </>
                                        ) : paymentStatusLabel === "Overdue" ? (
                                          <>
                                            <AlertCircle className="h-3.5 w-3.5" />
                                            {p.overdueStatus}
                                          </>
                                        ) : (
                                          <>
                                            <Clock className="h-3.5 w-3.5" />
                                            {p.pendingStatus}
                                          </>
                                        )}
                                      </span>
                                      {upcomingReferralCreditNote && (
                                        <p
                                          className="text-base font-semibold tabular-nums text-muted-foreground line-through decoration-muted-foreground/70"
                                          aria-label={
                                            language === "de"
                                              ? "Miete vor Gutschrift"
                                              : "Rent before credit"
                                          }
                                        >
                                          €
                                          {Number(
                                            upcomingGrossChargeEUR
                                          ).toLocaleString("de-DE")}
                                        </p>
                                      )}
                                      <p className="text-3xl font-bold tabular-nums tracking-tight sm:text-4xl">
                                        <span className="text-primary">€</span>
                                        <span className="text-foreground">
                                          {Number(
                                            upcomingOwed.totalCents / 100
                                          ).toLocaleString("de-DE")}
                                        </span>
                                      </p>
                                      {/* COL-472: what the total is made of
                                          once Mahngebühren are in it. */}
                                      {upcomingOwed.feeCents > 0 && (
                                        <p className="text-xs font-medium tabular-nums text-muted-foreground sm:text-right">
                                          {p.rentFeeSplit(
                                            eurLabel(upcomingOwed.rentCents),
                                            eurLabel(upcomingOwed.feeCents)
                                          )}
                                        </p>
                                      )}
                                      {upcomingReferralCreditNote && (
                                        <p className="inline-flex max-w-xs items-start gap-1.5 text-xs font-medium leading-snug text-primary sm:text-right">
                                          <Gift className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                                          {upcomingReferralCreditNote}
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                              <div className="mt-4 flex flex-col gap-2 border-t border-border/60 pt-4 text-sm text-muted-foreground">
                                <span className="inline-flex items-center gap-2">
                                  <Calendar className="h-4 w-4 shrink-0 text-primary/70" />
                                  {p.dueOn} {nextPaymentDueLabel}
                                </span>
                              </div>
                              {/* COL-472: fees are owed but card / SEPA only
                                  collects rent, so say how to settle them. A
                                  pool no due month carries is listed here too. */}
                              {(upcomingOwed.feeCents > 0 ||
                                upcomingStandaloneFeeCents > 0) && (
                                <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs leading-relaxed text-amber-900 dark:border-amber-800/50 dark:bg-amber-950/30 dark:text-amber-100">
                                  {upcomingStandaloneFeeCents > 0 && (
                                    <p className="flex items-center justify-between gap-2 font-semibold tabular-nums">
                                      <span>{p.outstandingFeesLabel}</span>
                                      <span>
                                        {eurLabel(upcomingStandaloneFeeCents)}
                                      </span>
                                    </p>
                                  )}
                                  <p
                                    className={
                                      upcomingStandaloneFeeCents > 0
                                        ? "mt-1"
                                        : undefined
                                    }
                                  >
                                    {p.feesByTransferNote}
                                  </p>
                                </div>
                              )}
                              {/* SEPA direct debit belongs with RENT — it is the
                                  recurring payment. Entry point lives here (Neel,
                                  pre-COL-89); full management arrives with COL-89. */}
                              {sepaDebitEnabled && !hasActiveMandate && (
                                <div className="mt-4">
                                  <Button
                                    variant="outline"
                                    size="lg"
                                    className="w-full min-w-0"
                                    onClick={() =>
                                      setSepaMandateOpen({
                                        bookingId: financialsBooking?.id
                                          ? Number(financialsBooking.id)
                                          : null,
                                      })
                                    }
                                  >
                                    <Landmark className="mr-2 h-4 w-4" />
                                    {p.payBySepa}
                                  </Button>
                                </div>
                              )}
                              {sepaDebitEnabled && hasActiveMandate && (
                                <div className="mt-4 space-y-1.5">
                                  <Badge variant="secondary">
                                    {sepaChargeInFlight
                                      ? p.sepaInProgressStatus
                                      : p.sepaMandateActiveBadge}
                                  </Badge>
                                  <p className="text-xs leading-relaxed text-muted-foreground">
                                    {sepaChargeInFlight
                                      ? p.sepaInProgressNote
                                      : p.sepaActiveAutoDebitNote}
                                  </p>
                                </div>
                              )}
                              {isUpcomingInvoiceProRata && (
                                <div className="mt-4 rounded-lg border border-primary/20 bg-muted/50 px-3 py-2.5">
                                  <p className="flex items-center gap-1.5 text-xs font-semibold text-foreground">
                                    <Receipt className="h-3.5 w-3.5 text-primary" />
                                    {p.proRataBadge}
                                  </p>
                                  <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                                    {p.upcomingInvoiceProRata}
                                  </p>
                                </div>
                              )}
                            </div>

                            {upcomingPayment?.id &&
                              paymentProofsByPaymentId?.[upcomingPayment.id]
                                ?.rejectionReasonToShow && (
                                <div className="mt-5 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-900 dark:border-red-900/50 dark:bg-red-950/40 dark:text-red-100">
                                  <p className="font-medium">
                                    {p.rejectionReason}
                                  </p>
                                  <p className="mt-1">
                                    {
                                      paymentProofsByPaymentId[
                                        upcomingPayment.id
                                      ].rejectionReasonToShow
                                    }
                                  </p>
                                  <p className="mt-1.5 text-xs text-red-800 dark:text-red-200">
                                    {language === "de"
                                      ? "Bitte laden Sie einen neuen Zahlungsnachweis über „Per Überweisung zahlen“ hoch."
                                      : "Please upload a new payment proof using Pay by bank transfer."}
                                  </p>
                                </div>
                              )}
                          </div>

                          <div className="flex flex-col gap-4 border-t border-border/80 bg-gradient-to-r from-muted/20 via-muted/10 to-primary/[0.03] px-5 py-5 sm:flex-row sm:items-center sm:justify-between sm:px-8 dark:from-muted/15">
                            <div className="flex min-w-0 w-full flex-col gap-3 sm:w-auto sm:flex-row sm:gap-3">
                              {!paidUpcomingPayment &&
                              paymentStatusLabel !== "Paid" &&
                              !sepaChargeInFlight ? (
                                <>
                                  {/* Feedback 8: an active mandate makes Pay Now
                                      debit via SEPA right away — the card
                                      checkout is for tenants WITHOUT one. */}
                                  {sepaDebitEnabled && hasActiveMandate ? (
                                    <Button
                                      className={cn(
                                        "h-11 w-full font-semibold shadow-md transition-all hover:shadow-lg sm:w-auto sm:min-w-[11rem]",
                                        // Halo while the Payments tab's pip
                                        // says the rent is overdue (COL-368).
                                        paymentStatusLabel === "Overdue" &&
                                          "animate-cta-pulse motion-reduce:animate-none"
                                      )}
                                      size="lg"
                                      disabled={sepaPayNowBusy}
                                      onClick={() => void payNowViaSepa()}
                                    >
                                      {sepaPayNowBusy ? (
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                      ) : (
                                        <Landmark className="mr-2 h-4 w-4" />
                                      )}
                                      {language === "de"
                                        ? "Jetzt per Lastschrift zahlen"
                                        : "Pay now by direct debit"}
                                    </Button>
                                  ) : stripeCardEnabled ? (
                                    <Button
                                      className={cn(
                                        "h-11 w-full font-semibold shadow-md transition-all hover:shadow-lg sm:w-auto sm:min-w-[11rem]",
                                        // Halo while the Payments tab's pip
                                        // says the rent is overdue (COL-368).
                                        paymentStatusLabel === "Overdue" &&
                                          "animate-cta-pulse motion-reduce:animate-none"
                                      )}
                                      size="lg"
                                      onClick={() => setPaymentStep("confirm")}
                                    >
                                      {p.payNow}
                                      <ArrowRight className="ml-2 h-4 w-4" />
                                    </Button>
                                  ) : null}
                                  {bankTransferDetails && (
                                    <Button
                                      variant="outline"
                                      size="lg"
                                      className={cn(
                                        "h-11 w-full border-border bg-background sm:w-auto sm:min-w-[11rem]",
                                        // Normally the secondary option, so it
                                        // stays quiet while a primary "Pay now"
                                        // carries the halo. When neither rail is
                                        // available this IS the only way to pay,
                                        // and an overdue pip pointing at a card
                                        // where nothing pulses is the
                                        // leads-nowhere case the pip exists to
                                        // avoid (COL-368).
                                        paymentStatusLabel === "Overdue" &&
                                          !(
                                            sepaDebitEnabled && hasActiveMandate
                                          ) &&
                                          !stripeCardEnabled &&
                                          "animate-cta-pulse motion-reduce:animate-none"
                                      )}
                                      onClick={async () => {
                                        const unpaidUpcoming =
                                          upcomingPayment &&
                                          !isPaymentEffectivelyPaid(
                                            upcomingPayment
                                          )
                                            ? upcomingPayment
                                            : null;
                                        let payment =
                                          unpaidUpcoming ??
                                          nextRentPayment ??
                                          null;
                                        if (
                                          !payment &&
                                          user?.id &&
                                          financialsBooking?.id &&
                                          upcomingRemainingChargeEUR
                                        ) {
                                          // Upsert by (booking, type='rent', month-of-due_date): never create a duplicate
                                          // rent row for a month that already has a Stripe/manual-paid or pending entry.
                                          const dueDate = getNextPaymentDate()
                                            .toISOString()
                                            .slice(0, 10);
                                          const { payment: upserted, error } =
                                            await ensureRentPaymentForMonth({
                                              userId: user.id,
                                              bookingId: Number(
                                                financialsBooking.id
                                              ),
                                              dueDate,
                                              // Same amount already shown on the card for this
                                              // no-row fallback (`upcomingRemainingChargeEUR`) — never
                                              // charge a different figure than what was displayed.
                                              amountCents: Math.round(
                                                Number(
                                                  upcomingRemainingChargeEUR
                                                ) * 100
                                              ),
                                              currency: "eur",
                                              status: "pending",
                                              description:
                                                language === "de"
                                                  ? "Miete (Überweisung)"
                                                  : "Rent (bank transfer)",
                                            });
                                          if (!error && upserted)
                                            payment = upserted as any;
                                        }
                                        if (!payment) {
                                          toast.error(
                                            language === "de"
                                              ? "Keine anstehende Zahlung gefunden."
                                              : "No upcoming payment found."
                                          );
                                          return;
                                        }
                                        setBankTransferPayment(payment);
                                        setPaymentProofFiles([]);
                                        setPaymentProofUploadProgress(0);
                                        setPaymentStep("bank_transfer_upload");
                                      }}
                                    >
                                      <Landmark className="mr-2 h-4 w-4" />
                                      {p.payByBankTransfer}
                                    </Button>
                                  )}
                                </>
                              ) : (
                                <Button
                                  className="h-11 w-full sm:w-auto sm:min-w-[11rem]"
                                  size="lg"
                                  variant="outline"
                                  disabled
                                >
                                  {sepaChargeInFlight ? (
                                    <>
                                      <Landmark className="mr-2 h-4 w-4" />
                                      {p.sepaInProgressStatus}
                                    </>
                                  ) : (
                                    p.paymentCompleted
                                  )}
                                </Button>
                              )}
                            </div>
                            <Button
                              type="button"
                              variant="ghost"
                              className="h-auto w-full shrink-0 py-2 text-sm text-muted-foreground hover:text-foreground sm:w-auto sm:self-center"
                              onClick={() => setPaymentStep("breakdown")}
                            >
                              {p.viewDetails}
                            </Button>
                          </div>
                        </Card>
                      </div>

                      <aside className="min-w-0 w-full shrink-0 space-y-6 lg:w-[min(100%,18rem)] xl:w-[20rem] lg:sticky lg:top-24">
                        <section
                          className="space-y-4"
                          aria-labelledby="payments-payment-options"
                        >
                          <h3
                            id="payments-payment-options"
                            className="flex items-center gap-2.5 text-left text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground"
                          >
                            <span
                              className="h-1.5 w-1.5 shrink-0 rounded-full bg-primary ring-4 ring-primary/15 dark:ring-primary/25"
                              aria-hidden
                            />
                            {p.paymentsSectionPaymentOptions}
                          </h3>
                          <Card className="w-full overflow-hidden rounded-2xl border border-border/80 bg-card shadow-[0_2px_8px_-2px_rgba(0,0,0,0.06)] transition-shadow duration-300 hover:shadow-md">
                            <div className="border-b border-border/60 bg-gradient-to-r from-muted/30 to-muted/10 px-5 py-4 sm:px-6">
                              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
                                <div className="min-w-0">
                                  <p className="text-base font-semibold text-foreground">
                                    {p.paymentMethod}
                                  </p>
                                  <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                                    {p.manageHowCollected}
                                  </p>
                                </div>
                                <div className="inline-flex w-fit shrink-0 items-center gap-1.5 rounded-full border border-emerald-200/80 bg-emerald-50/50 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-emerald-700 dark:border-emerald-800/40 dark:bg-emerald-950/30 dark:text-emerald-300">
                                  <Lock className="h-3 w-3" />
                                  <span>{p.secure}</span>
                                </div>
                              </div>
                            </div>
                            <div className="space-y-4 p-5 sm:p-6">
                              {/* Summary of the CHOSEN method (feedback 1b) — the
                                  picker on the method step is the place to change it. */}
                              {selectedPayMethod && (
                                <div className="rounded-lg border border-border bg-background p-3">
                                  <div className="flex items-start justify-between gap-3">
                                    <div className="flex min-w-0 items-start gap-3">
                                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-border bg-muted/40">
                                        {selectedPayMethod === "card" ? (
                                          <CreditCard className="h-4 w-4 text-muted-foreground" />
                                        ) : (
                                          <Landmark className="h-4 w-4 text-muted-foreground" />
                                        )}
                                      </div>
                                      <div className="min-w-0">
                                        <p className="text-xs font-medium text-foreground">
                                          {selectedPayMethod === "card"
                                            ? p.cardPaymentStripe
                                            : selectedPayMethod === "sepa_debit"
                                              ? language === "de"
                                                ? "SEPA-Lastschrift"
                                                : "SEPA Direct Debit"
                                              : language === "de"
                                                ? "Überweisung"
                                                : "Bank transfer"}
                                        </p>
                                        <p className="text-[11px] text-muted-foreground">
                                          {selectedPayMethod === "card"
                                            ? p.cardDetailsCheckout
                                            : selectedPayMethod === "sepa_debit"
                                              ? hasActiveMandate
                                                ? language === "de"
                                                  ? "Miete wird automatisch eingezogen."
                                                  : "Rent is debited automatically."
                                                : language === "de"
                                                  ? "Einrichtung noch nicht abgeschlossen."
                                                  : "Setup not completed yet."
                                              : language === "de"
                                                ? "Monatliche Überweisung an coliviq."
                                                : "Monthly transfer to coliviq."}
                                        </p>
                                      </div>
                                    </div>
                                    {(selectedPayMethod !== "sepa_debit" ||
                                      hasActiveMandate) && (
                                      <span className="inline-flex w-fit shrink-0 items-center gap-1 rounded-full border border-emerald-200/80 bg-emerald-50/60 px-2.5 py-0.5 text-[11px] font-semibold text-emerald-700 sm:self-center dark:border-emerald-800/40 dark:bg-emerald-950/30 dark:text-emerald-300">
                                        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                                        {p.active}
                                      </span>
                                    )}
                                  </div>
                                  {selectedPayMethod === "card" && (
                                    <div className="mt-3 rounded-md border border-amber-200/70 bg-amber-50/50 px-3 py-2 dark:border-amber-900/40 dark:bg-amber-950/20">
                                      <div className="flex items-start gap-2">
                                        <div className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-200">
                                          <AlertCircle className="h-3.5 w-3.5" />
                                        </div>
                                        <div className="min-w-0">
                                          <p className="text-[10px] font-semibold uppercase tracking-wide text-amber-800 dark:text-amber-200">
                                            {p.stripeAdditionalChargesNoteTitle}
                                          </p>
                                          <p className="mt-0.5 text-[11px] leading-relaxed text-amber-900/90 dark:text-amber-100/90">
                                            {p.stripeAdditionalChargesNoteBody}
                                          </p>
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              )}
                              <Button
                                variant="outline"
                                className="w-full rounded-md border-border"
                                size="sm"
                                onClick={() => setPaymentStep("method")}
                              >
                                {p.managePaymentMethod}
                              </Button>
                            </div>
                          </Card>
                        </section>
                      </aside>
                    </div>

                    <section
                      className="space-y-4"
                      aria-labelledby="payments-rent-deposit"
                    >
                      <h3
                        id="payments-rent-deposit"
                        className="flex items-center gap-2.5 text-left text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground"
                      >
                        <span
                          className="h-1.5 w-1.5 shrink-0 rounded-full bg-primary ring-4 ring-primary/15 dark:ring-primary/25"
                          aria-hidden
                        />
                        {p.paymentsSectionDetails}
                      </h3>
                      <div className="grid w-full min-w-0 grid-cols-1 gap-8 sm:grid-cols-2 sm:items-stretch lg:gap-10">
                        <Card className="flex min-w-0 flex-col overflow-hidden rounded-2xl border border-border/80 bg-card shadow-[0_2px_8px_-2px_rgba(0,0,0,0.06)] transition-shadow duration-300 hover:shadow-[0_4px_14px_-4px_rgba(0,0,0,0.08)]">
                          <div className="flex items-center justify-between gap-3 border-b border-border/80 bg-gradient-to-r from-primary/[0.06] via-muted/15 to-muted/5 px-5 py-4 sm:px-6 dark:from-primary/[0.08]">
                            <h2 className="text-base font-semibold tracking-tight text-foreground">
                              {p.rent}
                            </h2>
                            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/15 ring-1 ring-primary/20">
                              <Euro className="h-5 w-5 text-primary" />
                            </div>
                          </div>
                          <div className="flex flex-1 flex-col divide-y divide-border/70">
                            <div className="flex items-baseline justify-between gap-4 px-5 py-4 sm:px-6">
                              <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                                {p.monthlyRentUpper}
                              </span>
                              <span className="text-right text-xl font-bold tabular-nums sm:text-2xl">
                                <span className="text-primary">€</span>
                                <span className="text-foreground">
                                  {Number(
                                    displayContractMonthlyRentEUR || 0
                                  ).toLocaleString("de-DE", {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  })}
                                </span>
                              </span>
                            </div>
                            {isPartialFirstMonthRent && fullMonthlyRent > 0 && (
                              <div className="px-5 py-4 sm:px-6">
                                <div className="rounded-lg border border-blue-200/60 bg-blue-50/40 px-3 py-2.5 dark:border-blue-800/30 dark:bg-blue-950/20">
                                  <p className="flex items-center gap-1.5 text-xs font-semibold text-blue-700 dark:text-blue-300">
                                    <Receipt className="h-3 w-3" />
                                    {p.proRataBadge}
                                  </p>
                                  <p className="mt-1 text-xs leading-snug text-blue-600/70 dark:text-blue-300/60">
                                    {p.proRataFirstMonthNote(
                                      `€${Number(fullMonthlyRent).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                                    )}
                                  </p>
                                </div>
                              </div>
                            )}

                            {financialsBooking?.rooms && (
                              <div className="flex flex-col gap-1 px-5 py-4 sm:flex-row sm:items-baseline sm:justify-between sm:gap-6 sm:px-6">
                                <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                                  {p.room}
                                </span>
                                <span className="text-right text-sm font-medium leading-snug text-foreground sm:max-w-[14rem]">
                                  {[
                                    financialsBooking.rooms?.properties?.title,
                                    financialsBooking.rooms?.title,
                                  ]
                                    .filter(Boolean)
                                    .join(" · ") || "—"}
                                </span>
                              </div>
                            )}

                            <div className="px-5 py-4 sm:px-6">
                              <div className="flex flex-col gap-3">
                                <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                                  {p.monthsOfRentPaid}
                                </span>
                                {contractMonths != null ? (
                                  <>
                                    <Progress
                                      value={
                                        contractMonths > 0
                                          ? Math.round(
                                              (paidMonths / contractMonths) *
                                                100
                                            )
                                          : 0
                                      }
                                      className="h-2 w-full rounded-full bg-muted [&>div]:rounded-full [&>div]:bg-primary [&>div]:transition-all [&>div]:duration-500"
                                    />
                                    <div className="flex flex-wrap items-center gap-2 pt-1">
                                      <span className="inline-flex h-9 min-w-[2.25rem] items-center justify-center rounded-full bg-emerald-100 px-2 text-sm font-bold tabular-nums text-emerald-900 dark:bg-emerald-950/50 dark:text-emerald-100">
                                        {paidMonths}
                                      </span>
                                      <span className="text-sm font-medium text-muted-foreground">
                                        /
                                      </span>
                                      <span className="text-xl font-bold tabular-nums text-foreground">
                                        {contractMonths}
                                      </span>
                                      <span className="text-sm text-muted-foreground">
                                        {p.months}
                                      </span>
                                    </div>
                                  </>
                                ) : (
                                  <span className="text-sm font-semibold tabular-nums text-foreground">
                                    {paidMonths} {p.paid}
                                  </span>
                                )}
                              </div>
                            </div>

                            <div className="space-y-2 px-5 py-4 sm:px-6">
                              <div className="flex items-baseline justify-between gap-4">
                                <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                                  {p.nextPayment}
                                </span>
                                <span className="text-right text-sm font-semibold text-foreground">
                                  {nextPaymentDate.toLocaleDateString(
                                    undefined,
                                    { month: "long", year: "numeric" }
                                  )}{" "}
                                  · {p.rentLabel}
                                </span>
                              </div>
                              <p className="flex items-center justify-end gap-2 text-right text-xs text-muted-foreground">
                                <Calendar className="h-3.5 w-3.5 shrink-0 opacity-60" />
                                {p.dueOn} {nextPaymentDueLabel}
                              </p>
                            </div>
                          </div>
                        </Card>

                        {/* Deposit status card */}
                        <Card className="flex min-w-0 flex-col overflow-hidden rounded-2xl border border-border/80 bg-card shadow-[0_2px_8px_-2px_rgba(0,0,0,0.06)] transition-shadow duration-300 hover:shadow-[0_4px_14px_-4px_rgba(0,0,0,0.08)]">
                          <div className="border-b border-border/80 bg-gradient-to-r from-muted/25 to-muted/5 px-5 py-4 sm:px-6">
                            <div className="flex items-start justify-between gap-3">
                              <div>
                                <h2 className="text-base font-semibold tracking-tight text-foreground">
                                  {p.depositStatus}
                                </h2>
                                <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                                  {p.oneTimeDepositUpdates}
                                </p>
                              </div>
                              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/15 ring-1 ring-primary/20">
                                <Euro className="h-5 w-5 text-primary" />
                              </div>
                            </div>
                          </div>
                          <div className="flex flex-1 flex-col divide-y divide-border/70">
                            <div className="flex flex-col gap-3 px-5 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
                              <div className="min-w-0">
                                <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                                  {p.securityDeposit}
                                </p>
                                <p className="mt-1 text-2xl font-bold tabular-nums tracking-tight">
                                  <span className="text-primary">€</span>
                                  <span className="text-foreground">
                                    {Number(depositAmount || 0).toLocaleString(
                                      "de-DE",
                                      {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2,
                                      }
                                    )}
                                  </span>
                                </p>
                              </div>
                              {hasPaidDeposit ? (
                                <span className="inline-flex w-fit shrink-0 items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700 dark:border-emerald-800/50 dark:bg-emerald-950/60 dark:text-emerald-300">
                                  <CheckCircle2 className="h-3.5 w-3.5" />
                                  Paid
                                </span>
                              ) : (booking?.contract_status === "approved" ||
                                  booking?.contract_status === "signed") &&
                                depositAmount ? (
                                <span className="inline-flex w-fit shrink-0 items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs font-semibold text-amber-700 dark:border-amber-800/50 dark:bg-amber-950/50 dark:text-amber-300">
                                  <AlertCircle className="h-3.5 w-3.5" />
                                  Due
                                </span>
                              ) : (
                                <span className="inline-flex w-fit shrink-0 items-center gap-1.5 rounded-full border border-border bg-muted px-3 py-1.5 text-xs font-semibold text-muted-foreground">
                                  <Clock className="h-3.5 w-3.5" />
                                  Pending
                                </span>
                              )}
                            </div>

                            <div className="space-y-4 px-5 py-4 text-sm sm:px-6">
                              <div className="flex items-baseline justify-between gap-4">
                                <span className="text-xs font-medium text-muted-foreground">
                                  {p.expected}
                                </span>
                                <span className="text-right font-semibold tabular-nums">
                                  <span className="text-primary">€</span>
                                  <span className="text-foreground">
                                    {Number(depositAmount || 0).toLocaleString(
                                      "de-DE",
                                      {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2,
                                      }
                                    )}
                                  </span>
                                </span>
                              </div>
                              {totalDepositPaidCents > 0 ? (
                                <div className="flex flex-col gap-1 sm:flex-row sm:items-baseline sm:justify-between sm:gap-4">
                                  <span className="text-xs font-medium text-muted-foreground">
                                    {p.amountPaid}
                                  </span>
                                  <div className="text-right">
                                    <span className="font-semibold tabular-nums text-foreground">
                                      €
                                      {(
                                        totalDepositPaidCents / 100
                                      ).toLocaleString("de-DE", {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2,
                                      })}
                                    </span>
                                    {depositPayment?.paid_at && (
                                      <p className="mt-0.5 text-xs text-muted-foreground">
                                        {p.paidOn}{" "}
                                        {new Date(
                                          depositPayment.paid_at
                                        ).toLocaleDateString(undefined, {
                                          day: "numeric",
                                          month: "short",
                                          year: "numeric",
                                        })}
                                      </p>
                                    )}
                                  </div>
                                </div>
                              ) : (
                                <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
                                  <span className="text-xs font-medium text-muted-foreground">
                                    {p.depositActivity}
                                  </span>
                                  <span className="max-w-[15rem] text-right text-xs text-muted-foreground">
                                    {booking?.contract_status === "approved" ||
                                    booking?.contract_status === "signed"
                                      ? p.dueWhenSignedPayBelow
                                      : p.noDepositRecorded}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>

                          <div className="border-t border-border bg-muted/15 px-5 py-5 sm:px-6">
                            {(() => {
                              const depositPaymentRow = (payments || []).find(
                                (pm: any) =>
                                  Number(pm.booking_id) === bookingIdNum &&
                                  pm.type === "deposit"
                              );
                              const depositProof = depositPaymentRow?.id
                                ? paymentProofsByPaymentId?.[
                                    depositPaymentRow.id
                                  ]
                                : null;
                              return depositProof?.rejectionReasonToShow ? (
                                <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
                                  <p className="font-medium">
                                    {p.rejectionReason}
                                  </p>
                                  <p className="mt-1">
                                    {depositProof.rejectionReasonToShow}
                                  </p>
                                  <p className="mt-1.5 text-xs text-red-700">
                                    {language === "de"
                                      ? "Bitte laden Sie einen neuen Zahlungsnachweis über „Per Überweisung zahlen“ hoch."
                                      : "Please upload a new payment proof using Pay by bank transfer."}
                                  </p>
                                </div>
                              ) : null;
                            })()}

                            <p className="mt-4 text-xs leading-relaxed text-muted-foreground">
                              {p.depositHeldEndOfTenancy}
                            </p>
                            <div className="mt-4 flex flex-col gap-3">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-muted-foreground hover:text-foreground justify-start w-fit"
                                onClick={() => {
                                  queryClient.invalidateQueries({
                                    queryKey: ["payments", user?.id],
                                  });
                                  queryClient.refetchQueries({
                                    queryKey: ["payments", user?.id],
                                  });
                                  toast.success(p.paymentStatusRefreshed);
                                }}
                              >
                                <RefreshCw className="mr-2 h-4 w-4" />
                                {p.refreshPaymentStatus}
                              </Button>
                              {hasPaidDeposit && depositAmount ? (
                                <Button
                                  variant="outline"
                                  size="lg"
                                  className="w-full"
                                  onClick={() => setPaymentStep("history")}
                                >
                                  {p.viewDetails}
                                </Button>
                              ) : !hasPaidDeposit && depositAmount ? (
                                <>
                                  <div className="flex flex-col gap-3 pt-2 md:flex-row md:items-stretch">
                                    {stripeCardEnabled && (
                                      <Button
                                        className="w-full min-w-0 bg-primary font-semibold text-primary-foreground shadow-md transition-all hover:bg-primary/90 hover:shadow-lg md:flex-1"
                                        size="lg"
                                        disabled={isProcessingDepositPayment}
                                        onClick={async () => {
                                          try {
                                            setIsProcessingDepositPayment(true);
                                            if (!user?.id) {
                                              toast.error(
                                                t(
                                                  "dashboard.loginToPayDeposit",
                                                  "You need to be logged in to pay your deposit."
                                                )
                                              );
                                              return;
                                            }
                                            const bookingId = booking?.id
                                              ? Number(booking.id)
                                              : null;
                                            if (!bookingId) {
                                              toast.error(
                                                t(
                                                  "dashboard.missingBooking",
                                                  "Missing booking."
                                                )
                                              );
                                              return;
                                            }
                                            const amountCents = Math.round(
                                              Number(depositAmount) * 100
                                            );
                                            const { data: existing } =
                                              await supabase
                                                .from("payments")
                                                .select(
                                                  "id, amount_cents, due_date, type"
                                                )
                                                .eq("user_id", user.id)
                                                .eq("booking_id", bookingId)
                                                .eq("type", "deposit")
                                                .maybeSingle();
                                            let payment = existing ?? null;
                                            if (!payment) {
                                              const {
                                                data: created,
                                                error: createErr,
                                              } = await supabase
                                                .from("payments")
                                                .insert(
                                                  operatorStamped<"payments">({
                                                    user_id: user.id,
                                                    booking_id: bookingId,
                                                    type: "deposit",
                                                    amount_cents: amountCents,
                                                    currency: "eur",
                                                    status: "pending",
                                                    due_date:
                                                      financialsBooking?.move_in_date ||
                                                      financialsBooking?.start_date ||
                                                      new Date()
                                                        .toISOString()
                                                        .slice(0, 10),
                                                    description:
                                                      "Security deposit",
                                                  })
                                                )
                                                .select()
                                                .single();
                                              if (createErr || !created) {
                                                toast.error(
                                                  createErr?.message ||
                                                    t(
                                                      "dashboard.couldNotCreateDepositPayment",
                                                      "Could not create deposit payment."
                                                    )
                                                );
                                                return;
                                              }
                                              payment = created;
                                            }
                                            const depositCentsForFee2 = Number(
                                              payment.amount_cents ??
                                                amountCents
                                            );
                                            const feeOk2 =
                                              await confirmCardFee(
                                                depositCentsForFee2
                                              );
                                            if (!feeOk2) return;
                                            const { data, error } =
                                              await supabase.functions.invoke(
                                                "create-card-checkout",
                                                {
                                                  body: {
                                                    bookingId,
                                                    paymentId: payment.id,
                                                    dueDate: payment.due_date,
                                                    amountCents:
                                                      depositCentsForFee2,
                                                    currency: "eur",
                                                    type: "deposit",
                                                    description:
                                                      "Security deposit",
                                                    successUrl: `${window.location.origin}/dashboard?tab=payments&payment=success`,
                                                    cancelUrl: `${window.location.origin}/dashboard?tab=payments&payment=canceled`,
                                                  },
                                                }
                                              );
                                            if (error) {
                                              toast.error(
                                                error.message ||
                                                  t(
                                                    "dashboard.failedToStartDepositPayment",
                                                    "Failed to start deposit payment."
                                                  )
                                              );
                                              return;
                                            }
                                            const checkoutUrl = (data as any)
                                              ?.checkoutUrl;
                                            if (!checkoutUrl) {
                                              toast.error(
                                                t(
                                                  "dashboard.unableToStartCheckout",
                                                  "Unable to start Stripe Checkout session."
                                                )
                                              );
                                              return;
                                            }
                                            window.location.href = checkoutUrl;
                                          } finally {
                                            setIsProcessingDepositPayment(
                                              false
                                            );
                                          }
                                        }}
                                      >
                                        {isProcessingDepositPayment
                                          ? language === "de"
                                            ? "Weiterleitung…"
                                            : "Redirecting…"
                                          : p.payDeposit}
                                        <ArrowRight className="ml-2 w-4 h-4" />
                                      </Button>
                                    )}
                                    {bankTransferDetails && (
                                      <Button
                                        variant="outline"
                                        size="lg"
                                        className="w-full min-w-0 md:flex-1"
                                        disabled={
                                          uploadPaymentProofMutation.isPending
                                        }
                                        onClick={async () => {
                                          if (
                                            !user?.id ||
                                            !financialsBooking?.id ||
                                            !depositAmount
                                          )
                                            return;
                                          const bookingId = Number(
                                            financialsBooking.id
                                          );
                                          const amountCents = Math.round(
                                            Number(depositAmount) * 100
                                          );
                                          const { data: existing } =
                                            await supabase
                                              .from("payments")
                                              .select(
                                                "id, amount_cents, due_date, type"
                                              )
                                              .eq("user_id", user.id)
                                              .eq("booking_id", bookingId)
                                              .eq("type", "deposit")
                                              .maybeSingle();
                                          let payment = existing ?? null;
                                          if (!payment) {
                                            const { data: created, error } =
                                              await supabase
                                                .from("payments")
                                                .insert(
                                                  operatorStamped<"payments">({
                                                    user_id: user.id,
                                                    booking_id: bookingId,
                                                    type: "deposit",
                                                    amount_cents: amountCents,
                                                    currency: "eur",
                                                    status: "pending",
                                                    due_date:
                                                      financialsBooking?.move_in_date ||
                                                      financialsBooking?.start_date ||
                                                      new Date()
                                                        .toISOString()
                                                        .slice(0, 10),
                                                    description:
                                                      language === "de"
                                                        ? "Kaution (Überweisung)"
                                                        : "Security deposit (bank transfer)",
                                                  })
                                                )
                                                .select()
                                                .single();
                                            if (error || !created) {
                                              toast.error(
                                                language === "de"
                                                  ? "Zahlung konnte nicht erstellt werden."
                                                  : "Could not create payment."
                                              );
                                              return;
                                            }
                                            payment = created;
                                          }
                                          setBankTransferPayment(payment);
                                          setPaymentProofFiles([]);
                                          setPaymentStep(
                                            "bank_transfer_upload"
                                          );
                                        }}
                                      >
                                        <Landmark className="mr-2 w-4 h-4" />
                                        {p.payByBankTransfer}
                                      </Button>
                                    )}
                                  </div>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    className="w-full justify-center text-muted-foreground hover:text-foreground"
                                    onClick={() => setPaymentStep("history")}
                                  >
                                    {p.viewDetails}
                                  </Button>
                                  {!(
                                    booking?.contract_status === "approved" ||
                                    booking?.contract_status === "signed"
                                  ) && (
                                    <p className="text-xs text-muted-foreground">
                                      {p.depositPayableWhenSigned}
                                    </p>
                                  )}
                                </>
                              ) : null}
                            </div>
                          </div>
                        </Card>
                      </div>
                    </section>
                  </div>
                )}

                {/* Rent breakdown screen */}
                {paymentStep === "breakdown" && (
                  <div className="space-y-6">
                    {booking ? (
                      <Card className="space-y-6 p-4 sm:p-6 md:p-8">
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
                          <div className="min-w-0">
                            <h2 className="mb-1 text-xl font-bold text-foreground sm:text-2xl">
                              {p.rentBreakdown}
                            </h2>
                            <p className="text-sm text-muted-foreground">
                              {p.hereHowRentStructured}
                            </p>
                          </div>
                          <button
                            type="button"
                            className="shrink-0 self-start text-xs text-muted-foreground underline-offset-2 hover:underline sm:self-auto"
                            onClick={() => setPaymentStep("overview")}
                          >
                            {p.backToOverview}
                          </button>
                        </div>
                        {booking.room_number || booking.property_address ? (
                          <div className="rounded-lg border border-border bg-muted/40 p-4 text-sm">
                            <div className="flex items-center justify-between mb-2">
                              <div>
                                {booking.room_number && (
                                  <p className="font-semibold text-foreground">
                                    Room {booking.room_number}
                                  </p>
                                )}
                                {booking.property_address && (
                                  <p className="text-xs text-muted-foreground">
                                    {booking.property_address}
                                  </p>
                                )}
                              </div>
                              <Badge variant="secondary">
                                {booking.status === "active"
                                  ? "Active contract"
                                  : booking.status || "Contract"}
                              </Badge>
                            </div>
                          </div>
                        ) : null}

                        <div className="space-y-3 text-sm">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-medium text-foreground">
                                {p.baseRent}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {p.furnishedPrivateRoom}
                              </p>
                            </div>
                            <p className="font-semibold text-foreground">
                              €
                              {Number(
                                rentBreakdownLineItems.base
                              ).toLocaleString("de-DE", {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                            </p>
                          </div>
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-medium text-foreground">
                                {p.utilitiesServices}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {p.electricityWaterWiFi}
                              </p>
                            </div>
                            <p className="font-semibold text-foreground">
                              €
                              {Number(
                                rentBreakdownLineItems.utilities
                              ).toLocaleString("de-DE", {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                            </p>
                          </div>
                          <div className="flex items-center justify-between pt-2 border-t border-dashed border-border">
                            <p className="font-semibold text-foreground">
                              {p.totalMonthlyRent}
                            </p>
                            <p className="text-lg font-bold text-foreground">
                              €
                              {Number(
                                billedAmountForCurrentMonth
                              ).toLocaleString("de-DE", {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                            </p>
                          </div>
                        </div>

                        <div className="rounded-lg border border-border bg-muted/40 p-4 text-xs text-muted-foreground space-y-2">
                          <p className="font-medium text-foreground">
                            {p.depositStatus}
                          </p>
                          <p>
                            {p.depositHeldSeparate(
                              "€" +
                                Number(depositAmount).toLocaleString("de-DE")
                            )}
                          </p>
                        </div>

                        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 pt-2">
                          <p className="text-xs text-muted-foreground">
                            {p.nextCollectionDate(nextPaymentDueLabel)}
                          </p>
                          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                            <Button
                              variant="outline"
                              className="w-full sm:w-auto"
                              onClick={() => setPaymentStep("confirm")}
                            >
                              {p.reviewAndConfirmPayment}
                            </Button>
                            <Button
                              className="w-full sm:w-auto"
                              onClick={() => setPaymentStep("confirm")}
                            >
                              {p.payNow}
                            </Button>
                          </div>
                        </div>
                      </Card>
                    ) : (
                      <ComingSoon description="This feature will be available soon." />
                    )}
                  </div>
                )}

                {/* Confirm payment & success screen */}
                {(paymentStep === "confirm" || paymentStep === "success") && (
                  <Card className="mt-6 p-6 md:p-8 space-y-6">
                    <button
                      type="button"
                      className="flex items-center gap-2 text-xs text-muted-foreground hover:underline"
                      onClick={() => setPaymentStep("overview")}
                    >
                      <ArrowLeft className="w-3.5 h-3.5" />
                      {p.backToOverview}
                    </button>

                    <div className="space-y-6">
                      {paymentStep === "confirm" && (
                        <>
                          <div className="space-y-1">
                            <div>
                              <h2 className="text-2xl font-bold text-foreground mb-1">
                                {p.confirmPaymentTitle}
                              </h2>
                              <p className="text-sm text-muted-foreground">
                                {p.confirmPaymentSubtitle}
                              </p>
                            </div>
                            {sepaDebitEnabled && hasActiveMandate && (
                              <div className="mt-3 rounded-lg border border-primary/25 bg-primary/5 px-3 py-2.5 text-xs leading-relaxed text-muted-foreground">
                                {p.sepaAutoDebitPayNowNote}
                              </div>
                            )}
                          </div>

                          <div className="space-y-4 mt-4">
                            <div className="rounded-lg border border-border bg-muted/40 p-4 text-sm">
                              <div className="flex items-center justify-between mb-2">
                                <p className="font-medium text-foreground">
                                  {p.firstRentCollection}
                                </p>
                                <p className="flex items-baseline gap-2 font-semibold text-foreground">
                                  {upcomingReferralCreditNote && (
                                    <span className="text-xs font-medium text-muted-foreground line-through">
                                      €
                                      {Number(
                                        upcomingGrossChargeEUR
                                      ).toLocaleString("de-DE", {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2,
                                      })}
                                    </span>
                                  )}
                                  <span>
                                    €
                                    {Number(
                                      upcomingRemainingChargeEUR
                                    ).toLocaleString("de-DE", {
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2,
                                    })}
                                  </span>
                                </p>
                              </div>
                              <p className="text-xs text-muted-foreground">
                                {p.firstRentCollectionDesc}
                              </p>
                              {upcomingReferralCreditNote && (
                                <p className="mt-2 inline-flex items-start gap-1.5 text-xs font-medium leading-snug text-primary">
                                  <Gift className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                                  {upcomingReferralCreditNote}
                                </p>
                              )}
                            </div>

                            <div className="grid gap-3 sm:grid-cols-3 text-xs text-muted-foreground">
                              <div>
                                <p className="text-[11px] uppercase tracking-wide">
                                  {p.collectionDate}
                                </p>
                                <p className="font-medium text-foreground">
                                  {nextPaymentDueLabel}
                                </p>
                              </div>
                              <div>
                                <p className="text-[11px] uppercase tracking-wide">
                                  {p.paymentMethod}
                                </p>
                                <p className="font-medium text-foreground">
                                  {p.cardPaymentStripe}
                                </p>
                              </div>
                              <div>
                                <p className="text-[11px] uppercase tracking-wide">
                                  {p.receiptsHistory}
                                </p>
                                <p className="font-medium text-foreground">
                                  {p.availableInPaymentHistory}
                                </p>
                              </div>
                            </div>
                          </div>

                          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 pt-4">
                            <div className="space-y-2">
                              <p className="text-xs text-muted-foreground">
                                {p.stripeProcessedStatement}
                              </p>
                              <div className="rounded-md border border-amber-200/70 bg-amber-50/50 px-3 py-2 dark:border-amber-900/40 dark:bg-amber-950/20">
                                <div className="flex items-start gap-2">
                                  <div className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-200">
                                    <AlertCircle className="h-3.5 w-3.5" />
                                  </div>
                                  <div className="min-w-0">
                                    <p className="text-[10px] font-semibold uppercase tracking-wide text-amber-800 dark:text-amber-200">
                                      {p.stripeAdditionalChargesNoteTitle}
                                    </p>
                                    <p className="mt-0.5 text-xs leading-relaxed text-amber-900/90 dark:text-amber-100/90">
                                      {p.stripeAdditionalChargesNoteBody}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                              <Button
                                variant="outline"
                                className="w-full sm:w-auto"
                                onClick={() => setPaymentStep("overview")}
                              >
                                {p.cancel}
                              </Button>
                              <Button
                                className="w-full sm:w-auto"
                                disabled={isProcessingPayment}
                                onClick={async () => {
                                  try {
                                    setIsProcessingPayment(true);
                                    if (!user?.id) {
                                      toast.error(
                                        t(
                                          "dashboard.loginToPayRent",
                                          "You need to be logged in to pay your rent."
                                        )
                                      );
                                      return;
                                    }
                                    // Feedback 8: an active mandate means this
                                    // confirm settles by SEPA debit — never by
                                    // routing a mandated tenant into the card
                                    // checkout.
                                    if (sepaDebitEnabled && hasActiveMandate) {
                                      await payNowViaSepa();
                                      setPaymentStep("overview");
                                      return;
                                    }
                                    // Ensure booking_id is passed if booking exists
                                    const bookingId = booking?.id
                                      ? Number(booking.id)
                                      : null;
                                    if (!bookingId) {
                                      toast.error(
                                        t(
                                          "dashboard.missingBooking",
                                          "Missing booking."
                                        )
                                      );
                                      return;
                                    }

                                    // Ensure we are paying a specific existing rent row (month = due_date).
                                    // Upsert by (booking, type='rent', month-of-due_date) so a sibling paid/pending
                                    // row for the same month is reused instead of duplicated.
                                    let targetRent = upcomingPayment;
                                    if (!targetRent?.id) {
                                      const dueIso = nextPaymentDate
                                        ? new Date(nextPaymentDate)
                                            .toISOString()
                                            .slice(0, 10)
                                        : new Date().toISOString().slice(0, 10);
                                      // Same amount already shown on the card for this
                                      // no-row fallback (`upcomingRemainingChargeEUR`) — never
                                      // charge a different figure than what was displayed.
                                      const amountCents = Math.round(
                                        Number(upcomingRemainingChargeEUR) * 100
                                      );
                                      const {
                                        payment: upserted,
                                        error: createErr,
                                      } = await ensureRentPaymentForMonth({
                                        userId: user.id,
                                        bookingId,
                                        dueDate: dueIso,
                                        amountCents,
                                        currency: "eur",
                                        status: "pending",
                                        description: "Monthly rent payment",
                                      });
                                      if (createErr || !upserted) {
                                        toast.error(
                                          createErr?.message ||
                                            t(
                                              "dashboard.couldNotCreateRentPayment",
                                              "Could not create rent payment."
                                            )
                                        );
                                        return;
                                      }
                                      targetRent = upserted as any;
                                    }

                                    const checkoutAmountCents =
                                      targetRent?.amount_cents != null
                                        ? // COL-417: charge what's actually still open, not
                                          // the full month, once a receipt has landed.
                                          remainingRentCents(targetRent)
                                        : Math.round(
                                            Number(upcomingRemainingChargeEUR) *
                                              100
                                          );
                                    console.log(
                                      "Creating Stripe checkout with:",
                                      {
                                        bookingId,
                                        booking: booking?.id,
                                        amountCents: checkoutAmountCents,
                                      }
                                    );

                                    if (
                                      !checkoutAmountCents ||
                                      checkoutAmountCents <= 0
                                    ) {
                                      toast.error(
                                        t(
                                          "dashboard.payment.invalidRentAmount",
                                          "Invalid rent amount."
                                        )
                                      );
                                      return;
                                    }

                                    const feeOk3 =
                                      await confirmCardFee(checkoutAmountCents);
                                    if (!feeOk3) return;

                                    const { data, error } =
                                      await supabase.functions.invoke(
                                        "create-card-checkout",
                                        {
                                          body: {
                                            bookingId: bookingId,
                                            paymentId: targetRent?.id ?? null,
                                            dueDate:
                                              targetRent?.due_date ?? null,
                                            amountCents: checkoutAmountCents,
                                            currency: "eur",
                                            type: "rent",
                                            description: "Monthly rent payment",
                                            successUrl: `${window.location.origin}/dashboard?payment=success`,
                                            cancelUrl: `${window.location.origin}/dashboard?payment=canceled`,
                                          },
                                        }
                                      );
                                    if (error) {
                                      console.error(
                                        "Stripe checkout error:",
                                        error
                                      );
                                      toast.error(
                                        error.message ||
                                          t(
                                            "dashboard.failedToStartPayment",
                                            "Failed to start payment."
                                          )
                                      );
                                      return;
                                    }
                                    const checkoutUrl = (data as any)
                                      ?.checkoutUrl;
                                    if (!checkoutUrl) {
                                      toast.error(
                                        t(
                                          "dashboard.unableToStartCheckout",
                                          "Unable to start Stripe Checkout session."
                                        )
                                      );
                                      return;
                                    }
                                    window.location.href = checkoutUrl;
                                  } finally {
                                    setIsProcessingPayment(false);
                                  }
                                }}
                              >
                                {isProcessingPayment
                                  ? language === "de"
                                    ? "Weiterleitung…"
                                    : "Redirecting…"
                                  : p.confirmPayment}
                              </Button>
                            </div>
                          </div>
                        </>
                      )}

                      {paymentStep === "success" && (
                        <div className="space-y-6">
                          <div className="flex items-start gap-4">
                            <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-emerald-100 dark:bg-emerald-900/40">
                              <CheckCircle2 className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                            </div>
                            <div>
                              <h2 className="text-2xl font-bold text-foreground mb-1">
                                {p.paymentSuccessful}
                              </h2>
                              <p className="text-sm text-muted-foreground">
                                Your rent payment has been processed
                                successfully. A confirmation email and receipt
                                will be shared with you shortly.
                              </p>
                            </div>
                          </div>

                          <div className="grid gap-3 sm:grid-cols-3 text-xs text-muted-foreground">
                            <div>
                              <p className="text-[11px] uppercase tracking-wide">
                                What happens next
                              </p>
                              <p className="font-medium text-foreground">
                                The amount is charged to your selected card.
                                Future payments can be managed from your Payment
                                Overview.
                              </p>
                            </div>
                            <div>
                              <p className="text-[11px] uppercase tracking-wide">
                                Receipt
                              </p>
                              <p className="font-medium text-foreground">
                                A digital receipt will be available in your
                                payment history.
                              </p>
                            </div>
                            <div>
                              <p className="text-[11px] uppercase tracking-wide">
                                Changes
                              </p>
                              <p className="font-medium text-foreground">
                                You can update your payment method or review
                                history at any time from the dashboard.
                              </p>
                            </div>
                          </div>

                          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 pt-4">
                            <p className="text-xs text-muted-foreground">
                              Need help? Reach out to our support team if you
                              have questions about your payments.
                            </p>
                            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                              <Button
                                variant="outline"
                                className="w-full sm:w-auto"
                                onClick={() => setPaymentStep("overview")}
                              >
                                Back to payments overview
                              </Button>
                              <Button
                                className="w-full sm:w-auto"
                                onClick={() => {
                                  setPaymentStep("overview");
                                  setActiveTab("payments");
                                }}
                              >
                                View payment history
                              </Button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </Card>
                )}

                {/* Bank transfer: mandatory payment proof upload with progress */}
                {paymentStep === "bank_transfer_upload" &&
                  bankTransferPayment && (
                    <Card className="mt-6 p-6 md:p-8 space-y-6">
                      <button
                        type="button"
                        className="flex items-center gap-2 text-xs text-muted-foreground hover:underline"
                        onClick={() => {
                          setPaymentStep("overview");
                          setBankTransferPayment(null);
                          setPaymentProofFiles([]);
                          setPaymentProofUploadProgress(0);
                        }}
                      >
                        <ArrowLeft className="w-3.5 h-3.5" />
                        {p.backToOverview}
                      </button>
                      <div className="space-y-1">
                        <h2 className="text-2xl font-bold text-foreground">
                          {p.uploadPaymentProof}
                        </h2>
                        <p className="text-sm text-muted-foreground">
                          {p.uploadPaymentProofMandatory}
                        </p>
                      </div>
                      <div className="rounded-lg border border-border bg-muted/40 p-4 space-y-2 text-sm">
                        <p className="font-medium text-foreground">
                          {/* COL-472: a part-paid rent month asks for what is
                              still open, the same figure as the tile. */}
                          {eurLabel(
                            bankTransferPayment.type === "rent"
                              ? resolveRentSettlement(
                                  bankTransferPayment,
                                  () => null
                                ).remainingCents
                              : Number(bankTransferPayment.amount_cents) || 0
                          )}{" "}
                          ·{" "}
                          {bankTransferPayment.type === "deposit"
                            ? p.deposit
                            : p.rentLabel}{" "}
                          · {p.dueOn}{" "}
                          {bankTransferPayment.due_date
                            ? new Date(
                                bankTransferPayment.due_date
                              ).toLocaleDateString(undefined, {
                                day: "numeric",
                                month: "long",
                                year: "numeric",
                              })
                            : "—"}
                        </p>
                        {bankTransferPayment.type === "rent" &&
                          String(bankTransferPayment.id) ===
                            String(upcomingPayment?.id) &&
                          // COL-472: fees this row carries, or — if this row
                          // doesn't carry them (e.g. an older unpaid month is
                          // shown while the pool sits on a later, dunning
                          // month, or the rent is settled and only fees
                          // remain) — the standalone pool, so the bank
                          // transfer instructions never drop fees the tile
                          // above just told the tenant to pay this way.
                          (upcomingOwed.feeCents > 0
                            ? upcomingOwed.feeCents
                            : upcomingStandaloneFeeCents) > 0 && (
                            <p className="text-xs font-medium text-amber-800 dark:text-amber-300">
                              {p.bankTransferFeesNote(
                                eurLabel(
                                  upcomingOwed.feeCents > 0
                                    ? upcomingOwed.feeCents
                                    : upcomingStandaloneFeeCents
                                ),
                                eurLabel(
                                  resolveRentSettlement(
                                    bankTransferPayment,
                                    () => null
                                  ).remainingCents +
                                    (upcomingOwed.feeCents > 0
                                      ? upcomingOwed.feeCents
                                      : upcomingStandaloneFeeCents)
                                )
                              )}
                            </p>
                          )}
                        <p className="text-xs text-muted-foreground">
                          {language === "de"
                            ? "Überweisung an: "
                            : "Transfer to: "}
                          {bankTransferInfoForDisplay?.accountHolder} · IBAN{" "}
                          {bankTransferInfoForDisplay
                            ? formatIbanForDisplay(
                                bankTransferInfoForDisplay.iban
                              )
                            : ""}
                        </p>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-sm font-medium">
                          {p.uploadStatement}
                        </Label>
                        <p className="text-xs text-muted-foreground">
                          {language === "de"
                            ? "Sie können mehrere Dateien auswählen (z. B. separate Nachweise für Miete und Kaution)."
                            : "You can select multiple files (e.g. separate proofs for rent and deposit)."}
                        </p>
                        <div className="flex flex-col gap-2">
                          <input
                            id="payment-proof-upload"
                            type="file"
                            multiple
                            accept=".pdf,image/jpeg,image/jpg,image/png,image/webp,application/pdf"
                            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm file:border-0 file:bg-transparent file:text-sm file:font-medium"
                            onChange={(e) => {
                              const list = e.target.files
                                ? Array.from(e.target.files)
                                : [];
                              setPaymentProofFiles(list);
                            }}
                          />
                          {paymentProofFiles.length > 0 && (
                            <p className="text-xs text-muted-foreground">
                              {paymentProofFiles.length}{" "}
                              {language === "de"
                                ? "Datei(en) ausgewählt"
                                : "file(s) selected"}
                            </p>
                          )}
                          {(paymentProofUploading ||
                            uploadPaymentProofMutation.isPending) && (
                            <div className="space-y-2">
                              <Progress
                                value={paymentProofUploadProgress}
                                className="h-2"
                              />
                              <p className="text-xs text-muted-foreground">
                                {p.uploading}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-col sm:flex-row gap-2">
                        <Button
                          variant="outline"
                          onClick={() => {
                            setPaymentStep("overview");
                            setBankTransferPayment(null);
                            setPaymentProofFiles([]);
                          }}
                        >
                          {p.cancel}
                        </Button>
                        <Button
                          disabled={
                            paymentProofFiles.length === 0 ||
                            uploadPaymentProofMutation.isPending ||
                            paymentProofUploading
                          }
                          onClick={() => {
                            if (
                              paymentProofFiles.length === 0 ||
                              !bankTransferPayment?.id
                            )
                              return;
                            setPaymentProofUploading(true);
                            uploadPaymentProofMutation.mutate(
                              {
                                paymentId: bankTransferPayment.id,
                                files: paymentProofFiles,
                              },
                              {
                                onSettled: () =>
                                  setPaymentProofUploading(false),
                              }
                            );
                          }}
                        >
                          {uploadPaymentProofMutation.isPending ||
                          paymentProofUploading ? (
                            <>
                              <Loader2 className="mr-2 w-4 h-4 animate-spin" />
                              {p.uploading}
                            </>
                          ) : (
                            <>
                              <Upload className="mr-2 w-4 h-4" />
                              {p.uploadPaymentProof}{" "}
                              {paymentProofFiles.length > 0
                                ? `(${paymentProofFiles.length})`
                                : ""}
                            </>
                          )}
                        </Button>
                      </div>
                    </Card>
                  )}

                {/* Payment method picker (feedback 1) — one chosen method,
                    changeable anytime. Direct debit carries its setup flow (or
                    Active tag) inline; bank transfer reveals the account
                    details under the option; card explains Stripe Checkout. */}
                {paymentStep === "method" && (
                  <Card className="space-y-5 p-4 sm:space-y-6 sm:p-6 md:p-8">
                    <div className="space-y-1">
                      <h2 className="text-xl font-bold text-foreground sm:text-2xl">
                        {language === "de"
                          ? "Wie möchten Sie Ihre Miete zahlen?"
                          : "How would you like to pay rent?"}
                      </h2>
                      <p className="text-sm text-muted-foreground max-w-xl">
                        {language === "de"
                          ? "Wählen Sie eine Methode. Sie können sie jederzeit ändern."
                          : "Choose one method. You can change it anytime."}
                      </p>
                    </div>
                    <div
                      role="radiogroup"
                      aria-label={
                        language === "de" ? "Zahlungsmethode" : "Payment method"
                      }
                      className="space-y-3"
                    >
                      {sepaDebitEnabled && (
                        <div
                          role="radio"
                          aria-checked={selectedPayMethod === "sepa_debit"}
                          tabIndex={0}
                          onClick={() => choosePayMethod("sepa_debit")}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ")
                              choosePayMethod("sepa_debit");
                          }}
                          className={`cursor-pointer rounded-xl border p-4 transition-colors sm:p-5 ${
                            selectedPayMethod === "sepa_debit"
                              ? "border-primary bg-primary/[0.04]"
                              : "border-border bg-background hover:border-primary/40"
                          }`}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex min-w-0 items-start gap-3">
                              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-muted/60">
                                <Landmark className="h-5 w-5 text-muted-foreground" />
                              </div>
                              <div className="min-w-0">
                                <p className="flex flex-wrap items-center gap-2 text-sm font-semibold text-foreground">
                                  {language === "de"
                                    ? "Lastschrift"
                                    : "Direct debit"}
                                  {hasActiveMandate && (
                                    <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200/80 bg-emerald-50/60 px-2 py-0.5 text-[11px] font-semibold text-emerald-700 dark:border-emerald-800/40 dark:bg-emerald-950/30 dark:text-emerald-300">
                                      <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                                      {p.active}
                                    </span>
                                  )}
                                </p>
                                <p className="mt-0.5 text-xs text-muted-foreground">
                                  {language === "de"
                                    ? "Einmal einrichten — Ihre Miete wird jeden Monat automatisch eingezogen."
                                    : "Set it up once — your rent is collected automatically each month."}
                                </p>
                              </div>
                            </div>
                            <span
                              aria-hidden
                              className={`mt-1 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 ${
                                selectedPayMethod === "sepa_debit"
                                  ? "border-primary"
                                  : "border-border"
                              }`}
                            >
                              {selectedPayMethod === "sepa_debit" && (
                                <span className="h-2.5 w-2.5 rounded-full bg-primary" />
                              )}
                            </span>
                          </div>
                          {selectedPayMethod === "sepa_debit" &&
                            !hasActiveMandate && (
                              <div className="mt-4 flex flex-col gap-3 rounded-lg bg-muted/40 p-4 sm:flex-row sm:items-center sm:justify-between">
                                <div className="min-w-0">
                                  <p className="text-sm font-semibold text-foreground">
                                    {p.payBySepa}
                                  </p>
                                  <p className="mt-0.5 text-xs text-muted-foreground">
                                    {language === "de"
                                      ? "Verbinden Sie Ihr Bankkonto sicher, um automatische monatliche Zahlungen zu aktivieren."
                                      : "Connect your bank account securely to activate automatic monthly payments."}
                                  </p>
                                </div>
                                <Button
                                  className="w-full shrink-0 sm:w-auto"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSepaMandateOpen({
                                      bookingId: financialsBooking?.id
                                        ? Number(financialsBooking.id)
                                        : null,
                                    });
                                  }}
                                >
                                  {p.payBySepa}
                                </Button>
                              </div>
                            )}
                          {selectedPayMethod === "sepa_debit" &&
                            hasActiveMandate && (
                              <p className="mt-3 text-xs leading-relaxed text-muted-foreground">
                                {p.sepaActiveAutoDebitNote}
                              </p>
                            )}
                          {hasActiveMandate && pendingDdCancelRequest && (
                            <div className="mt-3 rounded-md border border-amber-200/70 bg-amber-50/50 px-3 py-2 text-xs leading-relaxed text-amber-900/90 dark:border-amber-900/40 dark:bg-amber-950/20 dark:text-amber-100/90">
                              {language === "de"
                                ? "Ihre Anfrage, das Lastschriftmandat zu beenden, wird von einem Admin geprüft. Bis zur Entscheidung bleibt die Lastschrift aktiv."
                                : "Your request to end the direct debit mandate is being reviewed by an admin. Direct debit stays active until it's decided."}
                            </div>
                          )}
                        </div>
                      )}
                      {stripeCardEnabled && (
                        <div
                          role="radio"
                          aria-checked={selectedPayMethod === "card"}
                          tabIndex={0}
                          onClick={() => choosePayMethod("card")}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ")
                              choosePayMethod("card");
                          }}
                          className={`cursor-pointer rounded-xl border p-4 transition-colors sm:p-5 ${
                            selectedPayMethod === "card"
                              ? "border-primary bg-primary/[0.04]"
                              : "border-border bg-background hover:border-primary/40"
                          }`}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex min-w-0 items-start gap-3">
                              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-muted/60">
                                <CreditCard className="h-5 w-5 text-muted-foreground" />
                              </div>
                              <div className="min-w-0">
                                <p className="text-sm font-semibold text-foreground">
                                  {language === "de"
                                    ? "Kartenzahlung"
                                    : "Card payment"}
                                </p>
                                <p className="mt-0.5 text-xs text-muted-foreground">
                                  {language === "de"
                                    ? "Zahlen Sie Ihre Miete jeden Monat manuell mit Ihrer Karte."
                                    : "Pay your rent manually each month with your card."}
                                </p>
                              </div>
                            </div>
                            <span
                              aria-hidden
                              className={`mt-1 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 ${
                                selectedPayMethod === "card"
                                  ? "border-primary"
                                  : "border-border"
                              }`}
                            >
                              {selectedPayMethod === "card" && (
                                <span className="h-2.5 w-2.5 rounded-full bg-primary" />
                              )}
                            </span>
                          </div>
                          {selectedPayMethod === "card" && (
                            <p className="mt-3 text-xs leading-relaxed text-muted-foreground">
                              {language === "de"
                                ? "Sie schließen jeden Monat eine sichere Zahlung im Stripe Checkout ab. Ihre Kartendaten werden von Stripe verarbeitet — coliviq speichert keine vollständigen Kartennummern. Um die Karte zu wechseln, zahlen Sie einfach mit der neuen Karte."
                                : "You'll complete a secure payment each month via Stripe Checkout. Your card details are handled by Stripe — coliviq never stores full card numbers. To change cards, simply pay with the new one."}
                            </p>
                          )}
                        </div>
                      )}
                      {bankTransferInfoForDisplay && (
                        <div
                          role="radio"
                          aria-checked={selectedPayMethod === "bank_transfer"}
                          tabIndex={0}
                          onClick={() => choosePayMethod("bank_transfer")}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ")
                              choosePayMethod("bank_transfer");
                          }}
                          className={`cursor-pointer rounded-xl border p-4 transition-colors sm:p-5 ${
                            selectedPayMethod === "bank_transfer"
                              ? "border-primary bg-primary/[0.04]"
                              : "border-border bg-background hover:border-primary/40"
                          }`}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex min-w-0 items-start gap-3">
                              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-muted/60">
                                <Landmark className="h-5 w-5 text-muted-foreground" />
                              </div>
                              <div className="min-w-0">
                                <p className="text-sm font-semibold text-foreground">
                                  {language === "de"
                                    ? "Überweisung"
                                    : "Bank transfer"}
                                </p>
                                <p className="mt-0.5 text-xs text-muted-foreground">
                                  {language === "de"
                                    ? "Überweisen Sie Ihre Miete jeden Monat manuell von Ihrer Bank."
                                    : "Transfer your rent manually each month from your bank."}
                                </p>
                              </div>
                            </div>
                            <span
                              aria-hidden
                              className={`mt-1 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 ${
                                selectedPayMethod === "bank_transfer"
                                  ? "border-primary"
                                  : "border-border"
                              }`}
                            >
                              {selectedPayMethod === "bank_transfer" && (
                                <span className="h-2.5 w-2.5 rounded-full bg-primary" />
                              )}
                            </span>
                          </div>
                          {selectedPayMethod === "bank_transfer" && (
                            <div className="mt-4 space-y-2 rounded-lg bg-muted/40 p-4 text-sm">
                              <p className="flex flex-col gap-0.5 sm:flex-row sm:justify-between sm:gap-4">
                                <span className="text-muted-foreground">
                                  {language === "de"
                                    ? "Kontoinhaber"
                                    : "Account holder"}
                                </span>
                                <span className="font-medium text-foreground tabular-nums sm:text-right">
                                  {bankTransferInfoForDisplay.accountHolder}
                                </span>
                              </p>
                              <p className="flex flex-col gap-0.5 sm:flex-row sm:justify-between sm:gap-4">
                                <span className="text-muted-foreground">
                                  IBAN
                                </span>
                                <span className="break-all font-medium text-foreground tabular-nums sm:max-w-[min(100%,16rem)] sm:text-right md:max-w-none">
                                  {formatIbanForDisplay(
                                    bankTransferInfoForDisplay.iban
                                  )}
                                </span>
                              </p>
                              <p className="flex flex-col gap-0.5 sm:flex-row sm:justify-between sm:gap-4">
                                <span className="text-muted-foreground">
                                  BIC
                                </span>
                                <span className="font-medium text-foreground tabular-nums sm:text-right">
                                  {bankTransferInfoForDisplay.bic}
                                </span>
                              </p>
                              <p className="flex flex-col gap-0.5 sm:flex-row sm:justify-between sm:gap-4">
                                <span className="text-muted-foreground">
                                  Bank
                                </span>
                                <span className="font-medium text-foreground sm:text-right">
                                  {bankTransferInfoForDisplay.bankName}
                                </span>
                              </p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="flex items-start gap-2.5 border-t border-border/60 pt-4 text-xs leading-relaxed text-muted-foreground">
                      <Info className="mt-0.5 h-4 w-4 shrink-0" />
                      <p>
                        {language === "de"
                          ? "Lastschrift läuft nach der Einrichtung automatisch. Karte und Überweisung erfordern jeden Monat eine Zahlung."
                          : "Direct debit is automatic after setup. Card and bank transfer require a payment every month."}
                      </p>
                    </div>
                  </Card>
                )}
                {paymentStep === "history" && (
                  <Card className="relative overflow-hidden space-y-5 border-border/60 bg-card p-4 shadow-sm sm:p-6 md:p-8">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
                      <div className="min-w-0 space-y-1">
                        <h2 className="text-xl font-bold tracking-tight text-foreground sm:text-2xl">
                          {p.paymentHistory}
                        </h2>
                        <p className="text-sm text-muted-foreground">
                          {p.allPastPaymentsHere}
                        </p>
                      </div>
                      <span className="inline-flex w-fit shrink-0 items-center gap-1.5 rounded-full border border-border/60 bg-muted/50 px-3 py-1.5 text-xs font-medium text-muted-foreground">
                        <ListChecks className="h-3.5 w-3.5" />
                        {p.last12Payments}
                      </span>
                    </div>
                    <div className="mt-1 space-y-2.5">
                      {payments && payments.length > 0 ? (
                        payments.map((payment: any) => (
                          <div
                            key={payment.id}
                            className="group flex flex-col gap-3 rounded-xl border border-border/60 bg-background px-4 py-3.5 shadow-sm transition-all hover:border-border hover:shadow-md sm:flex-row sm:items-center sm:justify-between sm:gap-4"
                          >
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-foreground">
                                {payment.due_date
                                  ? new Date(
                                      payment.due_date
                                    ).toLocaleDateString(undefined, {
                                      month: "long",
                                      year: "numeric",
                                    })
                                  : new Date(
                                      payment.created_at
                                    ).toLocaleDateString(undefined, {
                                      month: "long",
                                      year: "numeric",
                                    })}{" "}
                                {formatPaymentType(payment.type)}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                <span className="text-primary font-semibold">
                                  €
                                </span>
                                {Number(
                                  payment.amount_cents / 100
                                ).toLocaleString("de-DE", {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })}{" "}
                                ·{" "}
                                <span className="text-muted-foreground">
                                  Payment ID:
                                </span>{" "}
                                <span className="font-mono">
                                  {String(payment.id).slice(0, 8)}…
                                </span>
                              </p>
                              {(() => {
                                const proof =
                                  paymentProofsByPaymentId?.[payment.id];
                                const isStripe = !!(
                                  payment.stripe_payment_intent_id ||
                                  payment.stripe_charge_id
                                );
                                const isManual = !!proof && !isStripe;
                                const fmtUtc = (
                                  ts: string | null | undefined
                                ) =>
                                  ts
                                    ? new Date(ts)
                                        .toISOString()
                                        .replace("T", " ")
                                        .replace("Z", " UTC")
                                    : "—";

                                if (isStripe) {
                                  return (
                                    <div className="mt-1.5 text-[11px] text-muted-foreground space-y-0.5">
                                      <p>
                                        <span className="font-medium text-foreground/80">
                                          Method:
                                        </span>{" "}
                                        Stripe
                                      </p>
                                      {payment.stripe_payment_intent_id && (
                                        <p>
                                          <span className="font-medium text-foreground/80">
                                            Payment Intent:
                                          </span>{" "}
                                          <span className="font-mono">
                                            {payment.stripe_payment_intent_id}
                                          </span>
                                        </p>
                                      )}
                                      {payment.stripe_charge_id && (
                                        <p>
                                          <span className="font-medium text-foreground/80">
                                            Charge:
                                          </span>{" "}
                                          <span className="font-mono">
                                            {payment.stripe_charge_id}
                                          </span>
                                        </p>
                                      )}
                                      <p>
                                        <span className="font-medium text-foreground/80">
                                          Paid At:
                                        </span>{" "}
                                        {fmtUtc(payment.paid_at)}
                                      </p>
                                      <p>
                                        <span className="font-medium text-foreground/80">
                                          Created At:
                                        </span>{" "}
                                        {fmtUtc(payment.created_at)}
                                      </p>
                                    </div>
                                  );
                                }

                                if (isManual) {
                                  return (
                                    <div className="mt-1.5 text-[11px] text-muted-foreground space-y-0.5">
                                      <p>
                                        <span className="font-medium text-foreground/80">
                                          Method:
                                        </span>{" "}
                                        Manual
                                      </p>
                                      <p>
                                        <span className="font-medium text-foreground/80">
                                          Paid At:
                                        </span>{" "}
                                        {fmtUtc(payment.paid_at)}
                                      </p>
                                      <p>
                                        <span className="font-medium text-foreground/80">
                                          Proof Uploaded:
                                        </span>{" "}
                                        {fmtUtc(proof?.created_at)}
                                      </p>
                                      <p>
                                        <span className="font-medium text-foreground/80">
                                          Reviewed At:
                                        </span>{" "}
                                        {fmtUtc(proof?.reviewed_at)}
                                      </p>
                                      {proof?.admin_comment && (
                                        <p>
                                          <span className="font-medium text-foreground/80">
                                            Comment:
                                          </span>{" "}
                                          {proof.admin_comment}
                                        </p>
                                      )}
                                    </div>
                                  );
                                }

                                return null;
                              })()}
                            </div>
                            <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto sm:justify-end">
                              {payment.type === "deposit" && (
                                <span className="inline-flex items-center rounded-full bg-blue-50 px-2.5 py-1 text-[11px] font-medium text-blue-700 border border-blue-200">
                                  {p.oneTime}
                                </span>
                              )}
                              {paymentProofsByPaymentId?.[payment.id] && (
                                <span
                                  className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-medium border ${
                                    paymentProofsByPaymentId[payment.id]
                                      .status === "approved"
                                      ? "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/50 dark:text-emerald-300 dark:border-emerald-800/50"
                                      : paymentProofsByPaymentId[payment.id]
                                            .status === "rejected"
                                        ? "bg-red-50 text-red-700 border-red-200 dark:bg-red-950/50 dark:text-red-300 dark:border-red-800/50"
                                        : "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/50 dark:text-amber-300 dark:border-amber-800/50"
                                  }`}
                                >
                                  {paymentProofsByPaymentId[payment.id]
                                    .status === "approved" && (
                                    <CheckCircle2 className="h-3 w-3" />
                                  )}
                                  {paymentProofsByPaymentId[payment.id]
                                    .status === "rejected" && (
                                    <AlertCircle className="h-3 w-3" />
                                  )}
                                  {paymentProofsByPaymentId[payment.id]
                                    .status !== "approved" &&
                                    paymentProofsByPaymentId[payment.id]
                                      .status !== "rejected" && (
                                      <Hourglass className="h-3 w-3" />
                                    )}
                                  {paymentProofsByPaymentId[payment.id]
                                    .status === "approved"
                                    ? p.proofApproved
                                    : paymentProofsByPaymentId[payment.id]
                                          .status === "rejected"
                                      ? p.proofRejected
                                      : p.proofPendingReview}
                                </span>
                              )}
                              {/* Feedback 9: a submitted direct debit reads as
                                  its own state here too — the raw column still
                                  says pending/overdue while the money is at the
                                  bank. */}
                              {isSepaDebitInFlight(payment) ? (
                                <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold border bg-sky-50 text-sky-700 border-sky-200 dark:bg-sky-950/50 dark:text-sky-300 dark:border-sky-800/50">
                                  <Landmark className="h-3 w-3" />
                                  {p.sepaInProgressStatus}
                                </span>
                              ) : isReferralFreeMonth(payment) ? (
                                /* A month CoLiviq gave them. "Paid" would be
                                   wrong on their own statement — they never
                                   paid it — and a bare "€0.00 paid" row reads
                                   as a billing error rather than a reward. */
                                <span className="inline-flex items-center gap-1 rounded-full border border-turkis-strong bg-turkis-fill px-2.5 py-1 text-[11px] font-semibold text-petrol dark:text-turkis">
                                  <Gift className="h-3 w-3" />
                                  {language === "de"
                                    ? "Geschenkt · Empfehlung"
                                    : "Free month · referral"}
                                </span>
                              ) : (
                                <span
                                  className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold border ${
                                    payment.status === "paid"
                                      ? "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/50 dark:text-emerald-300 dark:border-emerald-800/50"
                                      : payment.status === "scheduled" ||
                                          payment.status === "pending"
                                        ? "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/50 dark:text-amber-300 dark:border-amber-800/50"
                                        : payment.status === "overdue"
                                          ? "bg-red-50 text-red-700 border-red-200 dark:bg-red-950/50 dark:text-red-300 dark:border-red-800/50"
                                          : "bg-muted text-muted-foreground border-border"
                                  }`}
                                >
                                  {payment.status === "paid" && (
                                    <CheckCircle2 className="h-3 w-3" />
                                  )}
                                  {(payment.status === "scheduled" ||
                                    payment.status === "pending") && (
                                    <Clock className="h-3 w-3" />
                                  )}
                                  {payment.status === "overdue" && (
                                    <AlertCircle className="h-3 w-3" />
                                  )}
                                  {payment.status}
                                </span>
                              )}
                              {paymentProofsByPaymentId?.[payment.id]
                                ?.rejectionReasonToShow && (
                                <p className="w-full text-[11px] text-destructive mt-1">
                                  {p.rejectionReason}:{" "}
                                  {
                                    paymentProofsByPaymentId[payment.id]
                                      .rejectionReasonToShow
                                  }
                                </p>
                              )}
                              {paymentProofsByPaymentId?.[payment.id]
                                ?.file_path && (
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  className="h-9 w-9 rounded-lg hover:bg-muted"
                                  type="button"
                                  // Signed on click (COL-436): the bucket is
                                  // going private, so there is no public link.
                                  onClick={async () => {
                                    if (
                                      !(await openProofInNewTab(
                                        paymentProofsByPaymentId[payment.id]
                                          .file_path
                                      ))
                                    ) {
                                      toast.error(
                                        language === "de"
                                          ? "Dokument konnte nicht geöffnet werden"
                                          : "Could not open the document"
                                      );
                                    }
                                  }}
                                >
                                  <FileText className="w-4 h-4" />
                                </Button>
                              )}
                              <Button
                                size="icon"
                                variant="ghost"
                                className="h-9 w-9 rounded-lg hover:bg-muted"
                                type="button"
                                onClick={() =>
                                  toast.info(
                                    t(
                                      "dashboard.receiptDownloadsSoon",
                                      "Receipt downloads will be available soon."
                                    )
                                  )
                                }
                              >
                                <Download className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        ))
                      ) : (
                        <p className="text-xs text-muted-foreground">
                          {p.noPaymentsRecordedYet}
                        </p>
                      )}
                    </div>
                  </Card>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Community Tab */}
          <TabsContent value="community" className="space-y-6">
            <Card className="p-8">
              <div className="space-y-6">
                <div>
                  <h2 className="text-2xl font-bold text-foreground mb-2">
                    {communityLabel}
                  </h2>
                  <p className="text-muted-foreground">
                    {language === "de"
                      ? "Bleiben Sie mit Ihrer Co-Living-Community in Verbindung und entdecken Sie anstehende Veranstaltungen."
                      : "Stay connected with your co-living community and discover upcoming events."}
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
                      <Calendar className="w-5 h-5" />
                      {language === "de"
                        ? "Anstehende Veranstaltungen"
                        : "Upcoming Events"}
                    </h3>
                    <ComingSoon
                      description={
                        language === "de"
                          ? "Veranstaltungsliste wird bald verfügbar sein."
                          : "Event listings will be available soon."
                      }
                    />
                  </div>

                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
                      <Users className="w-5 h-5" />
                      {communityLabel}
                    </h3>
                    <ComingSoon
                      description={
                        language === "de"
                          ? "Community-Funktionen werden bald verfügbar sein."
                          : "Community features will be available soon."
                      }
                    />
                  </div>
                </div>
              </div>
            </Card>
          </TabsContent>

          {/* Check-in / Check-out */}
          <TabsContent value="checkin" className="space-y-6">
            <ProtocolFlow />
            {/* CheckInCheckOut is shown only at the entry state — when a
                protocol step is active, hide it so the user can focus
                on the flow. */}
            {!searchParams.get("p") ? <CheckInCheckOut /> : null}
          </TabsContent>

          {/* Maintenance Tab - Support Tickets */}
          <TabsContent value="maintenance" className="space-y-6">
            <TenantSupportHelp applicant={false} />
          </TabsContent>

          {/* Referrals — share a code, track each referral, earn the rent credit */}
          <TabsContent value="referrals" className="space-y-6">
            <ReferralsPanel />
          </TabsContent>

          {/* Other Tabs - Placeholder Content */}
          <TabsContent value="perks">
            <ComingSoon description="This feature will be available soon." />
          </TabsContent>
        </Tabs>

        {/* Card fee disclosure dialog — top-level so it mounts on every tab */}
        <Dialog
          open={!!cardFeeDialog}
          onOpenChange={(open) => {
            if (!open) closeCardFeeDialog(false);
          }}
        >
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>{p.cardFeeTitle}</DialogTitle>
              <DialogDescription>{p.cardFeeExplainer}</DialogDescription>
            </DialogHeader>
            {cardFeeDialog &&
              (() => {
                const { subtotalCents, feeCents, totalCents } =
                  formatCardFeeBreakdown(cardFeeDialog.netCents);
                const fmt = (c: number) =>
                  `€${(c / 100).toLocaleString(
                    language === "de" ? "de-DE" : "en-IE",
                    {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    }
                  )}`;
                return (
                  <div className="space-y-2 py-2 text-sm">
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">
                        {p.cardFeeSubtotal}
                      </span>
                      <span className="font-medium text-foreground">
                        {fmt(subtotalCents)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">
                        {p.cardFeeProcessing}
                      </span>
                      <span className="font-medium text-foreground">
                        {fmt(feeCents)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between pt-2 border-t border-border">
                      <span className="font-semibold text-foreground">
                        {p.cardFeeTotal}
                      </span>
                      <span className="text-lg font-bold text-foreground">
                        {fmt(totalCents)}
                      </span>
                    </div>
                  </div>
                );
              })()}
            <DialogFooter className="flex flex-col-reverse sm:flex-row gap-2">
              <Button
                variant="outline"
                onClick={() => closeCardFeeDialog(false)}
              >
                {p.cancel}
              </Button>
              <Button onClick={() => closeCardFeeDialog(true)}>
                {p.continueToStripe}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* End-direct-debit request (feedback 6): switching away from an
            active mandate needs admin approval — confirm, then file the
            request; nothing changes until an admin decides. */}
        <Dialog
          open={endDdMethod !== null}
          onOpenChange={(open) => {
            if (!open) setEndDdMethod(null);
          }}
        >
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>
                {language === "de"
                  ? "Lastschriftmandat beenden?"
                  : "End direct debit mandate?"}
              </DialogTitle>
              <DialogDescription>
                {language === "de"
                  ? `Der Wechsel zu ${
                      endDdMethod === "card" ? "Kartenzahlung" : "Überweisung"
                    } beendet Ihr SEPA-Lastschriftmandat und muss von einem Admin bestätigt werden. Bis zur Entscheidung bleibt die Lastschrift aktiv.`
                  : `Switching to ${
                      endDdMethod === "card" ? "card payment" : "bank transfer"
                    } ends your SEPA direct debit mandate and needs admin approval. Direct debit stays active until it's decided.`}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="flex flex-col-reverse sm:flex-row gap-2">
              <Button variant="outline" onClick={() => setEndDdMethod(null)}>
                {p.cancel}
              </Button>
              <Button onClick={() => void requestDdCancellation()}>
                {language === "de"
                  ? "Beendigung anfragen"
                  : "Request cancellation"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* SEPA Direct Debit mandate dialog */}
        <Dialog
          open={!!sepaMandateOpen}
          onOpenChange={(open) => {
            if (!open) setSepaMandateOpen(null);
          }}
        >
          <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{p.sepaFormTitle}</DialogTitle>
            </DialogHeader>
            {sepaMandateOpen && (
              <SepaMandateForm
                bookingId={sepaMandateOpen.bookingId}
                creditorName={SEPA_CREDITOR_NAME}
                creditorId={SEPA_CREDITOR_ID}
                labels={{
                  title: p.sepaFormTitle,
                  creditorHeading: p.sepaCreditorHeading,
                  creditorNameLabel: p.sepaCreditorNameLabel,
                  creditorIdLabel: p.sepaCreditorIdLabel,
                  mandateReferenceLabel: p.sepaMandateReferenceLabel,
                  mandateReferencePending: p.sepaMandateReferencePending,
                  authorizationHeading: p.sepaAuthorizationHeading,
                  authorizationText: p.sepaAuthorizationText,
                  refundNoticeHeading: p.sepaRefundNoticeHeading,
                  refundNoticeText: p.sepaRefundNoticeText,
                  customerDataHeading: p.sepaCustomerDataHeading,
                  salutationLabel: p.sepaSalutation,
                  salutationMr: p.sepaMr,
                  salutationMrs: p.sepaMrs,
                  salutationDivers: p.sepaDivers,
                  firstName: p.sepaFirstName,
                  lastName: p.sepaLastName,
                  street: p.sepaStreet,
                  houseNumber: p.sepaHouseNumber,
                  postalCode: p.sepaPostalCode,
                  city: p.sepaCity,
                  country: p.sepaCountry,
                  iban: p.sepaIban,
                  bic: p.sepaBic,
                  accountHolder: p.sepaAccountHolder,
                  signatureHeading: p.sepaSignatureHeading,
                  signingPlace: p.sepaSigningPlace,
                  signingDate: p.sepaSigningDate,
                  confirmCheckbox: p.sepaConfirmCheckbox,
                  typedNameLabel: p.sepaTypedNameLabel,
                  typedNameHelper: p.sepaTypedNameHelper,
                  submit: p.sepaSubmit,
                  submitting: p.sepaSubmitting,
                  cancel: p.cancel,
                  genericError: p.sepaGenericError,
                  ibanInvalid: p.sepaIbanInvalid,
                  typedNameMismatch: p.sepaTypedNameMismatch,
                  mandatoryField: p.sepaMandatoryField,
                }}
                onSuccess={(mandateRef) => {
                  setSepaMandateOpen(null);
                  toast.success(p.sepaSuccessBody(mandateRef));
                  queryClient.invalidateQueries({
                    queryKey: ["payment-methods", user?.id],
                  });
                }}
                onCancel={() => setSepaMandateOpen(null)}
              />
            )}
          </DialogContent>
        </Dialog>

        {/* Stay dates — editable until contract is signed */}
        <Dialog
          open={stayDatesOpen}
          onOpenChange={(open) => {
            setStayDatesOpen(open);
            if (!open) {
              setStayDatesBookingId(null);
              setStayDatesRoomTitle("");
              setStayDatesMoveIn("");
              setStayDatesMoveOut("");
              setStayDatesMoveInOpen(false);
              setStayDatesMoveOutOpen(false);
            }
          }}
        >
          <DialogContent className="sm:max-w-md border-border/60 shadow-xl">
            <DialogHeader>
              <DialogTitle>
                {homeLabels.stayDatesDialogTitle}
                {stayDatesRoomTitle ? (
                  <span className="mt-2 inline-flex max-w-full items-center rounded-full border border-primary/25 bg-primary/10 px-3 py-1 text-sm font-semibold text-primary truncate shadow-sm">
                    {stayDatesRoomTitle}
                  </span>
                ) : null}
              </DialogTitle>
              <DialogDescription className="text-left text-muted-foreground leading-relaxed">
                {homeLabels.dateChangeNote}
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-2">
              <div className="space-y-2">
                <Label className="text-sm font-medium">
                  {homeLabels.moveInPicker}
                </Label>
                <Popover
                  open={stayDatesMoveInOpen}
                  onOpenChange={setStayDatesMoveInOpen}
                >
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal rounded-xl border-border/60 h-11",
                        !stayDatesMoveIn && "text-muted-foreground"
                      )}
                    >
                      <Calendar className="mr-2 h-4 w-4 shrink-0 opacity-70" />
                      {stayDatesMoveIn
                        ? format(parseISO(stayDatesMoveIn), "PPP")
                        : language === "de"
                          ? "Datum wählen"
                          : "Pick a date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <DatePickerCalendar
                      mode="single"
                      selected={
                        stayDatesMoveIn ? parseISO(stayDatesMoveIn) : undefined
                      }
                      onSelect={(d) => {
                        if (d) {
                          const iso = format(d, "yyyy-MM-dd");
                          setStayDatesMoveIn(iso);
                          const minTo = getMinToDateAfterSixMonths(iso);
                          if (!stayDatesMoveOut || stayDatesMoveOut < minTo) {
                            setStayDatesMoveOut(minTo);
                          }
                        }
                        setStayDatesMoveInOpen(false);
                      }}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium">
                  {homeLabels.moveOutPicker}
                </Label>
                <Popover
                  open={stayDatesMoveOutOpen}
                  onOpenChange={setStayDatesMoveOutOpen}
                >
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal rounded-xl border-border/60 h-11",
                        !stayDatesMoveOut && "text-muted-foreground"
                      )}
                      disabled={!stayDatesMoveIn}
                    >
                      <Calendar className="mr-2 h-4 w-4 shrink-0 opacity-70" />
                      {stayDatesMoveOut
                        ? format(parseISO(stayDatesMoveOut), "PPP")
                        : language === "de"
                          ? "Datum wählen"
                          : "Pick a date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <DatePickerCalendar
                      mode="single"
                      selected={
                        stayDatesMoveOut
                          ? parseISO(stayDatesMoveOut)
                          : undefined
                      }
                      onSelect={(d) => {
                        if (d) {
                          setStayDatesMoveOut(format(d, "yyyy-MM-dd"));
                        }
                        setStayDatesMoveOutOpen(false);
                      }}
                      disabled={
                        stayDatesMoveIn
                          ? {
                              before: parseISO(
                                getMinToDateAfterSixMonths(stayDatesMoveIn)
                              ),
                            }
                          : undefined
                      }
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>
            <DialogFooter className="flex-col-reverse sm:flex-row gap-2 sm:gap-0">
              <Button
                type="button"
                variant="outline"
                className="rounded-xl"
                onClick={() => setStayDatesOpen(false)}
              >
                {language === "de" ? "Abbrechen" : "Cancel"}
              </Button>
              <Button
                type="button"
                className="rounded-xl"
                onClick={() => {
                  if (
                    !stayDatesBookingId ||
                    !stayDatesMoveIn ||
                    !stayDatesMoveOut
                  )
                    return;
                  const minTo = getMinToDateAfterSixMonths(stayDatesMoveIn);
                  if (stayDatesMoveOut < minTo) {
                    toast.error(homeLabels.dateValidationError);
                    return;
                  }
                  updateStayDatesMutation.mutate({
                    bookingId: stayDatesBookingId,
                    moveIn: stayDatesMoveIn,
                    moveOut: stayDatesMoveOut,
                  });
                }}
                disabled={
                  updateStayDatesMutation.isPending ||
                  !stayDatesBookingId ||
                  !stayDatesMoveIn ||
                  !stayDatesMoveOut
                }
              >
                {updateStayDatesMutation.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {language === "de" ? "Speichern…" : "Saving…"}
                  </>
                ) : (
                  homeLabels.saveDates
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Account settings. The dialog itself lives in
            components/settings/AccountSettingsDialog so the owner portal can
            open the same screen — Dashboard used to own it outright, which is
            why owners had no account page at all. The tenant-only Documents
            panel stays here, in the scope that owns its upload state, and is
            handed back in as a slot. */}
        <AccountSettingsDialog
          open={settingsOpen}
          onOpenChange={setSettingsOpen}
          initialTab={settingsTab}
          documentsHasIssue={hasDocumentNotifications}
          phoneFallback={
            (allBookings as { phone?: string | null }[])
              .find((bk) => bk.phone?.trim())
              ?.phone?.trim() ?? ""
          }
          onClosed={() => {
            setSelectedIdentityFiles([]);
            setSelectedSalaryFiles([]);
            setIsEditingProfile(false);
            setSettingsTab("profile");
          }}
          documentsSlot={
            <>
              <TabsContent value="documents" className="m-0 space-y-5">
                {documentIssueKind && (
                  <div
                    className={`flex items-start gap-3 rounded-2xl border p-4 ${
                      documentIssueKind === "rejected"
                        ? "border-destructive/30 bg-destructive/10"
                        : "border-amber-500/30 bg-amber-500/10"
                    }`}
                  >
                    <AlertCircle
                      className={`h-5 w-5 shrink-0 mt-0.5 ${
                        documentIssueKind === "rejected"
                          ? "text-destructive"
                          : "text-amber-600"
                      }`}
                    />
                    <div className="min-w-0">
                      <p
                        className={`text-sm font-medium ${
                          documentIssueKind === "rejected"
                            ? "text-destructive"
                            : "text-amber-700"
                        }`}
                      >
                        {documentIssueKind === "rejected"
                          ? language === "de"
                            ? "Dokument abgelehnt"
                            : "Document declined"
                          : language === "de"
                            ? "Wird geprüft"
                            : "Under review"}
                      </p>
                      <p className="text-sm text-muted-foreground mt-0.5">
                        {documentIssueKind === "rejected"
                          ? language === "de"
                            ? "Eines deiner Dokumente wurde abgelehnt. Bitte lies den Hinweis unten und lade es erneut hoch."
                            : "One of your documents was declined. Please read the note below and upload it again."
                          : language === "de"
                            ? "Deine Dokumente werden geprüft. Wir melden uns, falls etwas fehlt."
                            : "Your documents are being reviewed. We'll let you know if anything is needed."}
                      </p>
                    </div>
                  </div>
                )}
                {/* Identity documents + Salary documents show separately below */}
                <div className="grid gap-5">
                  <div className="relative rounded-2xl border border-border/60 bg-card p-5 sm:p-6 shadow-sm">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="h-10 w-10 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center">
                        <IdCard className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <h3 className="text-base font-semibold text-foreground">
                          {language === "de"
                            ? "Ausweisdokumente"
                            : "Identity documents"}
                        </h3>
                        <p className="text-sm text-muted-foreground">
                          {language === "de"
                            ? "Laden Sie Ausweis/Pass hoch (PDF oder Bild, max. 10MB)."
                            : "Upload ID/passport (PDF or image, up to 10MB)."}
                        </p>
                      </div>
                    </div>
                    {/* existing identity upload/list block */}
                    {/*
                NOTE: Keeping the existing upload area & lists exactly as-is would require moving a large block.
                For now, we keep the original section code below unchanged (functionality preserved) and only restyle wrapper.
              */}
                    <div className="space-y-5">
                      {/* Upload Area */}
                      <div
                        onDragOver={handleDragOver}
                        onDrop={(e) => handleDrop(e, "id_passport")}
                        className="rounded-2xl border-2 border-dashed border-primary/25 bg-primary/[0.03] p-6 sm:p-8 hover:border-primary/45 transition-colors"
                      >
                        <div className="flex flex-col items-center justify-center gap-3">
                          <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center border border-primary/15">
                            <Upload className="w-6 h-6 text-primary" />
                          </div>
                          <div className="text-center">
                            <p className="text-sm font-medium text-foreground">
                              {language === "de"
                                ? "Dateien hier ablegen oder auswählen"
                                : "Drop files here or browse"}
                            </p>
                            <p className="text-xs text-muted-foreground mt-1">
                              {language === "de"
                                ? "PDF/JPG/PNG bis 10MB"
                                : "PDF/JPG/PNG up to 10MB"}
                            </p>
                          </div>
                          <input
                            type="file"
                            id="identity-upload"
                            ref={identityUploadInputRef}
                            multiple
                            accept=".pdf,.jpg,.jpeg,.png"
                            className="hidden"
                            onChange={(e) =>
                              handleFileSelect(e.target.files, "id_passport")
                            }
                          />
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() =>
                              identityUploadInputRef.current?.click()
                            }
                            disabled={uploadDocumentsMutation.isPending}
                            className="rounded-xl border-border/60"
                          >
                            <Upload className="w-4 h-4 mr-2" />
                            {language === "de"
                              ? "Dateien auswählen"
                              : "Select files"}
                          </Button>
                        </div>
                      </div>

                      {/* Selected Files Preview */}
                      {selectedIdentityFiles.length > 0 && (
                        <div className="space-y-3 rounded-2xl border border-border/60 bg-muted/20 p-4">
                          <div className="flex items-center justify-between gap-2">
                            <p className="text-sm font-semibold text-foreground">
                              {language === "de" ? "Ausgewählt" : "Selected"} (
                              {selectedIdentityFiles.length})
                            </p>
                          </div>
                          <div className="space-y-2 max-h-32 overflow-y-auto">
                            {selectedIdentityFiles.map((file, index) => (
                              <div
                                key={index}
                                className="flex items-center justify-between gap-2 p-3 rounded-xl bg-background border border-border/60"
                              >
                                <div className="flex items-center gap-2 flex-1 min-w-0">
                                  <File className="w-4 h-4 text-muted-foreground shrink-0" />
                                  <span className="text-sm text-foreground truncate">
                                    {file.name}
                                  </span>
                                  <span className="text-xs text-muted-foreground shrink-0">
                                    ({(file.size / 1024 / 1024).toFixed(2)} MB)
                                  </span>
                                </div>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() =>
                                    handleRemoveSelectedFile(
                                      index,
                                      "id_passport"
                                    )
                                  }
                                  className="h-9 w-9 rounded-xl"
                                >
                                  <X className="w-4 h-4" />
                                </Button>
                              </div>
                            ))}
                          </div>
                          <Button
                            onClick={() => handleSaveDocuments("id_passport")}
                            disabled={uploadDocumentsMutation.isPending}
                            className="w-full rounded-xl"
                          >
                            {uploadDocumentsMutation.isPending ? (
                              <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                {language === "de"
                                  ? "Wird hochgeladen…"
                                  : "Uploading…"}
                              </>
                            ) : (
                              <>
                                <Save className="w-4 h-4 mr-2" />
                                {language === "de"
                                  ? "Dokumente hochladen"
                                  : "Upload documents"}
                              </>
                            )}
                          </Button>
                        </div>
                      )}

                      {/* Uploaded Documents List */}
                      {identityDocuments && identityDocuments.length > 0 && (
                        <div className="space-y-3">
                          <p className="text-sm font-semibold text-foreground uppercase tracking-wide">
                            {language === "de"
                              ? "Hochgeladene Dokumente"
                              : "Uploaded Documents"}
                          </p>
                          <div className="space-y-2">
                            {identityDocuments.map((doc) => (
                              <AccountDocumentRow
                                key={doc.id}
                                doc={doc}
                                onRenamed={() =>
                                  queryClient.invalidateQueries({
                                    queryKey: ["identity-documents", user?.id],
                                  })
                                }
                                onDelete={
                                  canTenantDeleteDocument(doc)
                                    ? () =>
                                        deleteDocumentMutation.mutate({
                                          documentId: doc.id,
                                          fileUrl: doc.file_url,
                                        })
                                    : undefined
                                }
                                deleting={deleteDocumentMutation.isPending}
                              />
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="relative rounded-2xl border border-border/60 bg-card p-5 sm:p-6 shadow-sm">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="h-10 w-10 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center">
                        <Receipt className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <h3 className="text-base font-semibold text-foreground">
                          {language === "de"
                            ? "Gehaltsnachweise"
                            : "Salary statements"}
                        </h3>
                        <p className="text-sm text-muted-foreground">
                          {language === "de"
                            ? "Laden Sie Gehaltsnachweise hoch (PDF oder Bild, max. 10MB)."
                            : "Upload salary proof (PDF or image, up to 10MB)."}
                        </p>
                      </div>
                    </div>

                    {/* Upload Area */}
                    <div
                      onDragOver={handleDragOver}
                      onDrop={(e) => handleDrop(e, "proof_of_income")}
                      className="rounded-2xl border-2 border-dashed border-primary/25 bg-primary/[0.03] p-6 sm:p-8 hover:border-primary/45 transition-colors"
                    >
                      <div className="flex flex-col items-center justify-center gap-3">
                        <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center border border-primary/15">
                          <Upload className="w-6 h-6 text-primary" />
                        </div>
                        <div className="text-center">
                          <p className="text-sm font-medium text-foreground">
                            {language === "de"
                              ? "Dateien hier ablegen oder auswählen"
                              : "Drop files here or browse"}
                          </p>
                          <p className="text-xs text-muted-foreground mt-1">
                            {language === "de"
                              ? "PDF/JPG/PNG bis 10MB"
                              : "PDF/JPG/PNG up to 10MB"}
                          </p>
                        </div>
                        <input
                          type="file"
                          id="salary-upload"
                          ref={salaryUploadInputRef}
                          multiple
                          accept=".pdf,.jpg,.jpeg,.png"
                          className="hidden"
                          onChange={(e) =>
                            handleFileSelect(e.target.files, "proof_of_income")
                          }
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => salaryUploadInputRef.current?.click()}
                          disabled={uploadDocumentsMutation.isPending}
                          className="rounded-xl border-border/60"
                        >
                          <Upload className="w-4 h-4 mr-2" />
                          {language === "de"
                            ? "Dateien auswählen"
                            : "Select files"}
                        </Button>
                      </div>
                    </div>

                    {/* Selected Files Preview */}
                    {selectedSalaryFiles.length > 0 && (
                      <div className="space-y-3 mt-5 rounded-2xl border border-border/60 bg-muted/20 p-4">
                        <p className="text-sm font-semibold text-foreground">
                          {language === "de" ? "Ausgewählt" : "Selected"} (
                          {selectedSalaryFiles.length})
                        </p>
                        <div className="space-y-2 max-h-32 overflow-y-auto">
                          {selectedSalaryFiles.map((file, index) => (
                            <div
                              key={index}
                              className="flex items-center justify-between gap-2 p-3 rounded-xl bg-background border border-border/60"
                            >
                              <div className="flex items-center gap-2 flex-1 min-w-0">
                                <File className="w-4 h-4 text-muted-foreground shrink-0" />
                                <span className="text-sm text-foreground truncate">
                                  {file.name}
                                </span>
                                <span className="text-xs text-muted-foreground shrink-0">
                                  ({(file.size / 1024 / 1024).toFixed(2)} MB)
                                </span>
                              </div>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() =>
                                  handleRemoveSelectedFile(
                                    index,
                                    "proof_of_income"
                                  )
                                }
                                className="h-9 w-9 rounded-xl"
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            </div>
                          ))}
                        </div>
                        <Button
                          onClick={() => handleSaveDocuments("proof_of_income")}
                          disabled={uploadDocumentsMutation.isPending}
                          className="w-full rounded-xl"
                        >
                          {uploadDocumentsMutation.isPending ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              {language === "de"
                                ? "Wird hochgeladen…"
                                : "Uploading…"}
                            </>
                          ) : (
                            <>
                              <Save className="w-4 h-4 mr-2" />
                              {language === "de"
                                ? "Dokumente hochladen"
                                : "Upload documents"}
                            </>
                          )}
                        </Button>
                      </div>
                    )}

                    {/* Uploaded Documents List */}
                    {salaryDocuments && salaryDocuments.length > 0 && (
                      <div className="space-y-3 mt-5">
                        <p className="text-sm font-semibold text-foreground uppercase tracking-wide">
                          {language === "de"
                            ? "Hochgeladene Dokumente"
                            : "Uploaded Documents"}
                        </p>
                        <div className="space-y-2">
                          {salaryDocuments.map((doc) => (
                            <AccountDocumentRow
                              key={doc.id}
                              doc={doc}
                              onRenamed={() =>
                                queryClient.invalidateQueries({
                                  queryKey: ["salary-documents", user?.id],
                                })
                              }
                              onDelete={
                                canTenantDeleteDocument(doc)
                                  ? () =>
                                      deleteDocumentMutation.mutate({
                                        documentId: doc.id,
                                        fileUrl: doc.file_url,
                                      })
                                  : undefined
                              }
                              deleting={deleteDocumentMutation.isPending}
                            />
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </TabsContent>
            </>
          }
        />
      </div>
    </div>
  );
};

export default Dashboard;
